import authService from "../../services/authService";
import React, { useState, useEffect, useRef } from "react";
import { Navigate, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Card,
  Grid,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  FormControlLabel,
  Divider,
  CircularProgress,
  Tooltip,
  Autocomplete,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Send as SendIcon,
  RestartAlt as ResetIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Draw as DrawIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import residentService from "../../services/residentService";
import billingService from "../../services/billingService";
import facilityService from "../../services/facilityService";
import { FacilityDTO } from "../../types/facility.types";
import SignatureInput from "../../components/SignatureInput";
import { useTenant } from "../../contexts/TenantContext";
import { useAuth } from "../../contexts/AuthContext";
import { BillingClaimDTO, BillingClaimRequest, ClaimFormType, ClaimStatus, ClaimType, InvoiceFormData, InvoiceLine, SubmissionMethod, IndividualInvoiceRequest, IndividualInvoiceDTO } from "../../types/billing.types";
import { InsuranceCard } from "../../types/resident.types";
import { CLAIM_FORMS, CLAIM_STATUS_META, getApiErrorMessage, getClaimForm, normalizePreviousServices } from "./billingConfig";
import { downloadInvoicePdf, printClaimPdfDirect, printInvoicePdfDirect } from "./claimPdf";
import InvoiceFormSection from "./InvoiceFormSection";
import { isValidNpi, validateNpi } from "../../utils/npiUtils";
import { isValidTaxId, validateTaxId, formatTaxId } from "../../utils/taxIdUtils";

interface CmsServiceLine {
  id: string;
  dateFrom: string;
  dateTo: string;
  pos: string;
  emg: string;
  cpt: string;
  mod1: string;
  mod2: string;
  mod3: string;
  mod4: string;
  diagPointer: string;
  charges: number | string;
  daysUnits: number | string;
  epsdt: string;
  idQual: string;
  renderingNpi: string;
}

interface Ub04RevenueLine {
  id: string;
  revCode: string;
  description: string;
  hcpcsRate: string;
  serviceDate: string;
  units: number | string;
  totalCharges: number | string;
  nonCoveredCharges?: number | string;
}

const getClaimFormTheme = (parentTheme: any) =>
  createTheme(parentTheme, {
    components: {
      MuiTextField: {
        defaultProps: {
          InputLabelProps: { shrink: true },
        },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
              backgroundColor: "#FFFFFF",
              "& fieldset": {
                borderColor: "#CBD5E1",
              },
              "&:hover fieldset": {
                borderColor: "#94A3B8",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#0077B6",
                borderWidth: "1.5px",
              },
            },
            "& .MuiInputLabel-root": {
              color: "#1A2B45 !important",
              fontWeight: 500,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#1A2B45 !important",
              fontWeight: 500,
            },
            "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
              transform: "translate(14px, -9px) scale(0.75)",
              color: "#1A2B45 !important",
              backgroundColor: "#FFFFFF",
              padding: "0 4px",
            },
            "& .MuiInputBase-input": {
              color: "#1A2B45 !important",
              WebkitTextFillColor: "#1A2B45",
            },
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: {
            color: "#1A2B45 !important",
            fontWeight: 500,
            "&.Mui-focused": {
              color: "#1A2B45 !important",
              fontWeight: 500,
            },
            "&.MuiInputLabel-shrink": {
              color: "#1A2B45 !important",
            },
          },
        },
      },
    },
  });

const formatFedTaxNo = formatTaxId;

const getEffectiveFacility = (fac: FacilityDTO | null, tenantName?: string) => {
  const tName = fac?.name || tenantName || "Clover Home";
  const isClover = tName.toLowerCase().includes("clover");
  const isLakewood = tName.toLowerCase().includes("lakewood");

  const fallbackAddress = isClover ? "1458 Willow Creek Drive" : isLakewood ? "742 Evergreen Terrace" : "100 Healthcare Parkway";
  const fallbackCity = isClover ? "Dallas" : isLakewood ? "Springfield" : "Austin";
  const fallbackState = isClover ? "TX" : isLakewood ? "IL" : "TX";
  const fallbackZip = isClover ? "75201" : isLakewood ? "62704" : "78701";
  const fallbackPhone = isClover ? "(214) 555-0199" : isLakewood ? "(217) 555-0144" : "(512) 555-0100";
  const fallbackNpi = isClover ? "1982736450" : isLakewood ? "1871625349" : "1234567890";
  const fallbackTaxId = isClover ? "75-3849201" : isLakewood ? "36-9281743" : "12-3456789";

  return {
    name: fac?.name || tName,
    address: fac?.address || (fac as any)?.street || fallbackAddress,
    city: fac?.city || fallbackCity,
    state: fac?.state || fallbackState,
    postalCode: fac?.postalCode || (fac as any)?.zipCode || (fac as any)?.zip || fallbackZip,
    phone: fac?.phone || (fac as any)?.phoneNumber || fallbackPhone,
    npiNumber: fac?.npiNumber || (fac as any)?.npi || fallbackNpi,
    taxId: formatFedTaxNo((fac as any)?.taxId || (fac as any)?.fedTaxNo || (fac as any)?.federalTaxId || (fac as any)?.licenseNumber || fallbackTaxId),
    typeOfBill: (fac as any)?.typeOfBill || (fac?.admissionType === "HOSPICE" ? "0811" : "0211"),
  };
};

const ClaimEditorPage: React.FC = () => {
  const { currentTenantId, tenant } = useTenant();
  const { user } = useAuth();
  const parentTheme = useTheme();
  const claimTheme = React.useMemo(() => getClaimFormTheme(parentTheme), [parentTheme]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClaimId = searchParams.get("claimId") || searchParams.get("id") || searchParams.get("invoiceId");
  const stateClaimId = (location.state as any)?.claimId || (location.state as any)?.id;
  // /billing/new/:formType creates a claim; /billing/:claimId(/edit) reopens a saved one
  const { formType, claimId } = useParams<{ formType?: string; claimId?: string }>();
  const [activeClaimId, setActiveClaimId] = useState<string | undefined>(claimId || queryClaimId || stateClaimId || undefined);
  const [existingClaim, setExistingClaim] = useState<BillingClaimDTO | null>(null);
  const [claimLoadError, setClaimLoadError] = useState<string | null>(null);
  const formDefinition = getClaimForm(existingClaim?.formType ?? formType) || CLAIM_FORMS[0];
  const selectedForm = formDefinition.id;
  const isReadOnly = Boolean(existingClaim && !existingClaim.editable);
  const [saving, setSaving] = useState(false);
  const [currentFacility, setCurrentFacility] = useState<FacilityDTO | null>(null);

  // ── Residents list for autofill ──
  const [residents, setResidents] = useState<any[]>([]);
  const [selectedResidentId, setSelectedResidentId] = useState<string>("");
  const [residentsLoading, setResidentsLoading] = useState<boolean>(false);
  const [autofilling, setAutofilling] = useState<boolean>(false);
  const [residentSearchInput, setResidentSearchInput] = useState<string>("");
  const [patientDobTouched, setPatientDobTouched] = useState<boolean>(false);
  const [dobPickerError, setDobPickerError] = useState<string | null>(null);
  const [formTouched, setFormTouched] = useState<boolean>(false);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const markFieldTouched = (fieldName: string) => {
    setTouchedFields((prev) => (prev[fieldName] ? prev : { ...prev, [fieldName]: true }));
  };
  const isFieldTouched = (fieldName: string) => Boolean(formTouched || touchedFields[fieldName]);
  const residentSelectRequestIdRef = useRef<number>(0);

  // ── Multiple Insurance Selection ──
  const [residentInsurances, setResidentInsurances] = useState<InsuranceCard[]>([]);
  const [selectedInsuranceId, setSelectedInsuranceId] = useState<string>("");
  const [currentResidentData, setCurrentResidentData] = useState<any>(null);
  const [signatureModalTarget, setSignatureModalTarget] = useState<"patient" | "insured" | null>(null);
  const [signatureUploading, setSignatureUploading] = useState<"patient" | "insured" | null>(null);
  const pendingPatientSignatureFileRef = useRef<File | null>(null);
  const pendingInsuredSignatureFileRef = useRef<File | null>(null);
  const [printingPdf, setPrintingPdf] = useState(false);

  // ── Notification Snackbar ──
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // CMS-1500 FORM FIELDS (All fields matching the CMS-1500 form)
  const emptyCmsState = {
    // Section 1: Carrier & Coverage Program
    carrierName: "",
    carrierAddress: "",
    insuranceProgram: "",
    insuredId: "",

    // Section 2: Patient Demographics & Address
    residentFullName: "",
    patientLastName: "",
    patientFirstName: "",
    patientMiddleInitial: "",
    patientDob: "",
    patientSex: "",
    patientRelationship: "",
    patientAddress: "",
    patientCity: "",
    patientState: "",
    patientZip: "",
    patientPhone: "",

    // Section 3: Insured (Subscriber) Information
    insuredLastName: "",
    insuredFirstName: "",
    insuredMiddleInitial: "",
    insuredAddress: "",
    insuredCity: "",
    insuredState: "",
    insuredZip: "",
    insuredPhone: "",
    reservedNuccBox8: "",
    otherInsuredName: "",
    otherInsuredPolicy: "",
    reservedNuccBox9b: "",
    reservedNuccBox9c: "",
    otherInsuredPlanName: "",
    insuredPolicyGroup: "",
    insuredDob: "",
    insuredSex: "",
    otherClaimId: "",
    insurancePlanName: "",
    anotherBenefitPlan: "NO",

    // Section 4: Condition Causes & Authorizations
    conditionEmployment: "NO",
    conditionAutoAccident: "NO",
    conditionAutoState: "",
    conditionOtherAccident: "NO",
    claimCodes10d: "",
    patientSignatureType: "DIGITAL" as "DIGITAL" | "UPLOAD",
    patientDigitalSignature: "",
    patientSignatureImage: "",
    patientSignatureDate: "",
    insuredSignatureType: "DIGITAL" as "DIGITAL" | "UPLOAD",
    insuredDigitalSignature: "",
    insuredSignatureImage: "",
    insuredSignatureDate: "",

    // Section 5: Clinical Details & Diagnoses
    dateCurrentIllness: "",
    dateCurrentIllnessQual: "",
    otherDate: "",
    otherDateQual: "",
    unableWorkFrom: "",
    unableWorkTo: "",
    referringProviderName: "",
    referringProviderQual: "",
    referringProviderOtherId: "",
    referringProviderNpi: "",
    hospitalizationFrom: "",
    hospitalizationTo: "",
    additionalClaimInfo: "",
    outsideLab: "NO",
    outsideLabCharges: "",
    resubmissionCode: "",
    originalRefNo: "",
    priorAuthNumber: "",

    // ICD-10 Diagnoses
    icdIndicator: "0",
    diagA: "",
    diagB: "",
    diagC: "",
    diagD: "",
    diagE: "",
    diagF: "",
    diagG: "",
    diagH: "",
    diagI: "",
    diagJ: "",
    diagK: "",
    diagL: "",

    // Section 6: Billing Provider, Facility & Totals
    taxId: "",
    taxIdType: "EIN",
    patientAccountNo: "",
    acceptAssignment: "YES",
    totalCharge: 0,
    amountPaid: 0,
    reservedNuccBox30: "",
    balanceDue: 0,
    physicianSignature: "",
    physicianSignatureDate: "",

    // Service Facility
    facilityName: "",
    facilityAddress: "",
    facilityCity: "",
    facilityState: "",
    facilityZip: "",
    facilityNpi: "",
    facilityOtherId: "",

    // Billing Provider
    billingProviderName: "",
    billingProviderAddress: "",
    billingProviderCity: "",
    billingProviderState: "",
    billingProviderZip: "",
    billingProviderPhone: "",
    billingProviderNpi: "",
    billingProviderOtherId: "",
  };

  const [cms, setCms] = useState(emptyCmsState);

  // Service Lines (empty initial row)
  const [serviceLines, setServiceLines] = useState<CmsServiceLine[]>([
    {
      id: "1",
      dateFrom: "",
      dateTo: "",
      pos: "",
      emg: "",
      cpt: "",
      mod1: "",
      mod2: "",
      mod3: "",
      mod4: "",
      diagPointer: "",
      charges: "",
      daysUnits: "",
      epsdt: "",
      idQual: "",
      renderingNpi: "",
    },
  ]);

  // UB-04 State
  const emptyUb04State = {
    // Box 1: Facility / Provider Name & Address
    providerName: "",
    providerAddress: "",
    providerCity: "",
    providerState: "",
    providerZip: "",
    providerPhone: "",

    // Box 3: Patient Control & Medical Record
    patientControlNo: "",
    medicalRecordNo: "",

    // Box 4: Type of Bill (Default 0211 for SNF Inpatient)
    typeOfBill: "0211",

    // Box 5: Federal Tax No.
    fedTaxNo: "",

    // Box 6: Statement Covers Period
    statementCoversFrom: "",
    statementCoversThrough: "",

    // Box 8: Patient Name
    patientName: "",

    // Box 9: Patient Address
    patientAddress: "",
    patientCity: "",
    patientState: "",
    patientZip: "",

    // Box 10 & 11: Patient Demographics
    patientDob: "",
    patientSex: "M",

    // Box 38: Payer / Responsible Party Address
    payerAddress: "",
    payerCityStateZip: "",

    // Table summary
    creationDate: "",
    totalCharges: 0,

    // Box 50 - 56: Payer & Provider Identification
    payerName: "",
    healthPlanId: "",
    providerNpi: "",

    // Box 58 - 63: Insured & Treatment Authorization
    insuredName: "",
    insuredUniqueId: "",
    insuredGroupName: "",
    insuredGroupNo: "",
    treatmentAuthCode: "",

    // Box 66 - 67: Diagnosis Codes
    icdIndicator: "0",
    principalDiagnosis: "",
    otherDiagnosis1: "",
    otherDiagnosis2: "",

    // Box 76: Attending Provider
    attendingNpi: "",
    attendingQual: "",
    attendingLastName: "",
    attendingFirstName: "",

    // Box 77: Operating Provider
    operatingNpi: "",
    operatingLastName: "",
    operatingFirstName: "",
  };

  const initialUb04RevenueLines: Ub04RevenueLine[] = [
    {
      id: "1",
      revCode: "",
      description: "",
      hcpcsRate: "",
      serviceDate: "",
      units: 1,
      totalCharges: "",
      nonCoveredCharges: "",
    },
  ];

  const [ub04Form, setUb04Form] = useState(emptyUb04State);
  const [ub04RevenueLines, setUb04RevenueLines] = useState<Ub04RevenueLine[]>(initialUb04RevenueLines);

  // Invoice State
  const emptyInvoiceState: InvoiceFormData = {
    providerDba: "JSNU Healthcare LLC dba",
    providerName: "Lakewood Villa Care Center",
    providerAddress: "8708 Gerber Rd",
    providerCity: "Sacramento",
    providerState: "CA",
    providerZip: "95828",
    providerEmail: "jsnuhealthcare@gmail.com",
    providerPhone: "+1 (248) 294-8930",
    providerWebsite: "www.lakewoodvillacarecenter.com",
    providerLicenseNo: "342701553",
    providerNpi: "1558178681",

    billToClientName: "",
    billToAddress: "",
    shipToAddress: "",

    invoiceNumber: "",
    terms: "Net 15",
    invoiceDate: "",
    dueDate: "",

    residentName: "",
    residentCin: "",
    residentDob: "",
    servicePeriodFrom: "",
    servicePeriodTo: "",
    managedCarePlan: "",

    paymentMethod: "BANK",
    totalAmount: 0,
    statusLabel: "Draft",
    overdueDate: "",
    notes: "",
  };

  const initialInvoiceLines: InvoiceLine[] = [
    {
      id: "1",
      productOrService: "Caregiving",
      description: "",
      serviceLookupId: "e7f124ae-4512-4b70-a866-bea120d06f1e",
      qty: "" as any,
      rate: 0,
      amount: 0,
    },
  ];

  const [invoiceForm, setInvoiceForm] = useState<InvoiceFormData>(emptyInvoiceState);
  const [invoiceLines, setInvoiceLines] = useState<InvoiceLine[]>(initialInvoiceLines);

  const updateInvoiceField = (field: keyof InvoiceFormData, val: any) => {
    setInvoiceForm((prev) => ({ ...prev, [field]: val }));
  };

  const handleInvoiceLineChange = (id: string, field: keyof InvoiceLine, val: any) => {
    setInvoiceLines((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const updated = { ...row, [field]: val };
        if (field === "qty" || field === "rate") {
          const qty = field === "qty" ? val : row.qty;
          const rate = field === "rate" ? val : row.rate;
          const numQty = Number(qty) || 0;
          const numRate = Number(rate) || 0;
          updated.amount = Math.round(numQty * numRate * 100) / 100;
        }
        return updated;
      })
    );
  };

  const handleAddInvoiceLine = () => {
    const nextId = String(Date.now());
    setInvoiceLines((prev) => [
      ...prev,
      {
        id: nextId,
        productOrService: "",
        description: "",
        qty: "" as any,
        rate: 0,
        amount: 0,
      },
    ]);
  };

  const handleRemoveInvoiceLine = (id: string) => {
    setInvoiceLines((prev) => {
      const remaining = prev.filter((r) => r.id !== id);
      if (remaining.length === 0) {
        return [
          {
            id: String(Date.now()),
            productOrService: "",
            description: "",
            qty: "" as any,
            rate: 0,
            amount: 0,
          },
        ];
      }
      return remaining;
    });
  };

  useEffect(() => {
    const total = invoiceLines.reduce((acc, row) => acc + (Number(row.amount) || 0), 0);
    setInvoiceForm((prev) => ({
      ...prev,
      totalAmount: Math.round(total * 100) / 100,
    }));
  }, [invoiceLines]);

  const sortedResidents = React.useMemo(() => {
    return [...residents].sort((a: any, b: any) => {
      const nameA = (a.residentName || `${a.firstName || ""} ${a.lastName || ""}`).toLowerCase().trim();
      const nameB = (b.residentName || `${b.firstName || ""} ${b.lastName || ""}`).toLowerCase().trim();
      return nameA.localeCompare(nameB);
    });
  }, [residents]);

  const selectedResident = React.useMemo(() => {
    if (!selectedResidentId) return null;
    const found = residents.find((r) => (r.id || r.residentId) === selectedResidentId);
    if (found) return found;
    if (cms.residentFullName || ub04Form.patientName || invoiceForm.residentName) {
      return {
        id: selectedResidentId,
        residentName: cms.residentFullName || ub04Form.patientName || invoiceForm.residentName,
      };
    }
    return null;
  }, [residents, selectedResidentId, cms.residentFullName, ub04Form.patientName, invoiceForm.residentName]);

  const residentOptions = React.useMemo(() => {
    const active = sortedResidents.filter((r) => !r?.status || r?.status?.toUpperCase() === "ACTIVE");
    if (selectedResident && !active.some((r) => (r.id || r.residentId) === (selectedResident.id || selectedResident.residentId))) {
      return [selectedResident, ...active];
    }
    return active;
  }, [sortedResidents, selectedResident]);

  const effectiveSelectedInsuranceId = React.useMemo(() => {
    if (selectedInsuranceId && residentInsurances.some((c, idx) => (c.id || String(idx)) === selectedInsuranceId)) {
      return selectedInsuranceId;
    }
    const matched = residentInsurances.find((c) => {
      const cName = (c.insuranceCompanyName || c.provider || "").trim().toLowerCase();
      return (
        cName === (cms.carrierName || "").trim().toLowerCase() ||
        cName === (invoiceForm.billToClientName || "").trim().toLowerCase()
      );
    });
    if (matched) {
      return matched.id || String(residentInsurances.indexOf(matched));
    }
    if (residentInsurances.length > 0) {
      const primary = residentInsurances.find((c) =>
        (c.coverageType || c.insuranceType || "").toUpperCase().includes("PRIMARY")
      );
      if (primary) return primary.id || String(residentInsurances.indexOf(primary));
      return residentInsurances[0].id || "0";
    }
    return "";
  }, [selectedInsuranceId, residentInsurances, cms.carrierName, invoiceForm.billToClientName]);

  const effectiveUb04SelectedInsuranceId = React.useMemo(() => {
    if (selectedInsuranceId && residentInsurances.some((c, idx) => (c.id || String(idx)) === selectedInsuranceId)) {
      return selectedInsuranceId;
    }
    const matched = residentInsurances.find((c) =>
      (c.insuranceCompanyName || c.provider || "").trim().toLowerCase() === (ub04Form.payerName || "").trim().toLowerCase()
    );
    if (matched) {
      return matched.id || String(residentInsurances.indexOf(matched));
    }
    if (residentInsurances.length > 0) {
      const primary = residentInsurances.find((c) =>
        (c.coverageType || c.insuranceType || "").toUpperCase().includes("PRIMARY")
      );
      if (primary) return primary.id || String(residentInsurances.indexOf(primary));
      return residentInsurances[0].id || "0";
    }
    return "";
  }, [selectedInsuranceId, residentInsurances, ub04Form.payerName]);

  // Shape of BillingClaimDTO.formData written by buildClaimRequest
  type StoredFormData = {
    cms?: Partial<typeof cms>;
    serviceLines?: CmsServiceLine[];
    ub04Form?: Partial<typeof ub04Form>;
    ub04RevenueLines?: Ub04RevenueLine[];
    invoiceForm?: Partial<InvoiceFormData>;
    invoiceLines?: InvoiceLine[];
  };

  // Fetch facility details automatically on mount or when tenant changes
  useEffect(() => {
    let cancelled = false;
    const fetchFacilityDetails = async () => {
      const targetId = currentTenantId || user?.facilityId || user?.tenantId || localStorage.getItem("currentTenantId");
      let found: any = null;
      if (targetId) {
        try {
          found = await facilityService.getFacilityById(targetId);
        } catch (e) {
          // ignore error
        }
      }
      if (!found) {
        try {
          const listRes = await facilityService.getAllFacilities(0, 100);
          const items: any[] = (listRes as any)?.content ?? (listRes as any)?.items ?? (Array.isArray(listRes) ? listRes : []);
          if (targetId) {
            found = items.find((f: any) => String(f.id) === String(targetId));
          }
          if (!found && tenant?.name) {
            found = items.find((f: any) => (f.name || "").trim().toLowerCase() === tenant.name.trim().toLowerCase());
          }
          if (!found && items.length > 0) {
            found = items.find((f: any) => f.isPrimary) || items[0];
          }
        } catch (e) {
          // ignore error
        }
      }
      if (!cancelled && found) {
        setCurrentFacility(found);
      }
    };

    fetchFacilityDetails();
    return () => { cancelled = true; };
  }, [currentTenantId, tenant?.name, user?.facilityId, user?.tenantId]);

  // Sync effective facility details into both UB-04 and CMS-1500 forms
  useEffect(() => {
    const effFac = getEffectiveFacility(currentFacility, tenant?.name);
    setUb04Form((prev) => ({
      ...prev,
      providerName: prev.providerName || effFac.name,
      providerAddress: prev.providerAddress || effFac.address,
      providerCity: prev.providerCity || effFac.city,
      providerState: prev.providerState || effFac.state,
      providerZip: prev.providerZip || effFac.postalCode,
      providerPhone: prev.providerPhone || effFac.phone,
      providerNpi: prev.providerNpi || effFac.npiNumber,
      fedTaxNo: prev.fedTaxNo || effFac.taxId,
      typeOfBill: prev.typeOfBill || effFac.typeOfBill,
    }));

    setCms((prev) => ({
      ...prev,
      facilityName: prev.facilityName || effFac.name,
      facilityAddress: prev.facilityAddress || effFac.address,
      facilityCity: prev.facilityCity || effFac.city,
      facilityState: prev.facilityState || effFac.state,
      facilityZip: prev.facilityZip || effFac.postalCode,
      facilityNpi: prev.facilityNpi || effFac.npiNumber,
      billingProviderName: prev.billingProviderName || effFac.name,
      billingProviderAddress: prev.billingProviderAddress || effFac.address,
      billingProviderCity: prev.billingProviderCity || effFac.city,
      billingProviderState: prev.billingProviderState || effFac.state,
      billingProviderZip: prev.billingProviderZip || effFac.postalCode,
      billingProviderPhone: prev.billingProviderPhone || effFac.phone,
      billingProviderNpi: prev.billingProviderNpi || effFac.npiNumber,
      taxId: prev.taxId || effFac.taxId,
    }));
  }, [currentFacility, tenant?.name]);

  const effectiveFetchId = claimId || activeClaimId || queryClaimId || stateClaimId;

  // Reopen a saved claim: restore every form field from its stored form data
  useEffect(() => {
    if (!effectiveFetchId) return;
    let cancelled = false;
    billingService
      .getBillingClaimById(effectiveFetchId)
      .then((claim: any) => {
        if (cancelled) return;
        const data = (claim.formData || {}) as StoredFormData;
        if (data.cms) setCms({ ...emptyCmsState, ...data.cms });
        if (data.serviceLines?.length) setServiceLines(data.serviceLines);
        if (data.ub04Form) setUb04Form({ ...emptyUb04State, ...data.ub04Form });
        if (data.ub04RevenueLines?.length) setUb04RevenueLines(data.ub04RevenueLines);
        if (data.invoiceForm) setInvoiceForm({ ...emptyInvoiceState, ...data.invoiceForm });
        if (data.invoiceLines?.length) setInvoiceLines(data.invoiceLines);
        setSelectedResidentId(claim.residentId);
        setExistingClaim(claim);
        const resolvedId = claim.id || claim.claimId || claim._id || effectiveFetchId;
        if (resolvedId) setActiveClaimId(resolvedId);

        if (resolvedId) {
          billingService
            .getPatientSignature(resolvedId)
            .then((sigUrl) => {
              if (sigUrl && !cancelled) {
                setCms((prev) => ({
                  ...prev,
                  patientDigitalSignature: sigUrl,
                  patientSignatureImage: sigUrl,
                  patientSignatureType: (claim.patientSignatureType as any) || prev.patientSignatureType || "DIGITAL",
                  patientSignatureDate: claim.patientSignatureDate
                    ? formatDateToString(parseStringToDate(claim.patientSignatureDate))
                    : (prev.patientSignatureDate || getTodayFormatted()),
                }));
              }
            })
            .catch((e) => console.warn("Could not fetch patient signature for claim:", e));

          billingService
            .getInsuredSignature(resolvedId)
            .then((insUrl) => {
              if (insUrl && !cancelled) {
                setCms((prev) => ({
                  ...prev,
                  insuredDigitalSignature: insUrl,
                  insuredSignatureImage: insUrl,
                  insuredSignatureType: (claim.insuredSignatureType as any) || prev.insuredSignatureType || "DIGITAL",
                  insuredSignatureDate: claim.insuredSignatureDate
                    ? formatDateToString(parseStringToDate(claim.insuredSignatureDate))
                    : (prev.insuredSignatureDate || getTodayFormatted()),
                }));
              }
            })
            .catch((e) => console.warn("Could not fetch insured signature for claim:", e));
        }

        if (claim.residentId) {
          Promise.allSettled([
            residentService.getResidentById(claim.residentId),
            residentService.getResidentInsurances(claim.residentId),
          ]).then(([resResult, insResult]) => {
            if (cancelled) return;
            const res = resResult.status === "fulfilled" ? resResult.value : null;
            if (res) setCurrentResidentData(res);
            const apiCards: InsuranceCard[] = (insResult.status === "fulfilled" && Array.isArray(insResult.value)) ? insResult.value : [];
            const resCards: InsuranceCard[] = (res && Array.isArray(res.insuranceCards)) ? res.insuranceCards : [];
            const cardMap = new Map<string, InsuranceCard>();
            [...resCards, ...apiCards].forEach((card, idx) => {
              if (!card) return;
              const key = card.id || `${(card.insuranceCompanyName || card.provider || "").trim().toLowerCase()}-${(card.policyMemberId || card.policyNumber || "").trim()}-${idx}`;
              if (!cardMap.has(key)) {
                cardMap.set(key, card);
              }
            });
            const cards = Array.from(cardMap.values());
            if (cards.length > 0) {
              setResidentInsurances(cards);
              const savedCarrier = data.cms?.carrierName || data.ub04Form?.payerName || data.invoiceForm?.billToClientName || claim.payerName;
              const matched = cards.find((c) => (c.insuranceCompanyName || c.provider || "").trim().toLowerCase() === (savedCarrier || "").trim().toLowerCase());
              if (matched) {
                setSelectedInsuranceId(matched.id || String(cards.indexOf(matched)));
              } else {
                setSelectedInsuranceId(cards[0].id || "0");
              }
            }
          });
        }
      })
      .catch((err) => {
        // Fallback to fetch individual invoice if this is an invoice ID
        billingService
          .getIndividualInvoiceById(effectiveFetchId)
          .then((inv: IndividualInvoiceDTO) => {
            if (cancelled || !inv) return;
            setInvoiceForm({
              ...emptyInvoiceState,
              invoiceNumber: inv.invoiceNumber || "",
              invoiceDate: inv.invoiceDate || "",
              dueDate: inv.dueDate || "",
              terms: inv.paymentTerms || "Net 15",
              residentName: inv.residentName || "",
              residentCin: inv.residentCin || "",
              residentDob: inv.residentDob || "",
              servicePeriodFrom: inv.servicePeriodFrom || "",
              servicePeriodTo: inv.servicePeriodTo || "",
              managedCarePlan: inv.managedCarePlan || "",
              billToClientName: inv.payerName || inv.billTo || "",
              billToAddress: inv.billTo || "",
              shipToAddress: inv.shipTo || "",
              paymentMethod: inv.paymentMethod || "BANK / ACH Transfer",
              notes: inv.notes || "",
              totalAmount: inv.totalAmount || 0,
              statusLabel: inv.statusLabel || inv.status || "Draft",
            });
            if (inv.items && inv.items.length > 0) {
              setInvoiceLines(
                inv.items.map((item, idx) => ({
                  id: String(item.itemNumber || idx + 1),
                  productOrService: item.productOrService,
                  description: item.description,
                  qty: item.quantity,
                  rate: item.rate,
                  amount: item.amount,
                }))
              );
            }
            if (inv.residentId) {
              setSelectedResidentId(inv.residentId);
            }
            setExistingClaim({
              id: inv.id,
              claimNumber: inv.invoiceNumber,
              formType: ClaimFormType.INVOICE,
              status: (inv.status as any) || ClaimStatus.DRAFT,
              residentId: inv.residentId,
              residentName: inv.residentName,
              payerName: inv.payerName || inv.billTo || "MasterCare, Inc.",
              billedAmount: inv.totalAmount || 0,
              editable: true,
            } as any);
            const resolvedId = inv.id || effectiveFetchId;
            if (resolvedId) setActiveClaimId(resolvedId);
          })
          .catch(() => {
            if (!cancelled) setClaimLoadError(getApiErrorMessage(err, "Could not load the claim or invoice."));
          });
      });
    return () => {
      cancelled = true;
    };
    // emptyCmsState / emptyUb04State are constant defaults rebuilt each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFetchId]);

  const updateUb04Field = (field: string, value: any) => {
    setUb04Form((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddUb04RevenueLine = () => {
    setUb04RevenueLines((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        revCode: "",
        description: "",
        hcpcsRate: "",
        serviceDate: getTodayFormatted(),
        units: 1,
        totalCharges: "",
        nonCoveredCharges: "",
      },
    ]);
  };

  const handleRemoveUb04RevenueLine = (id: string) => {
    if (ub04RevenueLines.length === 1) return;
    setUb04RevenueLines((prev) => prev.filter((l) => l.id !== id));
  };

  // Load residents for autofill via Resident GET API
  const fetchResidents = async () => {
    setResidentsLoading(true);
    try {
      let list: any[] = [];
      const fid =
        user?.facilityId ||
        (user as any)?.facility?.id ||
        (user as any)?.tenantId ||
        currentTenantId ||
        tenant?.id;

      // 1. Try active lookup if facilityId is available and not localhost
      if (fid && fid !== "localhost") {
        try {
          const lookup = await residentService.getActiveLookup(String(fid));
          if (Array.isArray(lookup) && lookup.length > 0) {
            list = lookup;
          }
        } catch (e) {
          console.warn("Active lookup failed, trying getAllResidents", e);
        }
      }

      // 2. If list is still empty, try getAllResidents with facility
      if (list.length === 0) {
        try {
          const resp = await residentService.getAllResidents(
            0,
            200,
            fid && fid !== "localhost" ? String(fid) : undefined
          );
          const data = Array.isArray(resp)
            ? resp
            : resp?.content || (resp as any)?.data?.content || (resp as any)?.data || [];
          if (Array.isArray(data) && data.length > 0) {
            list = data;
          }
        } catch (e) {
          console.warn("getAllResidents with facility failed, trying fallback", e);
        }
      }

      // 3. Fallback: getAllResidents without facility filter
      if (list.length === 0) {
        try {
          const resp = await residentService.getAllResidents(0, 200);
          const data = Array.isArray(resp)
            ? resp
            : resp?.content || (resp as any)?.data?.content || (resp as any)?.data || [];
          if (Array.isArray(data) && data.length > 0) {
            list = data;
          }
        } catch (e) {
          console.error("All resident fetch attempts failed:", e);
        }
      }

      // Normalize and sort resident list alphabetically
      const normalized = list
        .map((r: any) => ({
          ...r,
          id: r.id || r.residentId,
          firstName: r.firstName || "",
          lastName: r.lastName || "",
          middleName: r.middleName || "",
          roomNumber: r.roomNumber || "",
        }))
        .filter((r: any) => Boolean(r.id))
        .sort((a: any, b: any) => {
          const nameA = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase().trim();
          const nameB = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase().trim();
          return nameA.localeCompare(nameB);
        });

      setResidents(normalized);
    } catch (err) {
      console.error("Could not fetch residents:", err);
    } finally {
      setResidentsLoading(false);
    }
  };

  useEffect(() => {
    fetchResidents();
  }, [user?.facilityId, currentTenantId, tenant?.id]);

  // Pre-select resident if residentId passed in URL query or navigation state
  useEffect(() => {
    const paramResidentId = searchParams.get("residentId") || (location.state as any)?.residentId;
    if (paramResidentId && !activeClaimId && !selectedResidentId) {
      handleResidentSelect(paramResidentId);
    }
  }, [searchParams, location.state, activeClaimId, selectedResidentId]);

  // Fetch patient and insured signatures via GET API when claim ID is available
  useEffect(() => {
    const targetId = claimId || activeClaimId;
    if (!targetId) return;
    let isCancelled = false;

    billingService.getPatientSignature(targetId).then((sigUrl) => {
      if (sigUrl && !isCancelled) {
        setCms((prev) => ({
          ...prev,
          patientDigitalSignature: sigUrl,
          patientSignatureImage: sigUrl,
        }));
      }
    });

    billingService.getInsuredSignature(targetId).then((insUrl) => {
      if (insUrl && !isCancelled) {
        setCms((prev) => ({
          ...prev,
          insuredDigitalSignature: insUrl,
          insuredSignatureImage: insUrl,
        }));
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [claimId, activeClaimId]);

  // Auto-calculate Total Charge & Balance Due from Box 24 lines
  useEffect(() => {
    const sum = serviceLines.reduce((acc, curr) => {
      const charge = parseFloat(String(curr.charges)) || 0;
      return acc + charge;
    }, 0);
    const paid = parseFloat(String(cms.amountPaid)) || 0;
    setCms((prev) => ({
      ...prev,
      totalCharge: sum,
      balanceDue: Math.max(0, sum - paid),
    }));
  }, [serviceLines, cms.amountPaid]);

  // Auto-calculate UB-04 Total Charges from revenue lines
  useEffect(() => {
    const sum = ub04RevenueLines.reduce((acc, curr) => {
      const charge = parseFloat(String(curr.totalCharges)) || 0;
      return acc + charge;
    }, 0);
    setUb04Form((prev) => ({
      ...prev,
      totalCharges: sum,
    }));
  }, [ub04RevenueLines]);

  // Helper to parse comma-separated addresses into street, city, state, zip
  const parseAddressComponents = (
    rawAddr?: string,
    rawCity?: string,
    rawState?: string,
    rawZip?: string
  ) => {
    let street = (rawAddr || "").trim();
    let city = (rawCity || "").trim();
    let state = (rawState || "").trim();
    let zip = (rawZip || "").trim();

    if (street.includes(",")) {
      const parts = street.split(",").map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 2) {
        street = parts[0];
        if (!city && parts.length >= 2) city = parts[1];
        if (!state && parts.length >= 3) state = parts[2];
        if (!zip) {
          for (let i = parts.length - 1; i >= 2; i--) {
            const part = parts[i];
            if (/^\d{3,10}(-\d{4})?$/.test(part) || /\d/.test(part)) {
              zip = part;
              break;
            }
          }
        }
      }
    }

    return { street, city, state, zip };
  };

  const parseGenderToSex = (g: any): "M" | "F" | "" => {
    if (!g) return "";
    const s = String(g).trim().toUpperCase();
    if (s === "F" || s.startsWith("FEMALE")) return "F";
    if (s === "M" || s.startsWith("MALE")) return "M";
    return "";
  };

  const normalizeRelationship = (rel: any): string => {
    if (!rel) return "Self";
    const s = String(rel).trim().toUpperCase();
    if (s === "SELF") return "Self";
    if (s === "SPOUSE") return "Spouse";
    if (s === "CHILD") return "Child";
    if (s === "OTHER") return "Other";
    return "Self";
  };

  const applyInsuranceCardToForm = (card: InsuranceCard, resObj?: any) => {
    if (!card) return;
    const res = resObj || currentResidentData;

    // Determine insurance program type
    let program = "";
    const insType = (card.insuranceType || card.planType || card.coverageType || "").toUpperCase();
    const compName = (card.insuranceCompanyName || card.provider || "").toUpperCase();
    if (insType.includes("MEDICARE") || compName.includes("MEDICARE") || res?.medicareNumber) {
      program = "MEDICARE";
    } else if (insType.includes("MEDICAID") || compName.includes("MEDICAID") || res?.medicaidNumber) {
      program = "MEDICAID";
    } else if (insType.includes("TRICARE") || insType.includes("CHAMPUS") || compName.includes("TRICARE")) {
      program = "TRICARE";
    } else if (insType.includes("CHAMPVA") || compName.includes("CHAMPVA")) {
      program = "CHAMPVA";
    } else if (insType.includes("GROUP") || compName.includes("GROUP")) {
      program = "GROUP_HEALTH_PLAN";
    } else if (insType.includes("FECA") || compName.includes("FECA")) {
      program = "FECA_BLK_LUNG";
    } else if (card.insuranceCompanyName || card.provider) {
      program = "OTHER";
    }

    const rawPolicy = card.policyMemberId || card.policyNumber || res?.medicareNumber || res?.medicaidNumber || res?.insurancePolicyNumber || "";
    const policyId = rawPolicy.toUpperCase().replace(/[^A-Z0-9-]/g, "").replace(/-{2,}/g, "-").replace(/^-+/, "");
    const carrierName = card.insuranceCompanyName || card.provider || "";
    const planName = card.planName || card.planType || "";
    const groupNo = card.groupNumber || res?.insuranceGroupNumber || "";
    const priorAuth = card.authorizationNumber || "";

    const cardRelationship = card.subscriberRelationship ? normalizeRelationship(card.subscriberRelationship) : "Self";
    const isSelfSubscriber = !card.subscriberRelationship || cardRelationship === "Self";

    const fullName = res ? [res.firstName, res.middleName, res.lastName].filter(Boolean).join(" ") : "";
    const resFormattedName = res?.lastName && res?.firstName ? `${res.lastName}, ${res.firstName}${res.middleName ? ` ${res.middleName}` : ""}`.trim() : fullName;
    const formattedDob = res?.dateOfBirth ? formatDateToString(parseStringToDate(res.dateOfBirth)) : "";
    const parsedPatientSex = parseGenderToSex(res?.gender);

    const patientAddr = res ? parseAddressComponents(
      res.address,
      res.city,
      res.state,
      res.zipCode || res.postalCode
    ) : { street: "", city: "", state: "", zip: "" };

    const insuredAddr = isSelfSubscriber
      ? patientAddr
      : parseAddressComponents(
        card.subscriberAddress,
        (card as any)?.subscriberCity,
        (card as any)?.subscriberState,
        (card as any)?.subscriberZip
      );

    const parsedInsuredSex = isSelfSubscriber
      ? parsedPatientSex
      : (parseGenderToSex(card.subscriberGender) || "M");

    const rawContactPhone = res ? ((res as any)?.phoneNumber || res.phone || (res as any)?.contactNumber || (res as any)?.mobilePhone || "") : "";
    const contactDigits = String(rawContactPhone).replace(/\D/g, "");
    const contactPhone = contactDigits.length === 11 && contactDigits.startsWith("1") ? contactDigits.slice(1) : contactDigits.slice(0, 10);
    const rawInsuredPhone = isSelfSubscriber ? contactPhone : ((card as any)?.subscriberPhone || card.insurancePhone || (card as any)?.phone || contactPhone);
    const insuredDigits = String(rawInsuredPhone).replace(/\D/g, "");
    const insuredPhone = insuredDigits.length === 11 && insuredDigits.startsWith("1") ? insuredDigits.slice(1) : insuredDigits.slice(0, 10);

    setCms((prev) => ({
      ...prev,
      carrierName: carrierName || prev.carrierName,
      carrierAddress: (card as any)?.address || (card as any)?.payerAddress || prev.carrierAddress,
      insuranceProgram: program || prev.insuranceProgram,
      insuredId: policyId || prev.insuredId,
      insurancePlanName: planName || prev.insurancePlanName,
      insuredPolicyGroup: groupNo || prev.insuredPolicyGroup,
      priorAuthNumber: priorAuth || prev.priorAuthNumber,
      patientRelationship: isSelfSubscriber ? "Self" : cardRelationship,

      // Insured demographics (Box 4, 7, 11)
      insuredLastName: isSelfSubscriber ? (resFormattedName || prev.insuredLastName) : (card.subscriberName || prev.insuredLastName),
      insuredFirstName: isSelfSubscriber ? (res?.firstName || prev.insuredFirstName) : (card.subscriberName ? card.subscriberName.split(" ")[0] : prev.insuredFirstName),
      insuredAddress: insuredAddr.street || prev.insuredAddress,
      insuredCity: insuredAddr.city || prev.insuredCity,
      insuredState: insuredAddr.state || prev.insuredState,
      insuredZip: insuredAddr.zip || prev.insuredZip,
      insuredPhone: insuredPhone || prev.insuredPhone,
      insuredDob: isSelfSubscriber ? (formattedDob || prev.insuredDob) : (card.subscriberDob ? formatDateToString(parseStringToDate(card.subscriberDob)) : prev.insuredDob),
      insuredSex: parsedInsuredSex || prev.insuredSex,
    }));

    setUb04Form((prev) => ({
      ...prev,
      payerName: carrierName || prev.payerName,
      healthPlanId: planName || prev.healthPlanId,
      payerAddress: (card as any)?.address || (card as any)?.payerAddress || prev.payerAddress,
      payerCityStateZip: (card as any)?.cityStateZip || prev.payerCityStateZip,
      insuredName: isSelfSubscriber ? (resFormattedName || fullName || prev.insuredName) : (card.subscriberName || resFormattedName || fullName),
      insuredUniqueId: policyId || prev.insuredUniqueId,
      insuredGroupName: planName || prev.insuredGroupName,
      insuredGroupNo: groupNo || prev.insuredGroupNo,
      treatmentAuthCode: priorAuth || prev.treatmentAuthCode,
    }));

    const cardPolicyMemberId =
      card.policyMemberId ||
      card.policyNumber ||
      (card as any)?.memberId ||
      (card as any)?.policyId ||
      res?.insurancePolicyNumber ||
      (res as any)?.policyMemberId ||
      "";

    setInvoiceForm((prev) => ({
      ...prev,
      residentCin: cardPolicyMemberId || prev.residentCin || "",
      billToClientName: carrierName || prev.billToClientName || "",
      billToAddress: (card as any)?.address || (card as any)?.payerAddress || carrierName || prev.billToAddress || "",
      shipToAddress: (card as any)?.address || (card as any)?.payerAddress || carrierName || prev.shipToAddress || "",
      managedCarePlan: carrierName || planName || prev.managedCarePlan,
    }));
  };

  const handleInsuranceCardSelect = (chosenId: string) => {
    setSelectedInsuranceId(chosenId);
    const card = residentInsurances.find((c, idx) => (c.id || String(idx)) === chosenId);
    if (card) {
      applyInsuranceCardToForm(card, currentResidentData);
    }
  };

  // Handle resident autofill - Instant synchronous display with non-blocking background enrichment
  const handleResidentSelect = (residentId: string) => {
    setSelectedResidentId(residentId);
    setPatientDobTouched(false);
    setDobPickerError(null);
    if (!residentId) {
      setCms(emptyCmsState);
      setUb04Form(emptyUb04State);
      setInvoiceForm(emptyInvoiceState);
      setInvoiceLines(initialInvoiceLines);
      setResidentInsurances([]);
      setSelectedInsuranceId("");
      setCurrentResidentData(null);
      return;
    }

    // 1. Instant synchronous autofill from cached list (0ms delay)
    const cached = residents.find((r) => (r.id || r.residentId) === residentId);
    setCurrentResidentData(cached || null);
    if (cached) {
      const cachedCards: InsuranceCard[] = Array.isArray(cached.insuranceCards) && cached.insuranceCards.length > 0
        ? cached.insuranceCards
        : [];
      if (cachedCards.length > 0) {
        setResidentInsurances(cachedCards);
        const pri = cachedCards.find((c) => (c.coverageType || c.insuranceType || "").toUpperCase().includes("PRIMARY")) || cachedCards[0];
        setSelectedInsuranceId(pri.id || String(cachedCards.indexOf(pri)));
      } else if (cached.insuranceProvider || cached.medicareNumber || cached.medicaidNumber) {
        const comp = cached.insuranceProvider || (cached.medicareNumber ? "Medicare" : cached.medicaidNumber ? "Medicaid" : "");
        if (comp) {
          const singleCard: InsuranceCard = {
            id: "cached-card",
            insuranceCompanyName: comp,
            insuranceType: cached.medicareNumber ? "MEDICARE" : cached.medicaidNumber ? "MEDICAID" : "OTHER",
            coverageType: "PRIMARY",
            policyMemberId: cached.insurancePolicyNumber || cached.medicareNumber || cached.medicaidNumber || "",
            groupNumber: cached.insuranceGroupNumber || "",
            subscriberRelationship: "Self",
          };
          setResidentInsurances([singleCard]);
          setSelectedInsuranceId("cached-card");
        } else {
          setResidentInsurances([]);
          setSelectedInsuranceId("");
        }
      } else {
        setResidentInsurances([]);
        setSelectedInsuranceId("");
      }

      const fullName = [cached.firstName, cached.middleName, cached.lastName].filter(Boolean).join(" ");
      const formattedDob = cached.dateOfBirth ? formatDateToString(parseStringToDate(cached.dateOfBirth)) : "";
      const cachedLastFirst = cached.lastName && cached.firstName ? `${cached.lastName}, ${cached.firstName}${cached.middleName ? ` ${cached.middleName}` : ""}`.trim() : fullName;
      const cachedMrn = cached.medicalRecordNumber || cached.mrnNumber || cached.emrNumber || cached.id;
      const rawCachedPolicy = cached.medicareNumber || cached.medicaidNumber || cached.insurancePolicyNumber || "";
      const cachedPolicy = rawCachedPolicy.toUpperCase().replace(/[^A-Z0-9-]/g, "").replace(/-{2,}/g, "-").replace(/^-+/, "");
      const cachedCarrier = cached.insuranceProvider || "";
      const cachedGroup = cached.insuranceGroupNumber || "";
      let cachedProgram = "";
      if (cached.medicareNumber) cachedProgram = "MEDICARE";
      else if (cached.medicaidNumber) cachedProgram = "MEDICAID";
      else if (cachedCarrier) cachedProgram = "OTHER";

      const cachedAddr = parseAddressComponents(
        cached.address,
        cached.city,
        cached.state,
        cached.zipCode || cached.postalCode
      );
      const cachedSex = parseGenderToSex(cached.gender);
      const rawCachedPhone = (cached as any).phoneNumber || cached.phone || (cached as any).contactNumber || (cached as any).mobilePhone || "";
      const cachedDigits = String(rawCachedPhone).replace(/\D/g, "");
      const cachedPhone = cachedDigits.length === 11 && cachedDigits.startsWith("1") ? cachedDigits.slice(1) : cachedDigits.slice(0, 10);

      const effFac = getEffectiveFacility(currentFacility, tenant?.name);
      const admissionFormatted = cached.admissionDate ? formatDateToString(parseStringToDate(cached.admissionDate)) : "";
      const todayFormatted = formatDateToString(new Date());

      setCms((prev) => ({
        ...prev,
        residentFullName: fullName || `${cached.firstName || ""} ${cached.lastName || ""}`.trim(),
        patientLastName: cached.lastName || prev.patientLastName,
        patientFirstName: cached.firstName || prev.patientFirstName,
        patientMiddleInitial: cached.middleName ? cached.middleName[0] : prev.patientMiddleInitial,
        patientDob: formattedDob || prev.patientDob,
        patientSex: cachedSex || prev.patientSex,
        patientRelationship: "Self",
        patientAddress: cachedAddr.street || prev.patientAddress,
        patientCity: cachedAddr.city || prev.patientCity,
        patientState: cachedAddr.state || prev.patientState,
        patientZip: cachedAddr.zip || prev.patientZip,
        patientPhone: cachedPhone || prev.patientPhone,
        patientAccountNo: cachedMrn || prev.patientAccountNo,
        diagA: cached.primaryDiagnosis || prev.diagA,
        facilityName: (cached as any).hospitalName || effFac.name,
        facilityAddress: (cached as any).hospitalAddress || (cached as any).pcpAddress || effFac.address,
        facilityCity: prev.facilityCity || effFac.city,
        facilityState: prev.facilityState || effFac.state,
        facilityZip: prev.facilityZip || effFac.postalCode,
        facilityNpi: prev.facilityNpi || effFac.npiNumber,
        billingProviderName: prev.billingProviderName || effFac.name,
        billingProviderAddress: prev.billingProviderAddress || effFac.address,
        billingProviderCity: prev.billingProviderCity || effFac.city,
        billingProviderState: prev.billingProviderState || effFac.state,
        billingProviderZip: prev.billingProviderZip || effFac.postalCode,
        billingProviderPhone: prev.billingProviderPhone || effFac.phone,
        billingProviderNpi: prev.billingProviderNpi || effFac.npiNumber,
        taxId: prev.taxId || effFac.taxId,
        referringProviderName: (cached as any).primaryCarePhysician || cached.attendingPhysicianName || (cached as any).doctorName || prev.referringProviderName,
        referringProviderNpi: (cached as any).pcpNPI || (cached as any).primaryPhysicianNpi || prev.referringProviderNpi,
        referringProviderQual: ((cached as any).primaryCarePhysician || cached.attendingPhysicianName) ? "DN" : prev.referringProviderQual,
        physicianSignature: (cached as any).primaryCarePhysician || cached.attendingPhysicianName || (cached as any).doctorName || prev.physicianSignature,
        carrierName: cachedCarrier || prev.carrierName,
        insuredId: cachedPolicy || prev.insuredId,
        insuredPolicyGroup: cachedGroup || prev.insuredPolicyGroup,
        insuranceProgram: cachedProgram || prev.insuranceProgram,

        // Insured demographics default to Self (patient's info)
        insuredLastName: cachedLastFirst || prev.insuredLastName,
        insuredFirstName: cached.firstName || prev.insuredFirstName,
        insuredAddress: cachedAddr.street || prev.insuredAddress,
        insuredCity: cachedAddr.city || prev.insuredCity,
        insuredState: cachedAddr.state || prev.insuredState,
        insuredZip: cachedAddr.zip || prev.insuredZip,
        insuredPhone: cachedPhone || prev.insuredPhone,
        insuredDob: formattedDob || prev.insuredDob,
        insuredSex: cachedSex || prev.insuredSex,
      }));

      setUb04Form((prev) => ({
        ...prev,
        providerName: effFac.name,
        providerAddress: prev.providerAddress || effFac.address,
        providerCity: prev.providerCity || effFac.city,
        providerState: prev.providerState || effFac.state,
        providerZip: prev.providerZip || effFac.postalCode,
        providerPhone: prev.providerPhone || effFac.phone,
        providerNpi: prev.providerNpi || effFac.npiNumber,
        fedTaxNo: prev.fedTaxNo || effFac.taxId,
        typeOfBill: prev.typeOfBill || effFac.typeOfBill,
        patientName: cachedLastFirst || prev.patientName,
        patientDob: formattedDob || prev.patientDob,
        patientSex: cachedSex || "M",
        patientAddress: cachedAddr.street || prev.patientAddress,
        patientCity: cachedAddr.city || prev.patientCity,
        patientState: cachedAddr.state || prev.patientState,
        patientZip: cachedAddr.zip || prev.patientZip,
        medicalRecordNo: cachedMrn,
        patientControlNo: cachedMrn,
        principalDiagnosis: cached.primaryDiagnosis || prev.principalDiagnosis,
        payerName: cachedCarrier || prev.payerName,
        payerAddress: prev.payerAddress,
        payerCityStateZip: prev.payerCityStateZip,
        insuredName: cachedLastFirst || prev.insuredName,
        insuredUniqueId: cachedPolicy || prev.insuredUniqueId,
        insuredGroupNo: cachedGroup || prev.insuredGroupNo,
        treatmentAuthCode: prev.treatmentAuthCode,
        statementCoversFrom: prev.statementCoversFrom || admissionFormatted,
        statementCoversThrough: prev.statementCoversThrough || todayFormatted,
        creationDate: prev.creationDate || todayFormatted,
        icdIndicator: prev.icdIndicator || "0",
        attendingQual: prev.attendingQual || "DN",
        attendingNpi: (cached as any)?.pcpNPI || (cached as any)?.primaryPhysicianNpi || prev.attendingNpi,
        attendingLastName: (cached as any)?.attendingPhysicianName ? (cached as any).attendingPhysicianName.split(" ").slice(-1)[0] : prev.attendingLastName,
        attendingFirstName: (cached as any)?.attendingPhysicianName ? (cached as any).attendingPhysicianName.split(" ")[0] : prev.attendingFirstName,
      }));

      const cachedPrimaryCard =
        cachedCards.find((c) => (c.coverageType || c.insuranceType || "").toUpperCase().includes("PRIMARY")) ||
        cachedCards[0];
      const cachedPolicyId =
        cachedPrimaryCard?.policyMemberId ||
        cachedPrimaryCard?.policyNumber ||
        (cachedPrimaryCard as any)?.memberId ||
        (cachedPrimaryCard as any)?.policyId ||
        cached.insurancePolicyNumber ||
        (cached as any)?.policyMemberId ||
        cached.medicaidNumber ||
        cached.medicareNumber ||
        cached.mrnNumber ||
        cached.id ||
        "";

      setInvoiceForm((prev) => ({
        ...prev,
        residentName: fullName || `${cached.firstName || ""} ${cached.lastName || ""}`.trim(),
        residentDob: formattedDob || prev.residentDob || "",
        residentCin: cachedPolicyId || prev.residentCin || "",
        servicePeriodFrom: admissionFormatted || prev.servicePeriodFrom || "",
        servicePeriodTo: admissionFormatted ? (todayFormatted || prev.servicePeriodTo || "") : (prev.servicePeriodTo || ""),
        managedCarePlan: cachedCarrier || (cachedCards[0]?.insuranceCompanyName) || prev.managedCarePlan || "",
        providerName: effFac.name || prev.providerName || "Lakewood Villa Care Center",
        providerDba: tenant?.name ? `${tenant.name} dba` : prev.providerDba || "JSNU Healthcare LLC dba",
        providerAddress: effFac.address || prev.providerAddress || "8708 Gerber Rd",
        providerCity: effFac.city || prev.providerCity || "Sacramento",
        providerState: effFac.state || prev.providerState || "CA",
        providerZip: effFac.postalCode || prev.providerZip || "95828",
        providerPhone: effFac.phone || prev.providerPhone || "+1 (248) 294-8930",
        providerEmail: (effFac as any)?.email || prev.providerEmail || "jsnuhealthcare@gmail.com",
        providerWebsite: (effFac as any)?.website || prev.providerWebsite || "www.lakewoodvillacarecenter.com",
        providerLicenseNo: effFac.taxId || (effFac as any)?.licenseNumber || prev.providerLicenseNo || "342701553",
        providerNpi: effFac.npiNumber || prev.providerNpi || "1558178681",
        billToClientName: cachedCarrier || prev.billToClientName || "",
        billToAddress: (cachedCards[0] as any)?.address || (cachedCards[0] as any)?.payerAddress || cachedCarrier || prev.billToAddress || "",
        shipToAddress: (cachedCards[0] as any)?.address || (cachedCards[0] as any)?.payerAddress || cachedCarrier || prev.shipToAddress || "",
        invoiceNumber: prev.invoiceNumber || "",
        terms: prev.terms || "Net 15",
        invoiceDate: prev.invoiceDate || "",
        dueDate: prev.dueDate || "",
        paymentMethod: prev.paymentMethod || "BANK",
      }));
    }

    // 2. Fetch full resident details, insurances & check draft claim in parallel background (non-blocking)
    const currentReqId = ++residentSelectRequestIdRef.current;

    Promise.allSettled([
      residentService.getResidentById(residentId),
      residentService.getResidentInsurances(residentId),
      (!activeClaimId && !claimId) ? billingService.getBillingClaimsByResident(residentId) : Promise.resolve(null),
      (selectedForm === "INVOICE" && !queryClaimId && !activeClaimId && !claimId) ? billingService.getResidentPreviousServices(residentId) : Promise.resolve(null),
    ]).then(([resResult, insResult, claimsResult, prevServicesResult]) => {
      // Discard if selection changed
      if (residentSelectRequestIdRef.current !== currentReqId) return;

      if (selectedForm === "INVOICE" && !queryClaimId && !activeClaimId && !claimId && prevServicesResult?.status === "fulfilled" && prevServicesResult.value) {
        const lines = normalizePreviousServices(prevServicesResult.value);
        if (lines.length > 0) {
          setInvoiceLines(lines);
        }
      }

      const res = resResult.status === "fulfilled" ? resResult.value : null;
      if (res) setCurrentResidentData(res);

      const apiCards: InsuranceCard[] = (insResult.status === "fulfilled" && Array.isArray(insResult.value)) ? insResult.value : [];
      const resCards: InsuranceCard[] = (res && Array.isArray(res.insuranceCards)) ? res.insuranceCards : [];

      const cardMap = new Map<string, InsuranceCard>();
      [...resCards, ...apiCards].forEach((card, idx) => {
        if (!card) return;
        const key = card.id || `${(card.insuranceCompanyName || card.provider || "").trim().toLowerCase()}-${(card.policyMemberId || card.policyNumber || "").trim()}-${idx}`;
        if (!cardMap.has(key)) {
          cardMap.set(key, card);
        }
      });
      let insuranceCards = Array.from(cardMap.values());

      if (insuranceCards.length === 0 && (res?.insuranceProvider || cached?.insuranceProvider || res?.medicareNumber || res?.medicaidNumber)) {
        const compName = res?.insuranceProvider || cached?.insuranceProvider || (res?.medicareNumber ? "Medicare" : res?.medicaidNumber ? "Medicaid" : "");
        if (compName) {
          insuranceCards = [{
            id: "primary-insurance",
            insuranceCompanyName: compName,
            insuranceType: res?.medicareNumber ? "MEDICARE" : res?.medicaidNumber ? "MEDICAID" : "OTHER",
            coverageType: "PRIMARY",
            policyMemberId: res?.insurancePolicyNumber || cached?.insurancePolicyNumber || res?.medicareNumber || res?.medicaidNumber || "",
            groupNumber: res?.insuranceGroupNumber || cached?.insuranceGroupNumber || "",
            subscriberRelationship: "Self",
          }];
        }
      }

      setResidentInsurances(insuranceCards);

      // Find Primary card or default to 1st card
      let primaryCard = insuranceCards[0];
      let selectedId = primaryCard ? (primaryCard.id || "0") : "";
      const foundPrimary = insuranceCards.find((c) =>
        (c.coverageType || c.insuranceType || "").toUpperCase().includes("PRIMARY")
      );
      if (foundPrimary) {
        primaryCard = foundPrimary;
        selectedId = foundPrimary.id || String(insuranceCards.indexOf(foundPrimary));
      }
      setSelectedInsuranceId(selectedId);

      if (res) {
        const fullName = [res.firstName, res.middleName, res.lastName].filter(Boolean).join(" ");
        const formattedDob = res.dateOfBirth ? formatDateToString(parseStringToDate(res.dateOfBirth)) : "";
        const resFormattedName = res.lastName && res.firstName ? `${res.lastName}, ${res.firstName}${res.middleName ? ` ${res.middleName}` : ""}`.trim() : fullName;
        const resMrn = res.medicalRecordNumber || res.mrnNumber || res.emrNumber || res.id || "";

        const patientAddr = parseAddressComponents(
          res.address,
          res.city,
          res.state,
          (res as any).zipCode || res.postalCode
        );

        const parsedPatientSex = parseGenderToSex(res.gender);
        const rawContactPhone = (res as any)?.phoneNumber || res.phone || (res as any)?.contactNumber || (res as any)?.mobilePhone || (res as any)?.subscriberPhone || "";
        const contactDigits = String(rawContactPhone).replace(/\D/g, "");
        const contactPhone = contactDigits.length === 11 && contactDigits.startsWith("1") ? contactDigits.slice(1) : contactDigits.slice(0, 10);

        const effFac = getEffectiveFacility(currentFacility, tenant?.name);
        const admissionFormatted = res.admissionDate ? formatDateToString(parseStringToDate(res.admissionDate)) : "";
        const todayFormatted = formatDateToString(new Date());

        setCms((prev) => ({
          ...prev,
          residentFullName: fullName || `${res.firstName || ""} ${res.lastName || ""}`.trim(),
          patientLastName: res.lastName || prev.patientLastName,
          patientFirstName: res.firstName || prev.patientFirstName,
          patientMiddleInitial: res.middleName ? res.middleName[0] : prev.patientMiddleInitial,
          patientDob: formattedDob || prev.patientDob,
          patientSex: parsedPatientSex || prev.patientSex,
          patientAddress: patientAddr.street || prev.patientAddress,
          patientCity: patientAddr.city || prev.patientCity,
          patientState: patientAddr.state || prev.patientState,
          patientZip: patientAddr.zip || prev.patientZip,
          patientPhone: contactPhone || prev.patientPhone,
          patientAccountNo: resMrn || prev.patientAccountNo,

          // Diagnosis
          diagA: res.primaryDiagnosis || (res as any).secondaryDiagnoses || prev.diagA,

          // Facility & Clinician Info
          facilityName: (res as any).hospitalName || effFac.name,
          facilityAddress: (res as any).hospitalAddress || effFac.address,
          facilityCity: prev.facilityCity || effFac.city,
          facilityState: prev.facilityState || effFac.state,
          facilityZip: prev.facilityZip || effFac.postalCode,
          facilityNpi: prev.facilityNpi || effFac.npiNumber,
          billingProviderName: prev.billingProviderName || effFac.name,
          billingProviderAddress: prev.billingProviderAddress || effFac.address,
          billingProviderCity: prev.billingProviderCity || effFac.city,
          billingProviderState: prev.billingProviderState || effFac.state,
          billingProviderZip: prev.billingProviderZip || effFac.postalCode,
          billingProviderPhone: prev.billingProviderPhone || effFac.phone,
          billingProviderNpi: prev.billingProviderNpi || effFac.npiNumber,
          taxId: prev.taxId || effFac.taxId,
          referringProviderName: (res as any).primaryCarePhysician || res.attendingPhysicianName || (res as any).doctorName || prev.referringProviderName,
          referringProviderNpi: (res as any).pcpNPI || (res as any).primaryPhysicianNpi || prev.referringProviderNpi,
          referringProviderQual: ((res as any).primaryCarePhysician || res.attendingPhysicianName) ? "DN" : prev.referringProviderQual,
          physicianSignature: (res as any).primaryCarePhysician || res.attendingPhysicianName || (res as any).doctorName || prev.physicianSignature,
        }));

        setUb04Form((prev) => ({
          ...prev,
          providerName: effFac.name,
          providerAddress: prev.providerAddress || effFac.address,
          providerCity: prev.providerCity || effFac.city,
          providerState: prev.providerState || effFac.state,
          providerZip: prev.providerZip || effFac.postalCode,
          providerPhone: prev.providerPhone || effFac.phone,
          providerNpi: prev.providerNpi || effFac.npiNumber,
          fedTaxNo: prev.fedTaxNo || effFac.taxId,
          typeOfBill: prev.typeOfBill || effFac.typeOfBill,
          patientControlNo: resMrn || prev.patientControlNo,
          medicalRecordNo: resMrn || prev.medicalRecordNo,
          statementCoversFrom: prev.statementCoversFrom || admissionFormatted,
          statementCoversThrough: prev.statementCoversThrough || todayFormatted,
          patientName: resFormattedName || fullName,
          patientAddress: patientAddr.street || prev.patientAddress,
          patientCity: patientAddr.city || prev.patientCity,
          patientState: patientAddr.state || prev.patientState,
          patientZip: patientAddr.zip || prev.patientZip,
          patientDob: formattedDob || prev.patientDob,
          patientSex: parsedPatientSex || "M",
          principalDiagnosis: res.primaryDiagnosis || prev.principalDiagnosis,
          creationDate: prev.creationDate || todayFormatted,
          icdIndicator: prev.icdIndicator || "0",
          attendingQual: prev.attendingQual || "DN",
          attendingNpi: (res as any)?.pcpNPI || (res as any)?.primaryPhysicianNpi || prev.attendingNpi,
          attendingLastName: res.attendingPhysicianName ? res.attendingPhysicianName.split(" ").slice(-1)[0] : ((res as any)?.primaryPhysicianLastName || prev.attendingLastName),
          attendingFirstName: res.attendingPhysicianName ? res.attendingPhysicianName.split(" ")[0] : ((res as any)?.primaryPhysicianFirstName || prev.attendingFirstName),
        }));

        const payerInsuranceName = (apiCards[0]?.insuranceCompanyName) || (apiCards[0]?.provider) || (res as any)?.insuranceProvider || "";
        const payerAddress = (apiCards[0] as any)?.address || (apiCards[0] as any)?.payerAddress || payerInsuranceName || "";

        const effPolicyMemberId =
          primaryCard?.policyMemberId ||
          primaryCard?.policyNumber ||
          (primaryCard as any)?.memberId ||
          (primaryCard as any)?.policyId ||
          apiCards[0]?.policyMemberId ||
          apiCards[0]?.policyNumber ||
          (apiCards[0] as any)?.memberId ||
          (apiCards[0] as any)?.policyId ||
          res.insurancePolicyNumber ||
          (res as any)?.policyMemberId ||
          res.medicaidNumber ||
          res.medicareNumber ||
          resMrn ||
          "";

        setInvoiceForm((prev) => ({
          ...prev,
          residentName: fullName || `${res.firstName || ""} ${res.lastName || ""}`.trim() || prev.residentName,
          residentDob: formattedDob || prev.residentDob,
          residentCin: effPolicyMemberId || prev.residentCin,
          servicePeriodFrom: admissionFormatted || prev.servicePeriodFrom,
          servicePeriodTo: todayFormatted || prev.servicePeriodTo,
          managedCarePlan: payerInsuranceName || prev.managedCarePlan,
          billToClientName: payerInsuranceName || prev.billToClientName || "",
          billToAddress: payerAddress || prev.billToAddress || "",
          shipToAddress: payerAddress || prev.shipToAddress || "",
          providerName: effFac.name || prev.providerName,
          providerAddress: effFac.address || prev.providerAddress,
          providerCity: effFac.city || prev.providerCity,
          providerState: effFac.state || prev.providerState,
          providerZip: effFac.postalCode || prev.providerZip,
          providerPhone: effFac.phone || prev.providerPhone,
          providerNpi: effFac.npiNumber || prev.providerNpi,
        }));

        if (primaryCard) {
          applyInsuranceCardToForm(primaryCard, res);
        }
      }

      // Check if this resident already has an existing draft claim
      if (claimsResult.status === "fulfilled" && claimsResult.value) {
        const resClaims = claimsResult.value;
        const draftClaim = resClaims?.find(
          (c: any) => (c.status === "DRAFT" || c.status === ClaimStatus.DRAFT) && (c.id || c.claimId)
        );
        if (draftClaim && !activeClaimId && !claimId) {
          const foundId = draftClaim.id || (draftClaim as any).claimId;
          setActiveClaimId(foundId);
          setExistingClaim(draftClaim);
        }
      }
    }).catch((err) => {
      console.warn("Background resident enrichment notice:", err);
    });
  };

  const updateField = (field: keyof typeof cms, val: any) => {
    setCms((prev) => ({ ...prev, [field]: val }));
  };

  // Format DD/MM/YYYY date input
  const handleDateChange = (field: keyof typeof cms, rawVal: string) => {
    const digitsOnly = rawVal.replace(/\D/g, "").slice(0, 8);
    let formatted = digitsOnly;
    if (digitsOnly.length > 4) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
    } else if (digitsOnly.length > 2) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    }
    updateField(field, formatted);
  };

  const getTodayFormatted = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const parseStringToDate = (str: string): Date | null => {
    if (!str) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const parts = str.split("-");
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2].slice(0, 2), 10);
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime())) return d;
    }
    const parts = str.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        const d = new Date(year, month, day);
        if (!isNaN(d.getTime())) return d;
      }
    }
    const fallback = new Date(str);
    return isNaN(fallback.getTime()) ? null : fallback;
  };

  const formatDateToString = (d: Date | null): string => {
    if (!d || isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatIsoSignatureDate = (dateStr?: string): string => {
    if (!dateStr || !dateStr.trim()) {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    }
    const trimmed = dateStr.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const parsed = parseStringToDate(trimmed);
    if (parsed && !isNaN(parsed.getTime())) {
      return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
    }
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  };


  // ── CMS-1500 Section 1 Validation Functions ──

  const validateCarrierName = (val?: string): string => {
    if (!val || !val.trim()) {
      return "Carrier / Payer Name is required.";
    }
    return "";
  };

  const VALID_INSURANCE_PROGRAMS = [
    "MEDICARE",
    "MEDICAID",
    "TRICARE",
    "CHAMPVA",
    "GROUP_HEALTH_PLAN",
    "GROUP_HEALTH",
    "FECA_BLK_LUNG",
    "FECA",
    "OTHER",
  ];

  const validateInsuranceProgram = (val?: string): string => {
    if (!val || !val.trim()) {
      return "Type of Insurance is required.";
    }
    if (!VALID_INSURANCE_PROGRAMS.includes(val.trim().toUpperCase())) {
      return "Only configured insurance types are allowed.";
    }
    return "";
  };

  interface InsuranceProviderRule {
    name: string;
    min: number;
    max: number;
  }

  const INSURANCE_PROVIDER_RULES: Record<string, InsuranceProviderRule> = {
    MEDICARE: {
      name: "Medicare",
      min: 9,
      max: 13,
    },
    MEDICAID: {
      name: "Medicaid",
      min: 8,
      max: 15,
    },
    TRICARE: {
      name: "Tricare",
      min: 9,
      max: 12,
    },
    CHAMPVA: {
      name: "CHAMPVA",
      min: 9,
      max: 12,
    },
    GROUP_HEALTH_PLAN: {
      name: "Group Health Plan",
      min: 6,
      max: 20,
    },
    GROUP_HEALTH: {
      name: "Group Health Plan",
      min: 6,
      max: 20,
    },
    FECA_BLK_LUNG: {
      name: "FECA Black Lung",
      min: 9,
      max: 12,
    },
    FECA: {
      name: "FECA Black Lung",
      min: 9,
      max: 12,
    },
    OTHER: {
      name: "Insured's",
      min: 4,
      max: 30,
    },
  };

  const getInsuranceProviderRule = (program?: string, carrier?: string): InsuranceProviderRule => {
    const p = (program || "").toUpperCase().trim();
    const c = (carrier || "").toUpperCase().trim();
    if (p === "MEDICARE" || (!p && c.includes("MEDICARE"))) return INSURANCE_PROVIDER_RULES.MEDICARE;
    if (p === "MEDICAID" || (!p && c.includes("MEDICAID"))) return INSURANCE_PROVIDER_RULES.MEDICAID;
    if (p === "TRICARE" || (!p && c.includes("TRICARE"))) return INSURANCE_PROVIDER_RULES.TRICARE;
    if (p === "CHAMPVA" || (!p && c.includes("CHAMPVA"))) return INSURANCE_PROVIDER_RULES.CHAMPVA;
    if (p === "FECA_BLK_LUNG" || p === "FECA" || (!p && (c.includes("FECA") || c.includes("BLACK LUNG")))) return INSURANCE_PROVIDER_RULES.FECA_BLK_LUNG;
    if (p === "GROUP_HEALTH_PLAN" || p === "GROUP_HEALTH" || (!p && (c.includes("BLUE CROSS") || c.includes("BCBS") || c.includes("AETNA") || c.includes("CIGNA") || c.includes("UNITED") || c.includes("HUMANA")))) {
      return INSURANCE_PROVIDER_RULES.GROUP_HEALTH_PLAN;
    }
    return INSURANCE_PROVIDER_RULES.OTHER;
  };

  const validateInsuredId = (val?: string, program?: string, carrier?: string): string => {
    if (!val || !val.trim()) {
      return "Insured's I.D. Number is required.";
    }
    const trimmed = val.trim();
    if (/\s/.test(trimmed)) {
      return "Insured's I.D. Number cannot contain spaces.";
    }
    // Allowed characters: Letters (A–Z), numbers (0–9), and hyphens (-)
    if (!/^[A-Za-z0-9-]+$/.test(trimmed)) {
      return "Insured's I.D. Number can only contain letters (A–Z), numbers (0–9), and hyphens (-). Special characters are not allowed.";
    }
    if (!/[A-Za-z0-9]/.test(trimmed)) {
      return "Insured's I.D. Number must contain at least one letter or number.";
    }
    if (trimmed.endsWith("-")) {
      return "Insured's I.D. Number cannot end with a hyphen.";
    }
    const rule = getInsuranceProviderRule(program || cms.insuranceProgram, carrier || cms.carrierName);
    if (trimmed.length < rule.min || trimmed.length > rule.max) {
      return `${rule.name} I.D. Number must be between ${rule.min} and ${rule.max} characters.`;
    }
    return "";
  };

  const validateResidentFullName = (val?: string): string => {
    if (!val || !val.trim()) {
      return "Resident Full Name is required and cannot be only spaces.";
    }
    const trimmed = val.trim();
    if (trimmed.length < 2) {
      return "Resident name must be at least 2 characters.";
    }
    // Allow supported name characters, spaces, apostrophes and hyphens; reject spaces-only or numbers/symbols
    if (!/^[a-zA-Z\u00C0-\u024F\s'\-]+$/.test(trimmed)) {
      return "Name can only contain letters, spaces, hyphens (-), and apostrophes (').";
    }
    return "";
  };

  const validateResidentCity = (val?: string): string => {
    if (!val) return "";
    const trimmed = val.trim();
    // Invalid if only spaces
    if (!trimmed) {
      return "Please enter a valid city name.";
    }
    // Allowed: Letters, spaces, hyphens (-), apostrophes ('), and periods (.)
    // Invalid: Numbers, special characters
    if (!/^[a-zA-Z\s.'-]+$/.test(trimmed)) {
      return "Please enter a valid city name.";
    }
    // Must contain at least one letter
    if (!/[a-zA-Z]/.test(trimmed)) {
      return "Please enter a valid city name.";
    }
    return "";
  };

  const validateResidentState = (val?: string): string => {
    if (!val) return "";
    const trimmed = val.trim();
    // Invalid if only spaces
    if (!trimmed) {
      return "Please enter a valid state name.";
    }
    // Allowed: Letters, spaces, hyphens (-)
    // Invalid: Numbers, special characters
    if (!/^[a-zA-Z\s-]+$/.test(trimmed)) {
      return "Please enter a valid state name.";
    }
    // Must contain at least one letter
    if (!/[a-zA-Z]/.test(trimmed)) {
      return "Please enter a valid state name.";
    }
    return "";
  };

  const validateZipCode = (val?: string): string => {
    if (!val) return "";
    const trimmed = val.trim();
    if (!trimmed) {
      return "";
    }
    if (!/^[a-zA-Z0-9]+$/.test(trimmed)) {
      return "ZIP Code must contain only letters and numbers.";
    }
    return "";
  };

  const validatePhoneNumber = (val?: string): string => {
    if (!val || !val.trim()) return "";
    const trimmed = val.trim();

    // Disallow non-digit characters
    if (/\D/.test(trimmed)) {
      return "Please enter a valid 10-digit telephone number.";
    }

    if (trimmed.length < 10) {
      return "Please enter a complete 10-digit telephone number.";
    }

    if (trimmed.length > 10) {
      return "Please enter a valid 10-digit telephone number.";
    }

    return "";
  };

  const validatePatientSex = (val?: string): string => {
    if (!val || !val.trim()) {
      return "Resident's Gender is required.";
    }
    const normalized = parseGenderToSex(val);
    if (!normalized || (normalized !== "M" && normalized !== "F")) {
      return "Accept only configured gender values (Male, Female).";
    }
    return "";
  };

  const validatePatientDob = (dobValue: string | null | undefined, isTouched = true): string => {
    if (!isTouched) return "";
    if (!dobValue || !dobValue.trim()) {
      return "Resident's birth date is required.";
    }
    const trimmed = dobValue.trim();
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = trimmed.match(regex);
    if (!match) {
      return "Invalid date format. Please use DD/MM/YYYY.";
    }
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    if (month < 1 || month > 12) {
      return "Invalid month. Month must be between 01 and 12.";
    }
    if (year < 1900) {
      return "Please enter a valid birth year (1900 or later).";
    }
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
      return `Invalid day. Day must be between 01 and ${daysInMonth} for this month.`;
    }
    const dateObj = new Date(year, month - 1, day);
    if (
      isNaN(dateObj.getTime()) ||
      dateObj.getFullYear() !== year ||
      dateObj.getMonth() !== month - 1 ||
      dateObj.getDate() !== day
    ) {
      return "Please enter a valid birth date.";
    }
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (dateObj > today) {
      return "Birth date cannot be in the future.";
    }
    return "";
  };

  // ── CMS-1500 Section 3 Validation Functions (Items 9 - 11) ──

  const US_STATES_AND_TERRITORIES = new Set([
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
    "DC", "AS", "GU", "MP", "PR", "VI", "FM", "MH", "PW", "AA", "AE", "AP",
  ]);

  const validateOtherInsuredName = (val?: string): string => {
    if (!val) return "";
    if (!val.trim()) {
      return "Please enter a valid other insured's name.";
    }
    const trimmed = val.trim();
    // Allow letters, spaces, hyphens, apostrophes, and commas
    // Reject numbers-only input and invalid special characters
    if (/[0-9]/.test(trimmed) || /[^A-Za-z\s,.'-]/.test(trimmed) || !/[A-Za-z]/.test(trimmed)) {
      return "Please enter a valid other insured's name.";
    }
    return "";
  };

  const validateOtherInsuredPolicy = (val?: string): string => {
    if (!val) return "";
    if (!val.trim()) {
      return "Please enter a valid policy or group number.";
    }
    const trimmed = val.trim();
    // Allow letters, numbers, hyphens, and valid policy-number characters
    if (/[^A-Za-z0-9\-_/ ]/.test(trimmed) || !/[A-Za-z0-9]/.test(trimmed)) {
      return "Please enter a valid policy or group number.";
    }
    return "";
  };

  const validateOtherInsuredPlanName = (val?: string): string => {
    if (!val) return "";
    if (!val.trim()) {
      return "Please enter a valid insurance plan name.";
    }
    const trimmed = val.trim();
    // Allow letters, numbers, spaces, and common punctuation
    if (/[^A-Za-z0-9\s.,'&/()#-]/.test(trimmed) || !/[A-Za-z0-9]/.test(trimmed)) {
      return "Please enter a valid insurance plan name.";
    }
    return "";
  };

  const validateAutoAccidentState = (stateVal?: string, autoAccident?: string): string => {
    const isAutoYes = (autoAccident || "").toUpperCase() === "YES";
    const trimmed = (stateVal || "").trim().toUpperCase();
    if (isAutoYes) {
      if (!trimmed || !US_STATES_AND_TERRITORIES.has(trimmed)) {
        return "Please enter a valid U.S. state.";
      }
      return "";
    }
    if (trimmed && !US_STATES_AND_TERRITORIES.has(trimmed)) {
      return "Please enter a valid U.S. state.";
    }
    return "";
  };

  const validateClaimCodes10d = (val?: string): string => {
    if (!val) return "";
    if (!val.trim()) {
      return "Please enter a valid claim code.";
    }
    const trimmed = val.trim();
    // Accept valid claim codes according to existing CMS-1500/NUCC implementation
    if (/[^A-Za-z0-9\s,-]/.test(trimmed) || !/[A-Za-z0-9]/.test(trimmed)) {
      return "Please enter a valid claim code.";
    }
    return "";
  };

  const validateInsuredPolicyGroup = (val?: string): string => {
    if (!val) return "";
    if (!val.trim()) {
      return "Please enter a valid policy or group number.";
    }
    const trimmed = val.trim();
    // Allow valid policy/group identifiers, including letters, numbers, hyphens, and supported special characters
    if (/[^A-Za-z0-9\-_/#. ]/.test(trimmed) || !/[A-Za-z0-9]/.test(trimmed)) {
      return "Please enter a valid policy or group number.";
    }
    return "";
  };

  const validateOtherClaimId = (val?: string): string => {
    if (!val) return "";
    if (!val.trim()) {
      return "Please enter a valid claim ID.";
    }
    const trimmed = val.trim();
    if (/[^A-Za-z0-9\-_./# ]/.test(trimmed) || !/[A-Za-z0-9]/.test(trimmed)) {
      return "Please enter a valid claim ID.";
    }
    return "";
  };

  const validateInsurancePlanName = (val?: string, anotherBenefitPlan?: string): string => {
    const isAnotherYes = (anotherBenefitPlan || "").toUpperCase() === "YES";
    const trimmed = (val || "").trim();

    if (isAnotherYes && !trimmed) {
      return "Please enter an insurance plan name.";
    }

    if (val && !trimmed) {
      return "Please enter an insurance plan name.";
    }

    if (trimmed) {
      if (/[^A-Za-z0-9\s.,'&/()#-]/.test(trimmed) || !/[A-Za-z0-9]/.test(trimmed)) {
        return "Please enter a valid insurance plan name.";
      }
    }

    return "";
  };

  // ── CMS-1500 Section 5 Validation Functions (Items 14 - 20) ──

  const VALID_ILLNESS_DATE_QUALS = new Set(["431", "484"]);
  const VALID_OTHER_DATE_QUALS = new Set(["454", "455", "453", "439", "444", "090", "091"]);

  const validateIllnessDateQual = (val?: string): string => {
    if (!val || !val.trim()) return "";
    const trimmed = val.trim();
    if (!VALID_ILLNESS_DATE_QUALS.has(trimmed)) {
      return "Please enter a valid illness date qualifier (431 or 484).";
    }
    return "";
  };

  const validateOtherDateQual = (val?: string): string => {
    if (!val || !val.trim()) return "";
    const trimmed = val.trim();
    if (!VALID_OTHER_DATE_QUALS.has(trimmed)) {
      return "Please enter a valid other date qualifier (e.g. 454 or 455).";
    }
    return "";
  };

  const validateReferringProviderName = (val?: string): string => {
    if (!val || !val.trim()) return "";
    const trimmed = val.trim();
    if (trimmed.length > 60) {
      return "Referring provider name cannot exceed 60 characters.";
    }
    if (/[0-9]/.test(trimmed) || /[^A-Za-z\s.'-]/.test(trimmed) || !/[A-Za-z]/.test(trimmed)) {
      return "Please enter a valid referring provider name.";
    }
    return "";
  };

  const validateReferringProviderOtherId = (val?: string): string => {
    if (!val || !val.trim()) return "";
    const trimmed = val.trim();
    if (trimmed.length > 17) {
      return "Referring provider other ID cannot exceed 17 characters.";
    }
    if (/[^A-Za-z0-9\-_ ]/.test(trimmed) || !/[A-Za-z0-9]/.test(trimmed)) {
      return "Please enter a valid referring provider other ID.";
    }
    return "";
  };

  const validateBillingProviderNpi = (val?: string): string => {
    return validateNpi(val, false);
  };

  const validateReferringProviderNpi = (val?: string): string => {
    return validateNpi(val, false);
  };

  const validateAdditionalClaimInfo = (val?: string): string => {
    if (!val) return "";
    const trimmed = val.trim();
    if (trimmed.length > 71) {
      return "Additional claim information cannot exceed 71 characters.";
    }
    if (/[^\x20-\x7E]/.test(trimmed)) {
      return "Please enter valid additional claim information.";
    }
    return "";
  };

  const validateOutsideLabCharges = (val?: string | number): string => {
    if (val === undefined || val === null || val === "") return "";
    const strVal = String(val).trim();
    if (!strVal) return "";
    if (!/^\d+(\.\d{1,2})?$/.test(strVal)) {
      return "Please enter a valid non-negative monetary amount (up to 2 decimal places).";
    }
    const num = parseFloat(strVal);
    if (isNaN(num) || num < 0) {
      return "Please enter a valid non-negative monetary amount.";
    }
    return "";
  };

  // ── ICD-10 Diagnosis Code Validation (Validation removed as requested) ──
  const isValidIcd10 = (_code?: string): boolean => {
    return true;
  };

  const validatePrimaryDiagnosis = (val?: string): string => {
    if (!val || !val.trim()) {
      return "Primary ICD-10 is required";
    }
    return "";
  };

  const validateSecondaryDiagnosis = (_val?: string): string => {
    return "";
  };

  // ── UB-04 Section 1: Facility & Billing Provider Information Validations ──
  const validateProviderFacilityName = (val?: string): string => {
    if (!val || !val.trim()) {
      return "Provider / facility name is required.";
    }
    const trimmed = val.trim();
    if (trimmed.length > 100) {
      return "Provider / facility name must not exceed 100 characters.";
    }
    if (!/^[A-Za-z0-9\s&'.-]+$/.test(trimmed)) {
      return "Please enter a valid provider or facility name.";
    }
    return "";
  };

  const validateFacilityNpi = (val?: string): string => {
    return validateNpi(val, true, "Billing Provider / Facility NPI is required.");
  };

  const validateFederalTaxId = (val?: string): string => {
    return validateTaxId(val, true, "Federal Tax No. (Fed. Tax ID) is required.");
  };

  const validateFacilityStreetAddress = (val?: string): string => {
    if (!val || !val.trim()) {
      return "Facility street address is required.";
    }
    const trimmed = val.trim();
    if (trimmed.length > 200) {
      return "Facility street address must not exceed 200 characters.";
    }
    if (!/^[A-Za-z0-9\s#\-.,/']+$/.test(trimmed)) {
      return "Please enter a valid facility street address.";
    }
    return "";
  };

  const validateFacilityCity = (val?: string): string => {
    if (!val || !val.trim()) {
      return "City is required.";
    }
    const trimmed = val.trim();
    if (trimmed.length > 100) {
      return "City must not exceed 100 characters.";
    }
    if (!/^[A-Za-z\s.'-]+$/.test(trimmed)) {
      return "Please enter a valid city name.";
    }
    return "";
  };

  const carrierNameError = React.useMemo(() => {
    if (!isFieldTouched("carrierName")) return "";
    return validateCarrierName(cms.carrierName);
  }, [formTouched, touchedFields.carrierName, cms.carrierName]);

  const insuranceProgramError = React.useMemo(() => {
    if (!isFieldTouched("insuranceProgram")) return "";
    return validateInsuranceProgram(cms.insuranceProgram);
  }, [formTouched, touchedFields.insuranceProgram, cms.insuranceProgram]);

  const activeInsuranceRule = React.useMemo(() => {
    return getInsuranceProviderRule(cms.insuranceProgram, cms.carrierName);
  }, [cms.insuranceProgram, cms.carrierName]);

  const insuredIdError = React.useMemo(() => {
    if (!isFieldTouched("insuredId")) return "";
    return validateInsuredId(cms.insuredId, cms.insuranceProgram, cms.carrierName);
  }, [formTouched, touchedFields.insuredId, cms.insuredId, cms.insuranceProgram, cms.carrierName]);

  const residentFullNameError = React.useMemo(() => {
    if (!isFieldTouched("residentFullName")) return "";
    return validateResidentFullName(cms.residentFullName);
  }, [formTouched, touchedFields.residentFullName, cms.residentFullName]);

  const patientCityError = React.useMemo(() => {
    if (!isFieldTouched("patientCity")) return "";
    return validateResidentCity(cms.patientCity);
  }, [formTouched, touchedFields.patientCity, cms.patientCity]);

  const patientStateError = React.useMemo(() => {
    if (!isFieldTouched("patientState")) return "";
    return validateResidentState(cms.patientState);
  }, [formTouched, touchedFields.patientState, cms.patientState]);

  const insuredCityError = React.useMemo(() => {
    if (!isFieldTouched("insuredCity")) return "";
    return validateResidentCity(cms.insuredCity);
  }, [formTouched, touchedFields.insuredCity, cms.insuredCity]);

  const insuredStateError = React.useMemo(() => {
    if (!isFieldTouched("insuredState")) return "";
    return validateResidentState(cms.insuredState);
  }, [formTouched, touchedFields.insuredState, cms.insuredState]);

  const patientZipError = React.useMemo(() => {
    if (!isFieldTouched("patientZip")) return "";
    return validateZipCode(cms.patientZip);
  }, [formTouched, touchedFields.patientZip, cms.patientZip]);

  const insuredZipError = React.useMemo(() => {
    if (!isFieldTouched("insuredZip")) return "";
    return validateZipCode(cms.insuredZip);
  }, [formTouched, touchedFields.insuredZip, cms.insuredZip]);

  const patientPhoneError = React.useMemo(() => {
    if (!isFieldTouched("patientPhone")) return "";
    return validatePhoneNumber(cms.patientPhone);
  }, [formTouched, touchedFields.patientPhone, cms.patientPhone]);

  const insuredPhoneError = React.useMemo(() => {
    if (!isFieldTouched("insuredPhone")) return "";
    return validatePhoneNumber(cms.insuredPhone);
  }, [formTouched, touchedFields.insuredPhone, cms.insuredPhone]);

  const otherInsuredNameError = React.useMemo(() => {
    if (!isFieldTouched("otherInsuredName")) return "";
    return validateOtherInsuredName(cms.otherInsuredName);
  }, [formTouched, touchedFields.otherInsuredName, cms.otherInsuredName]);

  const otherInsuredPolicyError = React.useMemo(() => {
    if (!isFieldTouched("otherInsuredPolicy")) return "";
    return validateOtherInsuredPolicy(cms.otherInsuredPolicy);
  }, [formTouched, touchedFields.otherInsuredPolicy, cms.otherInsuredPolicy]);

  const otherInsuredPlanNameError = React.useMemo(() => {
    if (!isFieldTouched("otherInsuredPlanName")) return "";
    return validateOtherInsuredPlanName(cms.otherInsuredPlanName);
  }, [formTouched, touchedFields.otherInsuredPlanName, cms.otherInsuredPlanName]);

  const conditionAutoStateError = React.useMemo(() => {
    if (!isFieldTouched("conditionAutoState") && (cms.conditionAutoAccident || "").toUpperCase() !== "YES") return "";
    return validateAutoAccidentState(cms.conditionAutoState, cms.conditionAutoAccident);
  }, [formTouched, touchedFields.conditionAutoState, cms.conditionAutoState, cms.conditionAutoAccident]);

  const claimCodes10dError = React.useMemo(() => {
    if (!isFieldTouched("claimCodes10d")) return "";
    return validateClaimCodes10d(cms.claimCodes10d);
  }, [formTouched, touchedFields.claimCodes10d, cms.claimCodes10d]);

  const insuredPolicyGroupError = React.useMemo(() => {
    if (!isFieldTouched("insuredPolicyGroup")) return "";
    return validateInsuredPolicyGroup(cms.insuredPolicyGroup);
  }, [formTouched, touchedFields.insuredPolicyGroup, cms.insuredPolicyGroup]);

  const otherClaimIdError = React.useMemo(() => {
    if (!isFieldTouched("otherClaimId")) return "";
    return validateOtherClaimId(cms.otherClaimId);
  }, [formTouched, touchedFields.otherClaimId, cms.otherClaimId]);

  const insurancePlanNameError = React.useMemo(() => {
    if (!isFieldTouched("insurancePlanName") && (cms.anotherBenefitPlan || "").toUpperCase() !== "YES") return "";
    return validateInsurancePlanName(cms.insurancePlanName, cms.anotherBenefitPlan);
  }, [formTouched, touchedFields.insurancePlanName, cms.insurancePlanName, cms.anotherBenefitPlan]);

  const dateCurrentIllnessQualError = React.useMemo(() => {
    if (!isFieldTouched("dateCurrentIllnessQual")) return "";
    return validateIllnessDateQual(cms.dateCurrentIllnessQual);
  }, [formTouched, touchedFields.dateCurrentIllnessQual, cms.dateCurrentIllnessQual]);

  const otherDateQualError = React.useMemo(() => {
    if (!isFieldTouched("otherDateQual")) return "";
    return validateOtherDateQual(cms.otherDateQual);
  }, [formTouched, touchedFields.otherDateQual, cms.otherDateQual]);

  const referringProviderNameError = React.useMemo(() => {
    if (!isFieldTouched("referringProviderName")) return "";
    return validateReferringProviderName(cms.referringProviderName);
  }, [formTouched, touchedFields.referringProviderName, cms.referringProviderName]);

  const referringProviderOtherIdError = React.useMemo(() => {
    if (!isFieldTouched("referringProviderOtherId")) return "";
    return validateReferringProviderOtherId(cms.referringProviderOtherId);
  }, [formTouched, touchedFields.referringProviderOtherId, cms.referringProviderOtherId]);

  const referringProviderNpiError = React.useMemo(() => {
    if (!isFieldTouched("referringProviderNpi")) return "";
    return validateReferringProviderNpi(cms.referringProviderNpi);
  }, [formTouched, touchedFields.referringProviderNpi, cms.referringProviderNpi]);

  const billingProviderNpiError = React.useMemo(() => {
    if (!isFieldTouched("billingProviderNpi")) return "";
    return validateBillingProviderNpi(cms.billingProviderNpi);
  }, [formTouched, touchedFields.billingProviderNpi, cms.billingProviderNpi]);

  const additionalClaimInfoError = React.useMemo(() => {
    if (!isFieldTouched("additionalClaimInfo")) return "";
    return validateAdditionalClaimInfo(cms.additionalClaimInfo);
  }, [formTouched, touchedFields.additionalClaimInfo, cms.additionalClaimInfo]);

  const outsideLabChargesError = React.useMemo(() => {
    if (!isFieldTouched("outsideLabCharges")) return "";
    return validateOutsideLabCharges(cms.outsideLabCharges);
  }, [formTouched, touchedFields.outsideLabCharges, cms.outsideLabCharges]);

  const diagAError = React.useMemo(() => {
    if (!isFieldTouched("diagA")) return "";
    return validatePrimaryDiagnosis(cms.diagA);
  }, [formTouched, touchedFields.diagA, cms.diagA]);

  const diagBError = React.useMemo(() => {
    if (!isFieldTouched("diagB")) return "";
    return validateSecondaryDiagnosis(cms.diagB);
  }, [formTouched, touchedFields.diagB, cms.diagB]);

  const diagCError = React.useMemo(() => {
    if (!isFieldTouched("diagC")) return "";
    return validateSecondaryDiagnosis(cms.diagC);
  }, [formTouched, touchedFields.diagC, cms.diagC]);

  const diagDError = React.useMemo(() => {
    if (!isFieldTouched("diagD")) return "";
    return validateSecondaryDiagnosis(cms.diagD);
  }, [formTouched, touchedFields.diagD, cms.diagD]);

  const diagEError = React.useMemo(() => {
    if (!isFieldTouched("diagE")) return "";
    return validateSecondaryDiagnosis(cms.diagE);
  }, [formTouched, touchedFields.diagE, cms.diagE]);

  const diagFError = React.useMemo(() => {
    if (!isFieldTouched("diagF")) return "";
    return validateSecondaryDiagnosis(cms.diagF);
  }, [formTouched, touchedFields.diagF, cms.diagF]);

  const diagGError = React.useMemo(() => {
    if (!isFieldTouched("diagG")) return "";
    return validateSecondaryDiagnosis(cms.diagG);
  }, [formTouched, touchedFields.diagG, cms.diagG]);

  const diagHError = React.useMemo(() => {
    if (!isFieldTouched("diagH")) return "";
    return validateSecondaryDiagnosis(cms.diagH);
  }, [formTouched, touchedFields.diagH, cms.diagH]);

  const diagIError = React.useMemo(() => {
    if (!isFieldTouched("diagI")) return "";
    return validateSecondaryDiagnosis(cms.diagI);
  }, [formTouched, touchedFields.diagI, cms.diagI]);

  const diagJError = React.useMemo(() => {
    if (!isFieldTouched("diagJ")) return "";
    return validateSecondaryDiagnosis(cms.diagJ);
  }, [formTouched, touchedFields.diagJ, cms.diagJ]);

  const diagKError = React.useMemo(() => {
    if (!isFieldTouched("diagK")) return "";
    return validateSecondaryDiagnosis(cms.diagK);
  }, [formTouched, touchedFields.diagK, cms.diagK]);

  const diagLError = React.useMemo(() => {
    if (!isFieldTouched("diagL")) return "";
    return validateSecondaryDiagnosis(cms.diagL);
  }, [formTouched, touchedFields.diagL, cms.diagL]);

  const ub04ProviderNameError = React.useMemo(() => {
    if (!isFieldTouched("ub04ProviderName")) return "";
    return validateProviderFacilityName(ub04Form.providerName);
  }, [formTouched, touchedFields.ub04ProviderName, ub04Form.providerName]);

  const ub04ProviderNpiError = React.useMemo(() => {
    if (!isFieldTouched("ub04ProviderNpi")) return "";
    return validateFacilityNpi(ub04Form.providerNpi);
  }, [formTouched, touchedFields.ub04ProviderNpi, ub04Form.providerNpi]);

  const ub04FedTaxNoError = React.useMemo(() => {
    if (!isFieldTouched("ub04FedTaxNo")) return "";
    return validateFederalTaxId(ub04Form.fedTaxNo);
  }, [formTouched, touchedFields.ub04FedTaxNo, ub04Form.fedTaxNo]);

  const ub04ProviderAddressError = React.useMemo(() => {
    if (!isFieldTouched("ub04ProviderAddress")) return "";
    return validateFacilityStreetAddress(ub04Form.providerAddress);
  }, [formTouched, touchedFields.ub04ProviderAddress, ub04Form.providerAddress]);

  const ub04ProviderCityError = React.useMemo(() => {
    if (!isFieldTouched("ub04ProviderCity")) return "";
    return validateFacilityCity(ub04Form.providerCity);
  }, [formTouched, touchedFields.ub04ProviderCity, ub04Form.providerCity]);

  const ub04PrincipalDiagError = React.useMemo(() => {
    if (!isFieldTouched("principalDiagnosis")) return "";
    return validatePrimaryDiagnosis(ub04Form.principalDiagnosis);
  }, [formTouched, touchedFields.principalDiagnosis, ub04Form.principalDiagnosis]);

  const ub04OtherDiag1Error = React.useMemo(() => {
    if (!isFieldTouched("otherDiagnosis1")) return "";
    return validateSecondaryDiagnosis(ub04Form.otherDiagnosis1);
  }, [formTouched, touchedFields.otherDiagnosis1, ub04Form.otherDiagnosis1]);

  const ub04OtherDiag2Error = React.useMemo(() => {
    if (!isFieldTouched("otherDiagnosis2")) return "";
    return validateSecondaryDiagnosis(ub04Form.otherDiagnosis2);
  }, [formTouched, touchedFields.otherDiagnosis2, ub04Form.otherDiagnosis2]);

  const patientSexError = React.useMemo(() => {
    if (!isFieldTouched("patientSex")) return "";
    return validatePatientSex(cms.patientSex);
  }, [formTouched, touchedFields.patientSex, cms.patientSex]);

  const patientDobError = React.useMemo(() => {
    if (!patientDobTouched) return "";
    if (dobPickerError === "maxDate" || dobPickerError === "disableFuture" || dobPickerError === "shouldDisableDate") {
      return "Birth date cannot be in the future.";
    }
    if (dobPickerError === "minDate") {
      return "Please enter a valid birth year (1900 or later).";
    }
    if (dobPickerError === "invalidDate") {
      return "Invalid date format. Please use DD/MM/YYYY.";
    }
    const currentVal = selectedForm === "UB_04" ? ub04Form.patientDob : cms.patientDob;
    return validatePatientDob(currentVal, patientDobTouched);
  }, [patientDobTouched, dobPickerError, selectedForm, ub04Form.patientDob, cms.patientDob]);


  // Click handler for Patient's Digital Signature button - direct draw
  const handlePatientDigitalSignatureClick = () => {
    updateField("patientSignatureType", "DIGITAL");
    setSignatureModalTarget("patient");
  };

  // Click handler for Insured's Digital Signature button - direct draw
  const handleInsuredDigitalSignatureClick = () => {
    updateField("insuredSignatureType", "DIGITAL");
    setSignatureModalTarget("insured");
  };

  // Modal save callback when user creates or re-draws digital signature
  const handleSignatureModalSave = async (signatureDataUrl: string) => {
    const isPatient = signatureModalTarget === "patient";
    const dateValue = isPatient ? (cms.patientSignatureDate || getTodayFormatted()) : (cms.insuredSignatureDate || getTodayFormatted());
    const isoDate = toApiDate(dateValue) || formatIsoSignatureDate(dateValue);

    if (isPatient) {
      updateField("patientDigitalSignature", signatureDataUrl);
      updateField("patientSignatureType", "DIGITAL");
      if (!cms.patientSignatureDate) {
        updateField("patientSignatureDate", getTodayFormatted());
      }
    } else if (signatureModalTarget === "insured") {
      updateField("insuredDigitalSignature", signatureDataUrl);
      updateField("insuredSignatureType", "DIGITAL");
      if (!cms.insuredSignatureDate) {
        updateField("insuredSignatureDate", getTodayFormatted());
      }
    }

    const targetClaimId =
      activeClaimId ||
      claimId ||
      queryClaimId ||
      stateClaimId ||
      existingClaim?.id ||
      (existingClaim as any)?.claimId ||
      (existingClaim as any)?._id;

    if (targetClaimId && signatureModalTarget) {
      try {
        if (isPatient) {
          await billingService.uploadPatientSignatureBase64(targetClaimId, signatureDataUrl, isoDate);
        } else {
          await billingService.uploadInsuredSignatureBase64(targetClaimId, signatureDataUrl, isoDate);
        }
      } catch (err) {
        console.warn("Could not upload digital signature to claim:", err);
      }
    }

    setSignatureModalTarget(null);
    setSnackbar({
      open: true,
      message: "Digital signature saved and applied successfully!",
      severity: "success",
    });
  };

  // Service Line management
  const handleAddServiceLine = () => {
    const newLine: CmsServiceLine = {
      id: String(Date.now()),
      dateFrom: "",
      dateTo: "",
      pos: "",
      emg: "",
      cpt: "",
      mod1: "",
      mod2: "",
      mod3: "",
      mod4: "",
      diagPointer: "",
      charges: "",
      daysUnits: "1",
      epsdt: "",
      idQual: "",
      renderingNpi: cms.billingProviderNpi || "",
    };
    setServiceLines([...serviceLines, newLine]);
  };

  const handleRemoveServiceLine = (id: string) => {
    if (serviceLines.length === 1) return;
    setServiceLines(serviceLines.filter((l) => l.id !== id));
  };

  const handleServiceLineChange = (id: string, field: keyof CmsServiceLine, val: any) => {
    setServiceLines((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: val } : l))
    );
  };

  // Date string (form display DD/MM/YYYY) → yyyy-MM-dd (API)
  const toApiDate = (value: string): string => {
    const d = parseStringToDate(value);
    if (!d) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const getClaimType = (): ClaimType => {
    const program = (cms.insuranceProgram || "").toUpperCase();
    const payer = (selectedForm === "UB_04" ? ub04Form.payerName : cms.carrierName).toUpperCase();
    if (program === "MEDICARE" || payer.includes("MEDICARE")) return ClaimType.MEDICARE;
    if (program === "MEDICAID" || payer.includes("MEDICAID")) return ClaimType.MEDICAID;
    return ClaimType.PRIVATE_INSURANCE;
  };

  // Summary of the form in the shape the billing-claims API stores
  const buildClaimRequest = (): BillingClaimRequest => {
    if (selectedForm === "INVOICE") {
      return {
        formType: ClaimFormType.INVOICE,
        residentId: selectedResidentId,
        claimType: ClaimType.PRIVATE_INSURANCE,
        payerName: invoiceForm.billToClientName || invoiceForm.managedCarePlan || "MasterCare, Inc.",
        policyNumber: invoiceForm.residentCin || undefined,
        serviceDateFrom: toApiDate(invoiceForm.servicePeriodFrom) || undefined,
        serviceDateTo: toApiDate(invoiceForm.servicePeriodTo) || undefined,
        diagnosisCodes: "",
        procedureCodes: invoiceLines.map((l) => l.productOrService).filter(Boolean).join(", "),
        billedAmount: Number(invoiceForm.totalAmount) || 0,
        submissionMethod: SubmissionMethod.PAPER,
        billingProviderNpi: invoiceForm.providerNpi || undefined,
        formData: { invoiceForm, invoiceLines },
      };
    }

    const isUb04 = selectedForm === "UB_04";
    const serviceDates = isUb04
      ? [ub04Form.statementCoversFrom, ub04Form.statementCoversThrough]
      : [serviceLines[0]?.dateFrom, serviceLines[serviceLines.length - 1]?.dateTo || serviceLines[serviceLines.length - 1]?.dateFrom];
    const diagnosisCodes = isUb04
      ? [ub04Form.principalDiagnosis, ub04Form.otherDiagnosis1, ub04Form.otherDiagnosis2]
      : [cms.diagA, cms.diagB, cms.diagC, cms.diagD, cms.diagE, cms.diagF, cms.diagG, cms.diagH, cms.diagI, cms.diagJ, cms.diagK, cms.diagL];
    const procedureCodes = isUb04
      ? ub04RevenueLines.map((l) => l.hcpcsRate || l.revCode)
      : serviceLines.map((l) => l.cpt);

    return {
      formType: selectedForm!,
      residentId: selectedResidentId,
      claimType: getClaimType(),
      payerName: isUb04 ? ub04Form.payerName : cms.carrierName,
      policyNumber: (isUb04 ? ub04Form.insuredUniqueId : cms.insuredId) || undefined,
      serviceDateFrom: toApiDate(serviceDates[0] || "") || undefined,
      serviceDateTo: toApiDate(serviceDates[1] || "") || undefined,
      diagnosisCodes: diagnosisCodes.filter(Boolean).join(", "),
      procedureCodes: procedureCodes.filter(Boolean).join(", "),
      billedAmount: Number(isUb04 ? ub04Form.totalCharges : cms.totalCharge) || 0,
      paidAmount: isUb04 ? undefined : Number(cms.amountPaid) || undefined,
      submissionMethod: SubmissionMethod.ELECTRONIC,
      placeOfService: isUb04 ? undefined : serviceLines[0]?.pos || undefined,
      billingProviderNpi: (isUb04 ? ub04Form.providerNpi : cms.billingProviderNpi) || undefined,
      formData: isUb04 ? { ub04Form, ub04RevenueLines } : { cms, serviceLines },
    };
  };

  /**
   * Handle signature file upload (Box 12 & 13)
   * Calls POST /api/v1/billing-claims/{id}/patient-signature/upload
   * or /api/v1/billing-claims/{id}/insured-signature/upload
   */
  const handleSignatureUpload = async (
    field: "patientSignatureImage" | "insuredSignatureImage",
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input value so re-selecting the same file fires onChange
    e.target.value = "";

    const isPatient = field === "patientSignatureImage";
    const sigSlot = isPatient ? "patient" : "insured";

    // Immediate preview for smooth UX
    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      const base64 = uploadEvent.target?.result as string;
      updateField(field, base64);
    };
    reader.readAsDataURL(file);

    // Keep pending file in ref
    if (isPatient) {
      pendingPatientSignatureFileRef.current = file;
      updateField("patientSignatureType", "UPLOAD");
      if (!cms.patientSignatureDate) {
        updateField("patientSignatureDate", getTodayFormatted());
      }
    } else {
      pendingInsuredSignatureFileRef.current = file;
      updateField("insuredSignatureType", "UPLOAD");
      if (!cms.insuredSignatureDate) {
        updateField("insuredSignatureDate", getTodayFormatted());
      }
    }

    // Determine target claim ID
    let targetClaimId =
      activeClaimId ||
      claimId ||
      queryClaimId ||
      stateClaimId ||
      existingClaim?.id ||
      (existingClaim as any)?.claimId ||
      (existingClaim as any)?._id;

    const signatureDateValue = isPatient
      ? (cms.patientSignatureDate || getTodayFormatted())
      : (cms.insuredSignatureDate || getTodayFormatted());
    const isoDate = toApiDate(signatureDateValue) || formatIsoSignatureDate(signatureDateValue);

    // If claim not created yet, but resident is selected, auto-create claim draft first
    if (!targetClaimId && selectedResidentId) {
      try {
        setSignatureUploading(sigSlot);
        const req = buildClaimRequest();
        const saved = await billingService.createBillingClaim(req);
        targetClaimId = saved.id || (saved as any)?.claimId;
        if (targetClaimId) {
          setActiveClaimId(targetClaimId);
          setExistingClaim(saved);
        }
      } catch (saveErr) {
        console.warn("Could not auto-create claim draft before signature upload:", saveErr);
      }
    }

    if (targetClaimId) {
      try {
        setSignatureUploading(sigSlot);
        if (isPatient) {
          await billingService.uploadPatientSignature(targetClaimId, file, isoDate);
          pendingPatientSignatureFileRef.current = null;
        } else {
          await billingService.uploadInsuredSignature(targetClaimId, file, isoDate);
          pendingInsuredSignatureFileRef.current = null;
        }
        setSnackbar({
          open: true,
          message: `${isPatient ? "Resident" : "Insured"} signature uploaded successfully!`,
          severity: "success",
        });
      } catch (err: any) {
        console.error("Signature upload failed:", err);
        setSnackbar({
          open: true,
          message: getApiErrorMessage(err, "Failed to upload signature to server."),
          severity: "error",
        });
      } finally {
        setSignatureUploading(null);
      }
    } else {
      setSnackbar({
        open: true,
        message: "Signature image selected. Please save the claim draft to upload it to the server.",
        severity: "info",
      });
    }
  };

  /**
   * Handle signature removal from UI and server (Box 12 & 13)
   */
  const handleRemoveSignature = async (type: "patient" | "insured") => {
    const isPatient = type === "patient";
    if (isPatient) {
      updateField("patientSignatureImage", "");
      updateField("patientDigitalSignature", "");
      pendingPatientSignatureFileRef.current = null;
    } else {
      updateField("insuredSignatureImage", "");
      updateField("insuredDigitalSignature", "");
      pendingInsuredSignatureFileRef.current = null;
    }

    const targetClaimId =
      activeClaimId ||
      claimId ||
      queryClaimId ||
      stateClaimId ||
      existingClaim?.id ||
      (existingClaim as any)?.claimId ||
      (existingClaim as any)?._id;

    if (targetClaimId) {
      try {
        if (isPatient) {
          await billingService.removePatientSignature(targetClaimId);
        } else {
          await billingService.removeInsuredSignature(targetClaimId);
        }
        setSnackbar({
          open: true,
          message: `${isPatient ? "Resident" : "Insured"} signature removed successfully.`,
          severity: "info",
        });
      } catch (err) {
        console.warn("Could not remove signature from server:", err);
      }
    }
  };

  // Mirrors the required fields on the backend BillingClaimRequest
  const getMissingFields = (request: BillingClaimRequest): string[] => {
    if (selectedForm === "INVOICE") {
      const missing: string[] = [];
      if (!request.residentId) missing.push("resident");
      if (!invoiceForm.billToClientName?.trim()) missing.push("client / payer name");
      if (!(request.billedAmount > 0)) missing.push("line items / charges");
      return missing;
    }

    const isUb04 = selectedForm === "UB_04";
    const missing: string[] = [];
    if (!request.residentId) missing.push("resident");
    if (!request.payerName) missing.push(isUb04 ? "payer name" : "carrier / payer name");
    if (isUb04) {
      if (validateProviderFacilityName(ub04Form.providerName)) missing.push("provider / facility name");
      if (validateFacilityNpi(ub04Form.providerNpi)) missing.push("billing provider / facility NPI");
      if (validateFederalTaxId(ub04Form.fedTaxNo)) missing.push("federal tax no.");
      if (validateFacilityStreetAddress(ub04Form.providerAddress)) missing.push("facility street address");
      if (validateFacilityCity(ub04Form.providerCity)) missing.push("facility city");
    } else {
      if (validateInsuranceProgram(cms.insuranceProgram)) missing.push("type of insurance");
      if (validateInsuredId(cms.insuredId)) missing.push("insured's I.D. number");
      if (validateResidentFullName(cms.residentFullName)) missing.push("resident full name");
      if (validatePatientDob(cms.patientDob, true) || dobPickerError) missing.push("resident birth date");
      if (validatePatientSex(cms.patientSex)) missing.push("resident gender");
      if (validateBillingProviderNpi(cms.billingProviderNpi)) missing.push("billing provider NPI");
    }
    if (!request.serviceDateFrom || !request.serviceDateTo) {
      missing.push(isUb04 ? "statement period" : "service dates");
    } else if (new Date(request.serviceDateTo).getTime() < new Date(request.serviceDateFrom).getTime()) {
      missing.push(isUb04 ? "valid statement period (Through date must be on or after From date)" : "valid service dates (To date must be on or after From date)");
    }
    if (!request.diagnosisCodes) missing.push(isUb04 ? "principal diagnosis" : "diagnosis code");
    if (!request.procedureCodes) missing.push(isUb04 ? "HCPCS / revenue code" : "CPT / HCPCS code");
    if (!(request.billedAmount > 0)) missing.push("charges");
    return missing;
  };

  // Automatically scrolls and focuses directly into the first invalid mandatory field
  const scrollToFirstInvalidField = (): boolean => {
    const isUb04 = selectedForm === "UB_04";
    let targetId: string | null = null;

    if (!selectedResidentId) {
      targetId = "resident-selector";
    } else if (selectedForm === "INVOICE") {
      if (!invoiceForm.billToClientName?.trim()) {
        targetId = "invoice-bill-to-client";
      }
    } else if (isUb04) {
      if (validateProviderFacilityName(ub04Form.providerName)) {
        targetId = "ub04-provider-name";
      } else if (validateFacilityNpi(ub04Form.providerNpi)) {
        targetId = "ub04-provider-npi";
      } else if (validateFederalTaxId(ub04Form.fedTaxNo)) {
        targetId = "ub04-fed-tax-no";
      } else if (validateFacilityStreetAddress(ub04Form.providerAddress)) {
        targetId = "ub04-provider-address";
      } else if (validateFacilityCity(ub04Form.providerCity)) {
        targetId = "ub04-provider-city";
      } else if (!ub04Form.payerName?.trim()) {
        targetId = "ub04-payer-name";
      } else if (!ub04Form.statementCoversFrom) {
        targetId = "ub04-statement-from";
      } else if (!ub04Form.statementCoversThrough) {
        targetId = "ub04-statement-through";
      } else if (validatePrimaryDiagnosis(ub04Form.principalDiagnosis)) {
        targetId = "ub04-principal-diag";
      } else if (validateSecondaryDiagnosis(ub04Form.otherDiagnosis1)) {
        targetId = "ub04-other-diag-1";
      } else if (validateSecondaryDiagnosis(ub04Form.otherDiagnosis2)) {
        targetId = "ub04-other-diag-2";
      } else {
        const emptyRevLine = ub04RevenueLines.find((l) => !l.hcpcsRate?.trim() && !l.revCode?.trim());
        if (emptyRevLine) {
          targetId = `ub04-revenue-code-${emptyRevLine.id}`;
        } else {
          const emptyChargeLine = ub04RevenueLines.find((l) => !(Number(l.totalCharges) > 0));
          if (emptyChargeLine && !(Number(ub04Form.totalCharges) > 0)) {
            targetId = `ub04-revenue-charges-${emptyChargeLine.id}`;
          }
        }
      }
    } else {
      // CMS-1500 Section 1 validations first
      if (validateCarrierName(cms.carrierName)) {
        targetId = "cms-carrier-name";
      } else if (validateInsuranceProgram(cms.insuranceProgram)) {
        targetId = "cms-insurance-type";
      } else if (validateInsuredId(cms.insuredId)) {
        targetId = "cms-insured-id";
      } else if (validateResidentFullName(cms.residentFullName)) {
        targetId = "cms-resident-full-name";
      } else if (validatePatientDob(cms.patientDob, true) || dobPickerError) {
        targetId = "cms-resident-dob";
      } else if (validatePatientSex(cms.patientSex)) {
        targetId = "cms-resident-gender";
      } else if (validateResidentCity(cms.patientCity)) {
        targetId = "cms-patient-city";
      } else if (validateResidentState(cms.patientState)) {
        targetId = "cms-patient-state";
      } else if (validateZipCode(cms.patientZip)) {
        targetId = "cms-patient-zip";
      } else if (validatePhoneNumber(cms.patientPhone)) {
        targetId = "cms-patient-phone";
      } else if (validateResidentCity(cms.insuredCity)) {
        targetId = "cms-insured-city";
      } else if (validateResidentState(cms.insuredState)) {
        targetId = "cms-insured-state";
      } else if (validateZipCode(cms.insuredZip)) {
        targetId = "cms-insured-zip";
      } else if (validatePhoneNumber(cms.insuredPhone)) {
        targetId = "cms-insured-phone";
      } else if (validateOtherInsuredName(cms.otherInsuredName)) {
        targetId = "cms-other-insured-name";
      } else if (validateOtherInsuredPolicy(cms.otherInsuredPolicy)) {
        targetId = "cms-other-insured-policy";
      } else if (validateOtherInsuredPlanName(cms.otherInsuredPlanName)) {
        targetId = "cms-other-insured-plan";
      } else if (validateAutoAccidentState(cms.conditionAutoState, cms.conditionAutoAccident)) {
        targetId = "cms-condition-auto-state";
      } else if (validateClaimCodes10d(cms.claimCodes10d)) {
        targetId = "cms-claim-codes-10d";
      } else if (validateInsuredPolicyGroup(cms.insuredPolicyGroup)) {
        targetId = "cms-insured-policy-group";
      } else if (validateOtherClaimId(cms.otherClaimId)) {
        targetId = "cms-other-claim-id";
      } else if (validateInsurancePlanName(cms.insurancePlanName, cms.anotherBenefitPlan)) {
        targetId = "cms-insurance-plan-name";
      } else if (validateIllnessDateQual(cms.dateCurrentIllnessQual)) {
        targetId = "cms-date-current-illness-qual";
      } else if (validateOtherDateQual(cms.otherDateQual)) {
        targetId = "cms-other-date-qual";
      } else if (validateReferringProviderName(cms.referringProviderName)) {
        targetId = "cms-referring-provider-name";
      } else if (validateReferringProviderOtherId(cms.referringProviderOtherId)) {
        targetId = "cms-referring-provider-other-id";
      } else if (validateReferringProviderNpi(cms.referringProviderNpi)) {
        targetId = "cms-referring-provider-npi";
      } else if (validateBillingProviderNpi(cms.billingProviderNpi)) {
        targetId = "cms-billing-provider-npi";
      } else if (validateAdditionalClaimInfo(cms.additionalClaimInfo)) {
        targetId = "cms-additional-claim-info";
      } else if (validateOutsideLabCharges(cms.outsideLabCharges)) {
        targetId = "cms-outside-lab-charges";
      } else if (validatePrimaryDiagnosis(cms.diagA)) {
        targetId = "cms-diag-a";
      } else if (validateSecondaryDiagnosis(cms.diagB)) {
        targetId = "cms-diag-b";
      } else if (validateSecondaryDiagnosis(cms.diagC)) {
        targetId = "cms-diag-c";
      } else if (validateSecondaryDiagnosis(cms.diagD)) {
        targetId = "cms-diag-d";
      } else if (validateSecondaryDiagnosis(cms.diagE)) {
        targetId = "cms-diag-e";
      } else if (validateSecondaryDiagnosis(cms.diagF)) {
        targetId = "cms-diag-f";
      } else if (validateSecondaryDiagnosis(cms.diagG)) {
        targetId = "cms-diag-g";
      } else if (validateSecondaryDiagnosis(cms.diagH)) {
        targetId = "cms-diag-h";
      } else if (validateSecondaryDiagnosis(cms.diagI)) {
        targetId = "cms-diag-i";
      } else if (validateSecondaryDiagnosis(cms.diagJ)) {
        targetId = "cms-diag-j";
      } else if (validateSecondaryDiagnosis(cms.diagK)) {
        targetId = "cms-diag-k";
      } else if (validateSecondaryDiagnosis(cms.diagL)) {
        targetId = "cms-diag-l";
      } else {
        const emptyFromLine = serviceLines.find((l) => !l.dateFrom);
        if (emptyFromLine) {
          targetId = `cms-service-from-${emptyFromLine.id}`;
        } else {
          const emptyToLine = serviceLines.find((l) => !l.dateTo);
          if (emptyToLine) {
            targetId = `cms-service-to-${emptyToLine.id}`;
          } else if (!cms.diagA?.trim()) {
            targetId = "cms-diag-a";
          } else {
            const emptyCptLine = serviceLines.find((l) => !l.cpt?.trim());
            if (emptyCptLine) {
              targetId = `cms-service-cpt-${emptyCptLine.id}`;
            } else {
              const emptyChargeLine = serviceLines.find((l) => !(Number(l.charges) > 0));
              if (emptyChargeLine && !(Number(cms.totalCharge) > 0)) {
                targetId = `cms-service-charges-${emptyChargeLine.id}`;
              }
            }
          }
        }
      }
    }

    setTimeout(() => {
      let el: HTMLElement | null = targetId ? (document.getElementById(targetId) as HTMLElement) : null;
      if (!el && targetId) {
        el = document.querySelector(`[id*="${targetId}"]`) as HTMLElement;
      }
      if (!el) {
        el = document.querySelector(".Mui-error input, .Mui-error, [aria-invalid='true']") as HTMLElement;
      }

      if (el) {
        const inputEl = (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")
          ? el
          : (el.querySelector("input, textarea, select") as HTMLElement) || el;

        inputEl.scrollIntoView({ behavior: "smooth", block: "center" });
        inputEl.focus();

        inputEl.style.transition = "box-shadow 0.25s ease, border-color 0.25s ease";
        inputEl.style.boxShadow = "0 0 0 4px rgba(239, 68, 68, 0.4)";
        setTimeout(() => {
          inputEl.style.boxShadow = "";
        }, 1500);
      }
    }, 50);

    return true;
  };

  // Helper to build individual invoice payload matching backend specification
  const buildIndividualInvoicePayload = (statusOverride: string = "DRAFT"): IndividualInvoiceRequest => {
    const paymentMethodMap: Record<string, string> = {
      BANK: "BANK / ACH Transfer",
      CHECK: "Check",
      CREDIT_CARD: "Credit Card",
      WIRE: "Wire Transfer",
    };

    const residentDisplayName =
      invoiceForm.residentName ||
      (selectedResident
        ? (selectedResident.residentName || `${selectedResident.firstName || ""} ${selectedResident.lastName || ""}`.trim())
        : "") ||
      "Resident";

    const todayFormatted = formatDateToString(new Date());

    const payer =
      invoiceForm.billToClientName ||
      invoiceForm.managedCarePlan ||
      (residentInsurances[0]?.insuranceCompanyName) ||
      "Master Care, Inc.";

    const billAddress = invoiceForm.billToAddress || invoiceForm.billToClientName || payer;
    const shipAddress = invoiceForm.shipToAddress || invoiceForm.billToAddress || billAddress;

    const effectiveFacilityId =
      selectedResident?.facilityId ||
      currentResidentData?.facilityId ||
      currentFacility?.id ||
      user?.facilityId ||
      user?.tenantId ||
      authService.getFacilityIdFromToken() ||
      localStorage.getItem("facilityId") ||
      localStorage.getItem("currentTenantId") ||
      "";

    const validLines = invoiceLines.filter(
      (l) => Boolean(l.productOrService?.trim()) || Boolean(l.description?.trim()) || Number(l.rate) > 0 || Number(l.amount) > 0
    );

    const itemsToMap = validLines.length > 0 ? validLines : [
      {
        id: "1",
        productOrService: "Caregiving",
        description: "",
        serviceLookupId: "e7f124ae-4512-4b70-a866-bea120d06f1e",
        qty: 1,
        rate: 0,
        amount: 0,
      }
    ];

    return {
      residentId: selectedResidentId,
      facilityId: effectiveFacilityId,
      residentName: residentDisplayName,
      residentCin:
        invoiceForm.residentCin ||
        residentInsurances[0]?.policyMemberId ||
        residentInsurances[0]?.policyNumber ||
        (currentResidentData as any)?.insurancePolicyNumber ||
        (currentResidentData as any)?.policyMemberId ||
        (currentResidentData as any)?.cinNumber ||
        (currentResidentData as any)?.medicaidNumber ||
        (currentResidentData as any)?.mrnNumber ||
        (selectedResident as any)?.insurancePolicyNumber ||
        (selectedResident as any)?.cinNumber ||
        "",
      residentDob:
        toApiDate(invoiceForm.residentDob) ||
        (currentResidentData?.dateOfBirth ? toApiDate(currentResidentData.dateOfBirth) : "") ||
        (selectedResident?.dateOfBirth ? toApiDate(selectedResident.dateOfBirth) : "") ||
        "",
      servicePeriodFrom:
        toApiDate(invoiceForm.servicePeriodFrom) ||
        toApiDate(todayFormatted),
      servicePeriodTo:
        toApiDate(invoiceForm.servicePeriodTo) ||
        toApiDate(todayFormatted),
      managedCarePlan: invoiceForm.managedCarePlan || payer,
      payerName: payer,
      billTo: billAddress,
      shipTo: shipAddress,
      paymentTerms: invoiceForm.terms || "Net 15",
      invoiceDate: toApiDate(invoiceForm.invoiceDate) || toApiDate(todayFormatted),
      dueDate:
        toApiDate(invoiceForm.dueDate) ||
        toApiDate(formatDateToString(new Date(Date.now() + 15 * 86400000))),
      items: itemsToMap.map((line, idx) => ({
        itemNumber: idx + 1,
        serviceLookupId:
          line.serviceLookupId ||
          (line.productOrService?.toLowerCase().includes("caregiving")
            ? "e7f124ae-4512-4b70-a866-bea120d06f1e"
            : undefined),
        productOrService: line.productOrService || "Caregiving",
        description: line.description || "",
        quantity: Number(line.qty) || 1,
        rate: Number(line.rate) || 0,
        amount: Number(line.amount) || ((Number(line.qty) || 1) * (Number(line.rate) || 0)),
      })),
      paymentMethod: paymentMethodMap[invoiceForm.paymentMethod] || invoiceForm.paymentMethod || "BANK / ACH Transfer",
      status: statusOverride,
      notes: invoiceForm.notes || "Remittance via ACH transfer.",
    };
  };

  // Save Draft (Calls POST then PUT for Individual Invoices, or /v1/billing-claims for claims)
  const handleSaveDraft = async () => {
    if (!selectedResidentId) {
      setSnackbar({
        open: true,
        message: "Please select a resident to save the draft.",
        severity: "error",
      });
      scrollToFirstInvalidField();
      return;
    }

    // ── INDIVIDUAL INVOICE DRAFT SAVE (POST on first create, PUT on subsequent updates) ──
    if (selectedForm === "INVOICE") {
      setSaving(true);
      try {
        const payload = buildIndividualInvoicePayload("DRAFT");
        const targetInvoiceId =
          activeClaimId ||
          searchParams.get("invoiceId") ||
          searchParams.get("id") ||
          claimId ||
          queryClaimId ||
          stateClaimId;

        if (targetInvoiceId) {
          // Subsequent draft save: Calls PUT /api/v1/individual-invoices/{id}
          const updated = await billingService.updateIndividualInvoice(targetInvoiceId, payload);
          const invNo = updated?.invoiceNumber || invoiceForm.invoiceNumber ? `(#${updated?.invoiceNumber || invoiceForm.invoiceNumber})` : "";
          setSnackbar({
            open: true,
            message: `Invoice draft ${invNo} updated successfully!`.trim(),
            severity: "success",
          });
          navigate("/billing?tab=invoices", {
            state: {
              message: `Invoice draft ${invNo} updated successfully.`.trim(),
              severity: "success",
            },
          });
        } else {
          // First draft save: Calls POST /api/v1/individual-invoices
          const created = await billingService.createIndividualInvoice(payload);
          const invNo = created?.invoiceNumber ? `(#${created.invoiceNumber})` : "";
          setSnackbar({
            open: true,
            message: `Invoice draft ${invNo} saved successfully!`.trim(),
            severity: "success",
          });
          navigate("/billing?tab=invoices", {
            state: {
              message: `Invoice draft ${invNo} saved successfully.`.trim(),
              severity: "success",
            },
          });
        }
      } catch (err: any) {
        console.error("Failed to save invoice draft:", err);
        setSnackbar({
          open: true,
          message: getApiErrorMessage(err, "Failed to save invoice draft."),
          severity: "error",
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    // ── INSURANCE CLAIM DRAFT SAVE (CMS-1500 / UB-04) ──
    const request = buildClaimRequest();

    let targetId =
      activeClaimId ||
      claimId ||
      queryClaimId ||
      stateClaimId ||
      existingClaim?.id ||
      (existingClaim as any)?.claimId ||
      (existingClaim as any)?._id;

    if (!targetId && selectedResidentId) {
      try {
        const claims = await billingService.getBillingClaimsByResident(selectedResidentId);
        const draft = claims.find(
          (c: any) => (c.status === "DRAFT" || c.status === ClaimStatus.DRAFT) && (c.id || c.claimId)
        );
        if (draft) {
          targetId = draft.id || (draft as any).claimId;
          setActiveClaimId(targetId);
          setExistingClaim(draft);
        }
      } catch (err) {
        console.warn("Could not check resident claims for draft update:", err);
      }
    }

    setSaving(true);
    try {
      // Calls PUT /v1/billing-claims/{id} when updating claim draft
      const saved = targetId
        ? await billingService.updateBillingClaim(targetId, request)
        : await billingService.createBillingClaim(request);

      const updatedId = saved.id || (saved as any)?.claimId || targetId;
      if (updatedId) {
        setActiveClaimId(updatedId);
        if (pendingPatientSignatureFileRef.current) {
          const isoDate = toApiDate(cms.patientSignatureDate || getTodayFormatted()) || formatIsoSignatureDate(cms.patientSignatureDate);
          try {
            await billingService.uploadPatientSignature(updatedId, pendingPatientSignatureFileRef.current, isoDate);
            pendingPatientSignatureFileRef.current = null;
          } catch (sigErr) {
            console.error("Failed to upload pending patient signature:", sigErr);
          }
        }
        if (pendingInsuredSignatureFileRef.current) {
          const isoDate = toApiDate(cms.insuredSignatureDate || getTodayFormatted()) || formatIsoSignatureDate(cms.insuredSignatureDate);
          try {
            await billingService.uploadInsuredSignature(updatedId, pendingInsuredSignatureFileRef.current, isoDate);
            pendingInsuredSignatureFileRef.current = null;
          } catch (sigErr) {
            console.error("Failed to upload pending insured signature:", sigErr);
          }
        }
      }
      setExistingClaim(saved);
      setSnackbar({
        open: true,
        message: "Claim draft saved successfully!",
        severity: "success",
      });
      navigate("/billing", { state: { message: "Claim draft saved successfully!" } });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(err, "Could not save the draft claim."),
        severity: "error",
      });
      setSaving(false);
    }
  };

  // Create or Update Individual Invoice (POST if new, PUT if existing, then redirect to list)
  const handleCreateIndividualInvoice = async () => {
    if (!selectedResidentId) {
      setSnackbar({
        open: true,
        message: "Please select a resident before creating an invoice.",
        severity: "error",
      });
      return;
    }

    if (!invoiceForm.invoiceDate) {
      setSnackbar({
        open: true,
        message: "Please enter the Invoice Date (DD/MM/YYYY).",
        severity: "error",
      });
      return;
    }

    const validLines = invoiceLines.filter(
      (l) => Boolean(l.productOrService?.trim()) || Boolean(l.description?.trim()) || Number(l.rate) > 0 || Number(l.amount) > 0
    );

    if (validLines.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select a Product or Service for at least one line item.",
        severity: "error",
      });
      return;
    }

    // Validate that each line item has a product/service, quantity >= 1, and positive rate
    for (let i = 0; i < validLines.length; i++) {
      const line = validLines[i];
      const lineLabel = line.productOrService?.trim() || `Line #${i + 1}`;
      const qtyNum = Number(line.qty);
      const rateNum = Number(line.rate);

      if (!line.productOrService?.trim()) {
        setSnackbar({
          open: true,
          message: `Please select a Product or Service for line #${i + 1}.`,
          severity: "error",
        });
        return;
      }

      if (isNaN(qtyNum) || qtyNum <= 0) {
        setSnackbar({
          open: true,
          message: `Quantity must be greater than 0 for "${lineLabel}".`,
          severity: "error",
        });
        return;
      }

      if (isNaN(rateNum) || rateNum <= 0) {
        setSnackbar({
          open: true,
          message: `Rate ($) must be greater than 0 for "${lineLabel}". Please enter a valid rate.`,
          severity: "error",
        });
        return;
      }
    }

    setSaving(true);
    try {
      const payload = buildIndividualInvoicePayload(
        (invoiceForm.statusLabel || "DRAFT").toUpperCase().replace(/\s+/g, "_")
      );
      const targetInvoiceId =
        activeClaimId ||
        searchParams.get("invoiceId") ||
        searchParams.get("id") ||
        claimId ||
        queryClaimId ||
        stateClaimId;

      let result: IndividualInvoiceDTO;
      if (targetInvoiceId) {
        result = await billingService.updateIndividualInvoice(targetInvoiceId, payload);
      } else {
        result = await billingService.createIndividualInvoice(payload);
      }

      // Finalize the invoice via POST /v1/individual-invoices/{id}/finalize
      const invoiceIdToFinalize = result?.id || targetInvoiceId;
      if (invoiceIdToFinalize) {
        try {
          const finalized = await billingService.finalizeIndividualInvoice(invoiceIdToFinalize);
          if (finalized) {
            result = finalized;
          }
        } catch (finalizeErr: any) {
          console.error("Failed to finalize individual invoice:", finalizeErr);
          setSnackbar({
            open: true,
            message: getApiErrorMessage(finalizeErr, "Invoice was saved but failed to finalize."),
            severity: "error",
          });
          setSaving(false);
          return;
        }
      }

      const invNo = result?.invoiceNumber ? `(#${result.invoiceNumber})` : "";
      setSnackbar({
        open: true,
        message: `Individual Invoice ${invNo} created and finalized successfully!`.trim(),
        severity: "success",
      });

      navigate("/billing?tab=invoices", {
        state: {
          message: `Individual Invoice ${invNo} created and finalized successfully.`.trim(),
          severity: "success",
        },
      });
    } catch (err: any) {
      console.error("Failed to save individual invoice:", err);
      setSnackbar({
        open: true,
        message: getApiErrorMessage(err, "Failed to create individual invoice."),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Submit Claim (creates if new via POST, updates via PUT if existing, then submits)
  const handleSubmitClaim = async () => {
    setFormTouched(true);
    markFieldTouched("carrierName");
    markFieldTouched("insuranceProgram");
    markFieldTouched("insuredId");
    markFieldTouched("residentFullName");
    markFieldTouched("patientSex");
    setPatientDobTouched(true);
    markFieldTouched("diagA");
    markFieldTouched("diagB");
    markFieldTouched("diagC");
    markFieldTouched("diagD");
    markFieldTouched("diagE");
    markFieldTouched("diagF");
    markFieldTouched("diagG");
    markFieldTouched("diagH");
    markFieldTouched("diagI");
    markFieldTouched("diagJ");
    markFieldTouched("diagK");
    markFieldTouched("diagL");
    markFieldTouched("principalDiagnosis");
    markFieldTouched("otherDiagnosis1");
    markFieldTouched("otherDiagnosis2");
    markFieldTouched("ub04ProviderName");
    markFieldTouched("ub04ProviderNpi");
    markFieldTouched("ub04FedTaxNo");
    markFieldTouched("ub04ProviderAddress");
    markFieldTouched("ub04ProviderCity");
    markFieldTouched("billingProviderNpi");

    if (selectedForm === "CMS_1500") {
      const carrierErr = validateCarrierName(cms.carrierName);
      const progErr = validateInsuranceProgram(cms.insuranceProgram);
      const insuredErr = validateInsuredId(cms.insuredId, cms.insuranceProgram, cms.carrierName);
      const nameErr = validateResidentFullName(cms.residentFullName);
      const dobErr = validatePatientDob(cms.patientDob, true) || (dobPickerError ? "Invalid birth date." : "");
      const sexErr = validatePatientSex(cms.patientSex);
      const cityErr = validateResidentCity(cms.patientCity);
      const stateErr = validateResidentState(cms.patientState);
      const zipErr = validateZipCode(cms.patientZip);
      const phoneErr = validatePhoneNumber(cms.patientPhone);
      const insuredCityErr = validateResidentCity(cms.insuredCity);
      const insuredStateErr = validateResidentState(cms.insuredState);
      const insuredZipErr = validateZipCode(cms.insuredZip);
      const insuredPhoneErr = validatePhoneNumber(cms.insuredPhone);

      const diagAErr = validatePrimaryDiagnosis(cms.diagA);
      const diagBErr = validateSecondaryDiagnosis(cms.diagB);
      const diagCErr = validateSecondaryDiagnosis(cms.diagC);
      const diagDErr = validateSecondaryDiagnosis(cms.diagD);
      const diagEErr = validateSecondaryDiagnosis(cms.diagE);
      const diagFErr = validateSecondaryDiagnosis(cms.diagF);
      const diagGErr = validateSecondaryDiagnosis(cms.diagG);
      const diagHErr = validateSecondaryDiagnosis(cms.diagH);
      const diagIErr = validateSecondaryDiagnosis(cms.diagI);
      const diagJErr = validateSecondaryDiagnosis(cms.diagJ);
      const diagKErr = validateSecondaryDiagnosis(cms.diagK);
      const diagLErr = validateSecondaryDiagnosis(cms.diagL);

      const firstErr = carrierErr || progErr || insuredErr || nameErr || dobErr || sexErr
        || cityErr || stateErr || zipErr || phoneErr
        || insuredCityErr || insuredStateErr || insuredZipErr || insuredPhoneErr
        || validateOtherInsuredName(cms.otherInsuredName)
        || validateOtherInsuredPolicy(cms.otherInsuredPolicy)
        || validateOtherInsuredPlanName(cms.otherInsuredPlanName)
        || validateAutoAccidentState(cms.conditionAutoState, cms.conditionAutoAccident)
        || validateClaimCodes10d(cms.claimCodes10d)
        || validateInsuredPolicyGroup(cms.insuredPolicyGroup)
        || validateOtherClaimId(cms.otherClaimId)
        || validateInsurancePlanName(cms.insurancePlanName, cms.anotherBenefitPlan)
        || validateIllnessDateQual(cms.dateCurrentIllnessQual)
        || validateOtherDateQual(cms.otherDateQual)
        || validateReferringProviderName(cms.referringProviderName)
        || validateReferringProviderOtherId(cms.referringProviderOtherId)
        || validateReferringProviderNpi(cms.referringProviderNpi)
        || validateBillingProviderNpi(cms.billingProviderNpi)
        || validateAdditionalClaimInfo(cms.additionalClaimInfo)
        || validateOutsideLabCharges(cms.outsideLabCharges)
        || diagAErr || diagBErr || diagCErr || diagDErr || diagEErr || diagFErr || diagGErr || diagHErr || diagIErr || diagJErr || diagKErr || diagLErr;

      if (firstErr) {
        setSnackbar({
          open: true,
          message: firstErr,
          severity: "error",
        });
        scrollToFirstInvalidField();
        return;
      }
    } else {
      markFieldTouched("ub04ProviderName");
      markFieldTouched("ub04ProviderNpi");
      markFieldTouched("ub04FedTaxNo");
      markFieldTouched("ub04ProviderAddress");
      markFieldTouched("ub04ProviderCity");

      const providerNameErr = validateProviderFacilityName(ub04Form.providerName);
      const providerNpiErr = validateFacilityNpi(ub04Form.providerNpi);
      const fedTaxNoErr = validateFederalTaxId(ub04Form.fedTaxNo);
      const providerAddressErr = validateFacilityStreetAddress(ub04Form.providerAddress);
      const providerCityErr = validateFacilityCity(ub04Form.providerCity);

      const dobValue = ub04Form.patientDob;
      const dobErr = validatePatientDob(dobValue, true);
      const ub04DiagErr = validatePrimaryDiagnosis(ub04Form.principalDiagnosis)
        || validateSecondaryDiagnosis(ub04Form.otherDiagnosis1)
        || validateSecondaryDiagnosis(ub04Form.otherDiagnosis2);

      const firstUb04Err = providerNameErr || providerNpiErr || fedTaxNoErr || providerAddressErr || providerCityErr || dobErr || ub04DiagErr;
      if (firstUb04Err || dobPickerError) {
        setSnackbar({
          open: true,
          message: firstUb04Err || "Please complete all required fields.",
          severity: "error",
        });
        scrollToFirstInvalidField();
        return;
      }
    }

    const request = buildClaimRequest();
    const missing = getMissingFields(request);
    if (missing.length > 0) {
      setSnackbar({
        open: true,
        message: `Please complete required fields: ${missing.join(", ")}`,
        severity: "error",
      });
      scrollToFirstInvalidField();
      return;
    }

    const targetId =
      activeClaimId ||
      claimId ||
      queryClaimId ||
      stateClaimId ||
      existingClaim?.id ||
      (existingClaim as any)?.claimId ||
      (existingClaim as any)?._id;

    setSaving(true);
    let saved: BillingClaimDTO;
    try {
      saved = targetId
        ? await billingService.updateBillingClaim(targetId, request)
        : await billingService.createBillingClaim(request);
    } catch (err: any) {
      setSnackbar({ open: true, message: getApiErrorMessage(err, "Could not save the claim."), severity: "error" });
      setSaving(false);
      return;
    }

    const claimLabel = `${formDefinition?.name} claim ${saved.claimNumber || ""}`.trim();
    const claimTargetId = saved.id || (saved as any)?.claimId || targetId;
    try {
      await billingService.submitBillingClaim(claimTargetId!);
      navigate("/billing", { state: { message: "Claim submitted successfully!" } });
    } catch (err: any) {
      // The draft exists now, so leave the editor rather than letting a retry create a duplicate.
      navigate("/billing", {
        state: {
          message: `${claimLabel} was saved as a draft but could not be submitted (${getApiErrorMessage(err, "unknown error")}). Submit it from the list.`,
          severity: "warning",
        },
      });
    }
  };

  /**
   * Print claim PDF on official form (CMS-1500 / UB-04)
   * Calls GET /v1/billing-claims/{id}/pdf
   */
  const handlePrintClaimPdf = async () => {
    if (selectedForm === "INVOICE") {
      let targetInvoiceId =
        activeClaimId ||
        searchParams.get("invoiceId") ||
        searchParams.get("id") ||
        claimId ||
        queryClaimId ||
        stateClaimId;

      if (!targetInvoiceId) {
        if (!selectedResidentId) {
          setSnackbar({
            open: true,
            message: "Please select a resident and save the invoice draft before printing.",
            severity: "error",
          });
          scrollToFirstInvalidField();
          return;
        }
        try {
          setPrintingPdf(true);
          const payload = buildIndividualInvoicePayload("DRAFT");
          const created = await billingService.createIndividualInvoice(payload);
          targetInvoiceId = created.id;
          if (targetInvoiceId) {
            setActiveClaimId(targetInvoiceId);
          }
        } catch (saveErr: any) {
          setSnackbar({
            open: true,
            message: getApiErrorMessage(saveErr, "Please save the draft invoice before printing."),
            severity: "error",
          });
          setPrintingPdf(false);
          return;
        }
      }

      if (targetInvoiceId) {
        setPrintingPdf(true);
        try {
          await printInvoicePdfDirect(targetInvoiceId);
        } catch (err: any) {
          console.error("Failed to generate invoice PDF:", err);
          try {
            await downloadInvoicePdf(targetInvoiceId, invoiceForm.invoiceNumber);
            setSnackbar({
              open: true,
              message: "Direct print failed; invoice PDF downloaded instead.",
              severity: "info",
            });
          } catch (dlErr: any) {
            setSnackbar({
              open: true,
              message: getApiErrorMessage(err, "Could not generate the invoice PDF."),
              severity: "error",
            });
          }
        } finally {
          setPrintingPdf(false);
        }
      }
      return;
    }

    let targetClaimId =
      activeClaimId ||
      claimId ||
      queryClaimId ||
      stateClaimId ||
      existingClaim?.id ||
      (existingClaim as any)?.claimId ||
      (existingClaim as any)?._id;

    if (!targetClaimId) {
      if (!selectedResidentId) {
        setSnackbar({
          open: true,
          message: "Please select a resident and save the claim draft before printing.",
          severity: "error",
        });
        scrollToFirstInvalidField();
        return;
      }
      try {
        setPrintingPdf(true);
        const req = buildClaimRequest();
        const saved = await billingService.createBillingClaim(req);
        targetClaimId = saved.id || (saved as any)?.claimId;
        if (targetClaimId) {
          setActiveClaimId(targetClaimId);
          setExistingClaim(saved);
        }
      } catch (saveErr: any) {
        setSnackbar({
          open: true,
          message: getApiErrorMessage(saveErr, "Please save the draft claim before printing."),
          severity: "error",
        });
        setPrintingPdf(false);
        return;
      }
    }

    if (targetClaimId) {
      setPrintingPdf(true);
      try {
        await printClaimPdfDirect(targetClaimId);
      } catch (err: any) {
        console.error("Failed to generate claim PDF:", err);
        setSnackbar({
          open: true,
          message: getApiErrorMessage(err, "Could not generate the claim form PDF."),
          severity: "error",
        });
      } finally {
        setPrintingPdf(false);
      }
    }
  };

  // Clear every field on the form
  const handleResetForm = () => {
    const effFac = getEffectiveFacility(currentFacility, tenant?.name);
    setPatientDobTouched(false);
    setFormTouched(false);
    setSelectedResidentId("");
    setCms({
      ...emptyCmsState,
      facilityName: effFac.name,
      facilityAddress: effFac.address,
      facilityCity: effFac.city,
      facilityState: effFac.state,
      facilityZip: effFac.postalCode,
      facilityNpi: effFac.npiNumber,
      billingProviderName: effFac.name,
      billingProviderAddress: effFac.address,
      billingProviderCity: effFac.city,
      billingProviderState: effFac.state,
      billingProviderZip: effFac.postalCode,
      billingProviderPhone: effFac.phone,
      billingProviderNpi: effFac.npiNumber,
      taxId: effFac.taxId,
    });
    setUb04Form({
      ...emptyUb04State,
      providerName: effFac.name,
      providerAddress: effFac.address,
      providerCity: effFac.city,
      providerState: effFac.state,
      providerZip: effFac.postalCode,
      providerPhone: effFac.phone,
      providerNpi: effFac.npiNumber,
      fedTaxNo: effFac.taxId,
      typeOfBill: effFac.typeOfBill,
    });
    setResidentInsurances([]);
    setSelectedInsuranceId("");
    setCurrentResidentData(null);
    setServiceLines([
      {
        id: "1",
        dateFrom: "",
        dateTo: "",
        pos: "",
        emg: "",
        cpt: "",
        mod1: "",
        mod2: "",
        mod3: "",
        mod4: "",
        diagPointer: "",
        charges: "",
        daysUnits: "1",
        epsdt: "",
        idQual: "",
        renderingNpi: "",
      },
    ]);
    setUb04RevenueLines(initialUb04RevenueLines);
    setInvoiceForm(emptyInvoiceState);
    setInvoiceLines(initialInvoiceLines);
    setSnackbar({ open: true, message: `${formDefinition?.name} fields cleared.`, severity: "info" });
  };

  if (effectiveFetchId && claimLoadError) {
    return (
      <Box sx={{ maxWidth: 1300, mx: "auto", pt: 2 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => navigate("/billing")}>Back to claims</Button>}>
          {claimLoadError}
        </Alert>
      </Box>
    );
  }

  if (effectiveFetchId && !existingClaim) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!formDefinition) {
    return <Navigate to="/billing" replace />;
  }

  const statusMeta = existingClaim?.status ? CLAIM_STATUS_META[existingClaim.status] : undefined;

  const sectionCardSx = {
    p: { xs: 2.5, sm: 3.5 },
    mb: 3,
    borderRadius: "16px",
    border: "1px solid #E2E8F0",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.04)",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      transition: "all 0.2s ease-in-out",
      "& fieldset": {
        borderColor: "#CBD5E1",
      },
      "&:hover fieldset": {
        borderColor: "#94A3B8",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0077B6",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
      backgroundColor: "#FFFFFF",
      padding: "0 4px",
      color: "#1A2B45 !important",
    },
    "& .MuiInputLabel-root": {
      color: "#1A2B45 !important",
      fontWeight: 500,
      "&.Mui-focused": {
        color: "#1A2B45 !important",
        fontWeight: 500,
      },
    },
    "& .MuiFormLabel-root": {
      color: "#1A2B45 !important",
      "&.Mui-focused": {
        color: "#1A2B45 !important",
      },
    },
    "& .MuiFormLabel-asterisk": {
      color: "#ef4444",
    },
    "& .MuiInputBase-input, & .MuiSelect-select": {
      color: "#1A2B45 !important",
      WebkitTextFillColor: "#1A2B45",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#94A3B8",
      opacity: 1,
    },
  };

  const renderSectionHeader = (stepNumber: number | string, title: string, subtitle?: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 2, mb: 2.5, borderBottom: "1px solid #F1F5F9" }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          backgroundColor: "#E0F2FE",
          color: "#0284C7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: "0.9rem",
          flexShrink: 0,
        }}
      >
        {stepNumber}
      </Box>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0F172A", lineHeight: 1.25 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "#64748B", display: "block", mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ pb: 8, maxWidth: 1300, mx: "auto" }}>
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 3,
          "@media print": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => navigate("/billing")} aria-label="Back to claims">
            <ArrowBackIcon />
          </IconButton>
          <ReceiptIcon sx={{ fontSize: 36, color: "#48CAE4" }} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
              <Typography variant="h5" fontWeight="700" sx={{ color: "text.primary" }}>
                {existingClaim ? `${existingClaim.claimNumber}` : `New ${formDefinition.name} Claim`}
              </Typography>
              {statusMeta && <Chip size="small" label={statusMeta.label} color={statusMeta.color} sx={{ fontWeight: 600 }} />}
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>
              {formDefinition.name} · {formDefinition.category} claim · {formDefinition.altName}
            </Typography>
          </Box>
        </Box>
        {Boolean(selectedResidentId) && (
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={printingPdf ? <CircularProgress size={16} /> : <PrintIcon />}
              disabled={printingPdf}
              onClick={handlePrintClaimPdf}
              sx={{ borderRadius: "12px", px: 2.5 }}
            >
              {printingPdf ? "Generating PDF..." : "Print"}
            </Button>
            {!isReadOnly && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  disabled={saving}
                  onClick={handleSaveDraft}
                  sx={{ borderRadius: "12px", px: 2.5 }}
                >
                  Save Draft
                </Button>
                {selectedForm === "INVOICE" ? (
                  <Button
                    id="create-invoice-top-btn"
                    variant="contained"
                    size="small"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}
                    disabled={saving}
                    onClick={handleCreateIndividualInvoice}
                    sx={{
                      borderRadius: "12px",
                      px: 2.5,
                      boxShadow: "none",
                      backgroundColor: "#48CAE4",
                      color: "#000000",
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#3ab3cc", boxShadow: "none" },
                    }}
                  >
                    {saving ? "Creating Invoice..." : "Create Invoice"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SendIcon />}
                    disabled={saving}
                    onClick={handleSubmitClaim}
                    sx={{
                      borderRadius: "12px",
                      px: 2.5,
                      boxShadow: "none",
                      backgroundColor: "#48CAE4",
                      color: "#000000",
                      "&:hover": { backgroundColor: "#3ab3cc", boxShadow: "none" },
                    }}
                  >
                    Submit Claim
                  </Button>
                )}
              </>
            )}
          </Box>
        )}
      </Box>

      {isReadOnly && (
        <Alert severity="info" sx={{ mb: 3, "@media print": { display: "none" } }}>
          This claim is {statusMeta?.label.toLowerCase()} and can no longer be edited. You can still print it.
        </Alert>
      )}

      {/* Native fieldset "disabled" makes every input read-only for submitted claims */}
      <Box component="fieldset" disabled={isReadOnly} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>

        {/* ── Controls Bar (Resident Selector - styled same as Notes) ── */}
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            p: 2,
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Autocomplete
            id="resident-selector"
            disabled={isReadOnly}
            options={residentOptions}
            getOptionLabel={(option: any) => {
              if (typeof option === "string") return option;
              const displayName =
                option.residentName ||
                [option.firstName, option.middleName, option.lastName].filter(Boolean).join(" ") ||
                `${option.firstName || ""} ${option.lastName || ""}`.trim();
              const roomBed = option.roomNumber
                ? ` (Room: ${option.roomNumber}${option.bedNumber ? `, Bed: ${option.bedNumber}` : ""})`
                : "";
              return `${displayName}${roomBed}` || "Unknown Resident";
            }}
            loading={residentsLoading}
            value={selectedResident}
            onChange={(_event: any, newValue: any | null) => {
              const newId = newValue ? (newValue.id || newValue.residentId) : "";
              handleResidentSelect(newId);
            }}
            inputValue={residentSearchInput}
            onInputChange={(_event, newInputValue) => {
              setResidentSearchInput(newInputValue);
            }}
            isOptionEqualToValue={(option: any, value: any) =>
              Boolean(option && value && (option.id || option.residentId) === (value.id || value.residentId))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Resident by Name"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <React.Fragment>
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </React.Fragment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {residentsLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f5f7f8",
                  },
                }}
              />
            )}
            sx={{ flex: 1, minWidth: 250 }}
          />
          <Tooltip title="Reset & Refresh Residents List">
            <span>
              <IconButton
                onClick={() => {
                  setSelectedResidentId("");
                  setCms(emptyCmsState);
                  setUb04Form(emptyUb04State);
                  setInvoiceForm(emptyInvoiceState);
                  setInvoiceLines(initialInvoiceLines);
                  setResidentInsurances([]);
                  setSelectedInsuranceId("");
                  setCurrentResidentData(null);
                  fetchResidents();
                }}
                disabled={residentsLoading || isReadOnly}
                sx={{
                  color: residentsLoading ? "#94A3B8" : "#0284C7",
                  "&:hover": { backgroundColor: "rgba(2, 132, 199, 0.08)" },
                }}
              >
                <RefreshIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════════════
          CMS-1500 DIGITAL FORM (MODERN, SPACIOUS, ALL FIELDS INCLUDED)
          ═══════════════════════════════════════════════════════════════════════ */}
        {selectedForm === "CMS_1500" && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 3 }}>
              {/* Top Info Banner */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 2.5,
                  border: "1px solid #BAE6FD",
                  backgroundColor: "#F0F9FF",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.dark" }}>
                    CMS-1500 Professional Claim Form
                  </Typography>
                  <Chip label="NUCC 02/12 Compliant" size="small" sx={{ backgroundColor: "#E0F2FE", color: "primary.dark", fontWeight: 600 }} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  All standard CMS-1500 professional claim fields are organized in clean sections below.
                </Typography>
              </Paper>

              {/* ── Conditional Display: Show form fields only when resident is selected ── */}
              <Box sx={{ display: selectedResidentId ? "none" : "block" }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 5,
                    textAlign: "center",
                    borderRadius: 2.5,
                    border: "1px dashed #BAE6FD",
                    backgroundColor: "background.default",
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 48, color: "primary.main", mb: 1.5, opacity: 0.8 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}>
                    Please Select a Resident to View & Fill Form
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select a resident from the "Search Resident by Name" box above to display and populate the CMS-1500 claim fields.
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ display: selectedResidentId ? "block" : "none" }}>
                {/* ── 1. CARRIER & ITEMS 1 - 3: CARRIER & RESIDENT DEMOGRAPHICS ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    1,
                    "Carrier Information & Items 1 - 3: Resident Demographics",
                    "Payer identification, resident profile and demographic details"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Row 1: Carrier & Items 1, 1a */}
                    <Grid item xs={12} sm={4}>
                      {residentInsurances.length > 1 ? (
                        <FormControl
                          fullWidth
                          size="small"
                          required
                          error={Boolean(carrierNameError)}
                          sx={{
                            "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                          }}
                        >
                          <InputLabel shrink id="cms-carrier-name-label">
                            Carrier / Payer Name
                          </InputLabel>
                          <Select
                            id="cms-carrier-name"
                            labelId="cms-carrier-name-label"
                            label="Carrier / Payer Name"
                            notched
                            value={effectiveSelectedInsuranceId}
                            onChange={(e) => {
                              markFieldTouched("carrierName");
                              handleInsuranceCardSelect(e.target.value);
                            }}
                            onBlur={() => markFieldTouched("carrierName")}
                            error={Boolean(carrierNameError)}
                          >
                            {residentInsurances.map((card, idx) => {
                              const valId = card.id || String(idx);
                              const name = card.insuranceCompanyName || card.provider || "Unnamed Insurance";
                              const typeTag = card.coverageType || card.insuranceType;
                              const labelText = typeTag ? `${name} (${typeTag})` : name;

                              return (
                                <MenuItem key={valId} value={valId}>
                                  {labelText}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          {Boolean(carrierNameError) && (
                            <FormHelperText error>{carrierNameError}</FormHelperText>
                          )}
                        </FormControl>
                      ) : (
                        <TextField
                          id="cms-carrier-name"
                          InputProps={{ id: "cms-carrier-name" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          required
                          error={Boolean(carrierNameError)}
                          helperText={carrierNameError || undefined}
                          label="Carrier / Payer Name"
                          value={cms.carrierName}
                          onChange={(e) => {
                            markFieldTouched("carrierName");
                            updateField("carrierName", e.target.value);
                          }}
                          onBlur={() => markFieldTouched("carrierName")}
                          sx={{
                            "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                          }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        size="small"
                        required
                        error={Boolean(insuranceProgramError)}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                        }}
                      >
                        <InputLabel id="cms-insurance-type-label">1. Type of Insurance</InputLabel>
                        <Select
                          id="cms-insurance-type"
                          labelId="cms-insurance-type-label"
                          value={
                            cms.insuranceProgram === "GROUP_HEALTH"
                              ? "GROUP_HEALTH_PLAN"
                              : cms.insuranceProgram === "FECA"
                                ? "FECA_BLK_LUNG"
                                : cms.insuranceProgram || ""
                          }
                          label="1. Type of Insurance"
                          error={Boolean(insuranceProgramError)}
                          onChange={(e) => {
                            markFieldTouched("insuranceProgram");
                            const newProgram = e.target.value;
                            updateField("insuranceProgram", newProgram);
                            if (cms.insuredId) {
                              const newRule = getInsuranceProviderRule(newProgram, cms.carrierName);
                              if (cms.insuredId.length > newRule.max) {
                                updateField("insuredId", cms.insuredId.slice(0, newRule.max));
                              }
                            }
                          }}
                          onBlur={() => markFieldTouched("insuranceProgram")}
                        >
                          <MenuItem value=""><em>Select Insurance Type</em></MenuItem>
                          <MenuItem value="MEDICARE">Medicare</MenuItem>
                          <MenuItem value="MEDICAID">Medicaid</MenuItem>
                          <MenuItem value="TRICARE">TRICARE</MenuItem>
                          <MenuItem value="CHAMPVA">CHAMPVA</MenuItem>
                          <MenuItem value="GROUP_HEALTH_PLAN">Group Health Plan</MenuItem>
                          <MenuItem value="FECA_BLK_LUNG">FECA Black Lung</MenuItem>
                          <MenuItem value="OTHER">Other Commercial</MenuItem>
                        </Select>
                        {Boolean(insuranceProgramError) && (
                          <FormHelperText error>{insuranceProgramError}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-insured-id"
                        InputProps={{ id: "cms-insured-id" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        required
                        label="1a. Insured's I.D. Number"
                        inputProps={{
                          maxLength: activeInsuranceRule.max,
                        }}
                        value={cms.insuredId}
                        error={Boolean(insuredIdError)}
                        helperText={insuredIdError || undefined}
                        onChange={(e) => {
                          markFieldTouched("insuredId");
                          const rule = getInsuranceProviderRule(cms.insuranceProgram, cms.carrierName);
                          // Allowed characters: Letters (A–Z), numbers (0–9), and hyphens (-)
                          // Disallow special characters and spaces completely
                          const filtered = e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9-]/g, "")
                            .replace(/-{2,}/g, "-")
                            .replace(/^-+/, "")
                            .slice(0, rule.max);
                          updateField("insuredId", filtered);
                        }}
                        onBlur={() => markFieldTouched("insuredId")}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                        }}
                      />
                    </Grid>

                    {/* Row 2: Items 2, 3 */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-resident-full-name"
                        InputProps={{ id: "cms-resident-full-name" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        required
                        label="Resident Full Name"
                        value={cms.residentFullName}
                        error={Boolean(residentFullNameError)}
                        helperText={residentFullNameError || undefined}
                        onChange={(e) => {
                          markFieldTouched("residentFullName");
                          const val = e.target.value.replace(/[^A-Za-z\s'-]/g, "");
                          updateField("residentFullName", val);
                          const parts = val.trim().split(/\s+/);
                          if (parts.length === 1) {
                            updateField("patientFirstName", parts[0] || "");
                            updateField("patientLastName", "");
                          } else if (parts.length > 1) {
                            updateField("patientFirstName", parts[0] || "");
                            updateField("patientLastName", parts.slice(1).join(" ") || "");
                          }
                        }}
                        onBlur={() => markFieldTouched("residentFullName")}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="3. Resident's Birth Date"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(cms.patientDob)}
                        onChange={(newDate: Date | null) => {
                          setPatientDobTouched(true);
                          if (newDate && !isNaN(newDate.getTime())) {
                            updateField("patientDob", formatDateToString(newDate));
                          } else {
                            updateField("patientDob", "");
                          }
                        }}
                        onError={(newError) => {
                          setDobPickerError(newError ? String(newError) : null);
                        }}
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            required: true,
                            error: Boolean(patientDobError),
                            helperText: patientDobError || undefined,
                            onBlur: () => setPatientDobTouched(true),
                            sx: {
                              backgroundColor: "background.paper",
                              "& .MuiFormLabel-asterisk": {
                                color: "#d32f2f !important",
                              },
                            },
                          },
                        }}
                        sx={{
                          "& .MuiFormLabel-asterisk": {
                            color: "#d32f2f !important",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        size="small"
                        required
                        error={Boolean(patientSexError)}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                        }}
                      >
                        <InputLabel id="cms-resident-gender-label">3. Resident's Gender</InputLabel>
                        <Select
                          id="cms-resident-gender"
                          labelId="cms-resident-gender-label"
                          value={parseGenderToSex(cms.patientSex)}
                          label="3. Resident's Gender"
                          error={Boolean(patientSexError)}
                          onChange={(e) => {
                            markFieldTouched("patientSex");
                            updateField("patientSex", e.target.value);
                          }}
                          onBlur={() => markFieldTouched("patientSex")}
                        >
                          <MenuItem value=""><em>Select Gender</em></MenuItem>
                          <MenuItem value="M">Male (M)</MenuItem>
                          <MenuItem value="F">Female (F)</MenuItem>
                        </Select>
                        {Boolean(patientSexError) && (
                          <FormHelperText error>{patientSexError}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── 2. ITEMS 4 - 8: INSURED (SUBSCRIBER) & RESIDENT ADDRESS ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    2,
                    "Items 4 - 8: Insured (Subscriber) & Resident Address",
                    "Subscriber identity, family relation, and physical contact addresses"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Row 1: Subscriber Info & Relationship */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-insured-name"
                        InputProps={{ id: "cms-insured-name" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="4. Insured's Name"
                        value={cms.insuredLastName}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^A-Za-z\s,.'-]/g, "");
                          updateField("insuredLastName", val);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>6. Resident Relationship to Insured</InputLabel>
                        <Select
                          value={normalizeRelationship(cms.patientRelationship)}
                          label="6. Resident Relationship to Insured"
                          onChange={(e) => updateField("patientRelationship", e.target.value)}
                        >
                          <MenuItem value=""><em>Select Relationship</em></MenuItem>
                          <MenuItem value="Self">Self</MenuItem>
                          <MenuItem value="Spouse">Spouse</MenuItem>
                          <MenuItem value="Child">Child</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="8. Reserved for NUCC Use"
                        value={cms.reservedNuccBox8}
                        onChange={(e) => updateField("reservedNuccBox8", e.target.value)}
                      />
                    </Grid>

                    {/* Row 2: Resident's Address vs Insured's Address Side-by-Side Cards */}
                    <Grid item xs={12} md={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: "14px",
                          backgroundColor: "#FFFFFF",
                          borderColor: "#CBD5E1",
                          height: "100%",
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1E293B", mb: 2 }}>
                          5. Resident's Address & Contact
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="5. Resident's Address (Street)"
                              value={cms.patientAddress}
                              onChange={(e) => updateField("patientAddress", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              id="cms-patient-city"
                              InputProps={{ id: "cms-patient-city" }}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="5. Resident's City"
                              value={cms.patientCity}
                              error={Boolean(patientCityError)}
                              helperText={patientCityError || undefined}
                              onChange={(e) => {
                                markFieldTouched("patientCity");
                                const val = e.target.value.replace(/[^A-Za-z\s.'-]/g, "");
                                updateField("patientCity", val);
                              }}
                              onBlur={() => markFieldTouched("patientCity")}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3.5}>
                            <TextField
                              id="cms-patient-state"
                              InputProps={{ id: "cms-patient-state" }}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="5. Resident's State"
                              value={cms.patientState}
                              error={Boolean(patientStateError)}
                              helperText={patientStateError || undefined}
                              onChange={(e) => {
                                markFieldTouched("patientState");
                                const val = e.target.value.replace(/[^A-Za-z\s-]/g, "");
                                updateField("patientState", val);
                              }}
                              onBlur={() => markFieldTouched("patientState")}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3.5}>
                            <TextField
                              id="cms-patient-zip"
                              InputProps={{ id: "cms-patient-zip" }}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="5. Resident's ZIP Code"
                              value={cms.patientZip}
                              error={Boolean(patientZipError)}
                              helperText={patientZipError || undefined}
                              inputProps={{ maxLength: 10 }}
                              onChange={(e) => {
                                markFieldTouched("patientZip");
                                const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10);
                                updateField("patientZip", val);
                              }}
                              onBlur={() => markFieldTouched("patientZip")}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              id="cms-patient-phone"
                              InputProps={{ id: "cms-patient-phone" }}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="5. Resident's Telephone"
                              value={cms.patientPhone}
                              error={Boolean(patientPhoneError)}
                              helperText={patientPhoneError || undefined}
                              inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                              onChange={(e) => {
                                markFieldTouched("patientPhone");
                                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                updateField("patientPhone", val);
                              }}
                              onBlur={() => markFieldTouched("patientPhone")}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: "14px",
                          backgroundColor: "#FFFFFF",
                          borderColor: "#CBD5E1",
                          height: "100%",
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1E293B", mb: 2 }}>
                          7. Insured's Address & Contact
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="7. Insured's Address (Street)"
                              value={cms.insuredAddress}
                              onChange={(e) => updateField("insuredAddress", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              id="cms-insured-city"
                              InputProps={{ id: "cms-insured-city" }}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="7. Insured's City"
                              value={cms.insuredCity}
                              error={Boolean(insuredCityError)}
                              helperText={insuredCityError || undefined}
                              onChange={(e) => {
                                markFieldTouched("insuredCity");
                                const val = e.target.value.replace(/[^A-Za-z\s.'-]/g, "");
                                updateField("insuredCity", val);
                              }}
                              onBlur={() => markFieldTouched("insuredCity")}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3.5}>
                            <TextField
                              id="cms-insured-state"
                              InputProps={{ id: "cms-insured-state" }}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="7. Insured's State"
                              value={cms.insuredState}
                              error={Boolean(insuredStateError)}
                              helperText={insuredStateError || undefined}
                              onChange={(e) => {
                                markFieldTouched("insuredState");
                                const val = e.target.value.replace(/[^A-Za-z\s-]/g, "");
                                updateField("insuredState", val);
                              }}
                              onBlur={() => markFieldTouched("insuredState")}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3.5}>
                            <TextField
                              id="cms-insured-zip"
                              InputProps={{ id: "cms-insured-zip" }}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="7. Insured's ZIP Code"
                              value={cms.insuredZip}
                              error={Boolean(insuredZipError)}
                              helperText={insuredZipError || undefined}
                              inputProps={{ maxLength: 10 }}
                              onChange={(e) => {
                                markFieldTouched("insuredZip");
                                const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10);
                                updateField("insuredZip", val);
                              }}
                              onBlur={() => markFieldTouched("insuredZip")}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              id="cms-insured-phone"
                              InputProps={{ id: "cms-insured-phone" }}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              size="small"
                              label="7. Insured's Telephone"
                              value={cms.insuredPhone}
                              error={Boolean(insuredPhoneError)}
                              helperText={insuredPhoneError || undefined}
                              inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                              onChange={(e) => {
                                markFieldTouched("insuredPhone");
                                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                updateField("insuredPhone", val);
                              }}
                              onBlur={() => markFieldTouched("insuredPhone")}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── 3. ITEMS 9 - 11: OTHER INSURED, ACCIDENT & POLICY DETAILS ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    3,
                    "Items 9 - 11: Other Insured, Accident Details & Policy Info",
                    "Secondary coverage, accident causation and primary policy numbers"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Row 1: 9, 9a, 9d */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-other-insured-name"
                        InputProps={{ id: "cms-other-insured-name" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="9. Other Insured's Name"
                        value={cms.otherInsuredName}
                        error={Boolean(otherInsuredNameError)}
                        helperText={otherInsuredNameError || undefined}
                        onChange={(e) => {
                          markFieldTouched("otherInsuredName");
                          const val = e.target.value.replace(/[^A-Za-z\s,.'-]/g, "");
                          updateField("otherInsuredName", val);
                        }}
                        onBlur={() => markFieldTouched("otherInsuredName")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-other-insured-policy"
                        InputProps={{ id: "cms-other-insured-policy" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="9a. Other Insured's Policy / Group Number"
                        value={cms.otherInsuredPolicy}
                        error={Boolean(otherInsuredPolicyError)}
                        helperText={otherInsuredPolicyError || undefined}
                        onChange={(e) => {
                          markFieldTouched("otherInsuredPolicy");
                          const val = e.target.value.replace(/[^A-Za-z0-9\-_/ ]/g, "");
                          updateField("otherInsuredPolicy", val);
                        }}
                        onBlur={() => markFieldTouched("otherInsuredPolicy")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-other-insured-plan"
                        InputProps={{ id: "cms-other-insured-plan" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="9d. Insurance Plan Name or Program Name"
                        value={cms.otherInsuredPlanName}
                        error={Boolean(otherInsuredPlanNameError)}
                        helperText={otherInsuredPlanNameError || undefined}
                        onChange={(e) => {
                          markFieldTouched("otherInsuredPlanName");
                          const val = e.target.value.replace(/[^A-Za-z0-9\s.,'&/()#-]/g, "");
                          updateField("otherInsuredPlanName", val);
                        }}
                        onBlur={() => markFieldTouched("otherInsuredPlanName")}
                      />
                    </Grid>

                    {/* Row 2: 9b, 9c NUCC */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="9b. Reserved for NUCC Use"
                        value={cms.reservedNuccBox9b}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        disabled
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="9c. Reserved for NUCC Use"
                        value={cms.reservedNuccBox9c}
                      />
                    </Grid>

                    {/* Row 3: 10a, 10b, 10b State, 10c */}
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>10a. Condition Related to Employment?</InputLabel>
                        <Select
                          value={cms.conditionEmployment}
                          label="10a. Condition Related to Employment?"
                          onChange={(e) => updateField("conditionEmployment", e.target.value)}
                        >
                          <MenuItem value="NO">No</MenuItem>
                          <MenuItem value="YES">Yes</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="cms-auto-accident-label">10b. Auto Accident?</InputLabel>
                        <Select
                          labelId="cms-auto-accident-label"
                          value={cms.conditionAutoAccident}
                          label="10b. Auto Accident?"
                          onChange={(e) => {
                            updateField("conditionAutoAccident", e.target.value);
                            if (e.target.value === "YES") {
                              markFieldTouched("conditionAutoState");
                            }
                          }}
                        >
                          <MenuItem value="NO">No</MenuItem>
                          <MenuItem value="YES">Yes</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        id="cms-condition-auto-state"
                        InputProps={{ id: "cms-condition-auto-state" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="10b. Auto Accident Place (State)"
                        value={cms.conditionAutoState}
                        error={Boolean(conditionAutoStateError)}
                        helperText={conditionAutoStateError || undefined}
                        inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
                        onChange={(e) => {
                          markFieldTouched("conditionAutoState");
                          const val = e.target.value.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();
                          updateField("conditionAutoState", val);
                        }}
                        onBlur={() => markFieldTouched("conditionAutoState")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>10c. Other Accident?</InputLabel>
                        <Select
                          value={cms.conditionOtherAccident}
                          label="10c. Other Accident?"
                          onChange={(e) => updateField("conditionOtherAccident", e.target.value)}
                        >
                          <MenuItem value="NO">No</MenuItem>
                          <MenuItem value="YES">Yes</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Row 4: 10d, 11, 11a DOB */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-claim-codes-10d"
                        InputProps={{ id: "cms-claim-codes-10d" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="10d. Claim Codes (Designated by NUCC)"
                        value={cms.claimCodes10d}
                        error={Boolean(claimCodes10dError)}
                        helperText={claimCodes10dError || undefined}
                        onChange={(e) => {
                          markFieldTouched("claimCodes10d");
                          const val = e.target.value.replace(/[^A-Za-z0-9\s,-]/g, "");
                          updateField("claimCodes10d", val);
                        }}
                        onBlur={() => markFieldTouched("claimCodes10d")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-insured-policy-group"
                        InputProps={{ id: "cms-insured-policy-group" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="11. Insured's Policy Group or FECA Number"
                        value={cms.insuredPolicyGroup}
                        error={Boolean(insuredPolicyGroupError)}
                        helperText={insuredPolicyGroupError || undefined}
                        onChange={(e) => {
                          markFieldTouched("insuredPolicyGroup");
                          const val = e.target.value.replace(/[^A-Za-z0-9\-_/#. ]/g, "");
                          updateField("insuredPolicyGroup", val);
                        }}
                        onBlur={() => markFieldTouched("insuredPolicyGroup")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="11a. Insured's Date of Birth"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(cms.insuredDob)}
                        onChange={(newDate: Date | null) =>
                          updateField("insuredDob", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                          },
                        }}
                      />
                    </Grid>

                    {/* Row 5: 11a Gender, 11b, 11c */}
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>11a. Insured's Gender</InputLabel>
                        <Select
                          value={cms.insuredSex}
                          label="11a. Insured's Gender"
                          onChange={(e) => updateField("insuredSex", e.target.value)}
                        >
                          <MenuItem value=""><em>Select Gender</em></MenuItem>
                          <MenuItem value="M">Male (M)</MenuItem>
                          <MenuItem value="F">Female (F)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-other-claim-id"
                        InputProps={{ id: "cms-other-claim-id" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="11b. Other Claim ID (Designated by NUCC)"
                        value={cms.otherClaimId}
                        error={Boolean(otherClaimIdError)}
                        helperText={otherClaimIdError || undefined}
                        onChange={(e) => {
                          markFieldTouched("otherClaimId");
                          const val = e.target.value.replace(/[^A-Za-z0-9\-_./# ]/g, "");
                          updateField("otherClaimId", val);
                        }}
                        onBlur={() => markFieldTouched("otherClaimId")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-insurance-plan-name"
                        InputProps={{ id: "cms-insurance-plan-name" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="11c. Insurance Plan Name or Program Name"
                        value={cms.insurancePlanName}
                        error={Boolean(insurancePlanNameError)}
                        helperText={insurancePlanNameError || undefined}
                        onChange={(e) => {
                          markFieldTouched("insurancePlanName");
                          const val = e.target.value.replace(/[^A-Za-z0-9\s.,'&/()#-]/g, "");
                          updateField("insurancePlanName", val);
                        }}
                        onBlur={() => markFieldTouched("insurancePlanName")}
                      />
                    </Grid>

                    {/* Row 6: 11d */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>11d. Is There Another Health Benefit Plan?</InputLabel>
                        <Select
                          value={cms.anotherBenefitPlan}
                          label="11d. Is There Another Health Benefit Plan?"
                          onChange={(e) => {
                            updateField("anotherBenefitPlan", e.target.value);
                            if (e.target.value === "YES") {
                              markFieldTouched("insurancePlanName");
                            }
                          }}
                        >
                          <MenuItem value="NO">No</MenuItem>
                          <MenuItem value="YES">Yes (Complete Items 9, 9a, 9d)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── 4. ITEMS 12 & 13: RESIDENT & INSURED SIGNATURES ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    4,
                    "Items 12 - 13: Resident & Insured Signatures / Authorizations",
                    "Consent declarations, release of information and authorization dates"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Item 12: Resident's or Authorized Person's Signature */}
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          backgroundColor: "background.default",
                          borderColor: "divider",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                              12. Resident's or Authorized Person's Signature
                            </Typography>
                            <Chip
                              label={cms.patientSignatureType === "DIGITAL" ? "Digital Signature" : "Upload Signature"}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: "0.72rem", height: 22 }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                            Authorizes release of medical information & payment of government benefits.
                          </Typography>

                          {/* Signature Mode Toggle: Digital vs Upload */}
                          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                            <Button
                              size="small"
                              variant={cms.patientSignatureType === "DIGITAL" ? "contained" : "outlined"}
                              startIcon={<DrawIcon />}
                              onClick={handlePatientDigitalSignatureClick}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.8rem",
                                backgroundColor: cms.patientSignatureType === "DIGITAL" ? "#0077B6" : "transparent",
                              }}
                            >
                              Digital Signature
                            </Button>
                            <Button
                              size="small"
                              variant={cms.patientSignatureType === "UPLOAD" ? "contained" : "outlined"}
                              startIcon={<CloudUploadIcon />}
                              onClick={() => updateField("patientSignatureType", "UPLOAD")}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.8rem",
                                backgroundColor: cms.patientSignatureType === "UPLOAD" ? "#0077B6" : "transparent",
                              }}
                            >
                              Upload Signature
                            </Button>
                          </Box>

                          {/* Mode 1: Digital Signature */}
                          {cms.patientSignatureType === "DIGITAL" && (
                            <Box sx={{ mb: 2 }}>
                              {cms.patientDigitalSignature ? (
                                <Box
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    border: "1px solid", borderColor: "divider",
                                    backgroundColor: "background.paper",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      border: "1px solid", borderColor: "divider",
                                      borderRadius: 1.5,
                                      backgroundColor: "background.default",
                                      width: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      minHeight: 70,
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={cms.patientDigitalSignature}
                                      alt="Resident Digital Signature"
                                      sx={{ maxHeight: 60, maxWidth: "100%", objectFit: "contain" }}
                                    />
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "center" }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<DrawIcon />}
                                      onClick={() => setSignatureModalTarget("patient")}
                                      sx={{ textTransform: "none", fontSize: "0.8rem" }}
                                    >
                                      Re-draw Signature
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="text"
                                      startIcon={<CloseIcon />}
                                      onClick={() => handleRemoveSignature("patient")}
                                      sx={{ textTransform: "none", fontSize: "0.8rem" }}
                                    >
                                      Remove
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  startIcon={<DrawIcon />}
                                  onClick={() => setSignatureModalTarget("patient")}
                                  sx={{
                                    borderStyle: "dashed",
                                    borderWidth: 1.5,
                                    py: 2,
                                    textTransform: "none",
                                    borderColor: "primary.main",
                                    color: "primary.main",
                                    backgroundColor: "background.paper",
                                    fontSize: "0.95rem",
                                    fontWeight: 600,
                                    "&:hover": { backgroundColor: "#F0F9FF", borderColor: "primary.dark" },
                                  }}
                                >
                                  Click to Draw Digital Signature
                                </Button>
                              )}
                            </Box>
                          )}

                          {/* Mode 2: Upload Signature File */}
                          {cms.patientSignatureType === "UPLOAD" && (
                            <Box sx={{ mb: 2 }}>
                              <Button
                                fullWidth
                                variant="outlined"
                                component="label"
                                startIcon={signatureUploading === "patient" ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                disabled={signatureUploading === "patient"}
                                sx={{
                                  borderStyle: "dashed",
                                  borderWidth: 1.5,
                                  py: 1.5,
                                  textTransform: "none",
                                  borderColor: "primary.main",
                                  color: "primary.main",
                                  backgroundColor: "background.paper",
                                }}
                              >
                                {signatureUploading === "patient" ? "Uploading Signature..." : "Choose Signature Image (PNG / JPG)"}
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={(e) => handleSignatureUpload("patientSignatureImage", e)}
                                />
                              </Button>

                              {cms.patientSignatureImage && (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    border: "1px solid", borderColor: "divider",
                                    backgroundColor: "background.paper",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={cms.patientSignatureImage}
                                    alt="Resident Signature"
                                    sx={{ maxHeight: 48, maxWidth: 180, objectFit: "contain" }}
                                  />
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<CloseIcon />}
                                    onClick={() => handleRemoveSignature("patient")}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>

                        {/* Date Signed with Calendar Picker */}
                        <Box sx={{ mt: 1, pt: 2, borderTop: "1px solid #E2E8F0" }}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="12. Date Signed"
                              format="dd/MM/yyyy"
                              disableFuture
                              minDate={new Date("1900-01-01")}
                              maxDate={new Date()}
                              shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                              value={parseStringToDate(cms.patientSignatureDate)}
                              onChange={(newDate: Date | null) =>
                                updateField("patientSignatureDate", formatDateToString(newDate))
                              }
                              slotProps={{
                                textField: {
                                  InputLabelProps: { shrink: true },
                                  fullWidth: true,
                                  size: "small",
                                  sx: { backgroundColor: "background.paper" },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Item 13: Insured's or Authorized Person's Signature */}
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          backgroundColor: "background.default",
                          borderColor: "divider",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                              13. Insured's or Authorized Person's Signature
                            </Typography>
                            <Chip
                              label={cms.insuredSignatureType === "DIGITAL" ? "Digital Signature" : "Upload Signature"}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: "0.72rem", height: 22 }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                            Authorizes payment of medical benefits to physician/supplier for services described below.
                          </Typography>

                          {/* Signature Mode Toggle: Digital vs Upload */}
                          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                            <Button
                              size="small"
                              variant={cms.insuredSignatureType === "DIGITAL" ? "contained" : "outlined"}
                              startIcon={<DrawIcon />}
                              onClick={handleInsuredDigitalSignatureClick}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.8rem",
                                backgroundColor: cms.insuredSignatureType === "DIGITAL" ? "#0077B6" : "transparent",
                              }}
                            >
                              Digital Signature
                            </Button>
                            <Button
                              size="small"
                              variant={cms.insuredSignatureType === "UPLOAD" ? "contained" : "outlined"}
                              startIcon={<CloudUploadIcon />}
                              onClick={() => updateField("insuredSignatureType", "UPLOAD")}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.8rem",
                                backgroundColor: cms.insuredSignatureType === "UPLOAD" ? "#0077B6" : "transparent",
                              }}
                            >
                              Upload Signature
                            </Button>
                          </Box>

                          {/* Mode 1: Digital Signature */}
                          {cms.insuredSignatureType === "DIGITAL" && (
                            <Box sx={{ mb: 2 }}>
                              {cms.insuredDigitalSignature ? (
                                <Box
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    border: "1px solid", borderColor: "divider",
                                    backgroundColor: "background.paper",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      border: "1px solid", borderColor: "divider",
                                      borderRadius: 1.5,
                                      backgroundColor: "background.default",
                                      width: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      minHeight: 70,
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={cms.insuredDigitalSignature}
                                      alt="Insured Digital Signature"
                                      sx={{ maxHeight: 60, maxWidth: "100%", objectFit: "contain" }}
                                    />
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "center" }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<DrawIcon />}
                                      onClick={() => setSignatureModalTarget("insured")}
                                      sx={{ textTransform: "none", fontSize: "0.8rem" }}
                                    >
                                      Re-draw Signature
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="text"
                                      startIcon={<CloseIcon />}
                                      onClick={() => handleRemoveSignature("insured")}
                                      sx={{ textTransform: "none", fontSize: "0.8rem" }}
                                    >
                                      Remove
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  startIcon={<DrawIcon />}
                                  onClick={() => setSignatureModalTarget("insured")}
                                  sx={{
                                    borderStyle: "dashed",
                                    borderWidth: 1.5,
                                    py: 2,
                                    textTransform: "none",
                                    borderColor: "primary.main",
                                    color: "primary.main",
                                    backgroundColor: "background.paper",
                                    fontSize: "0.95rem",
                                    fontWeight: 600,
                                    "&:hover": { backgroundColor: "#F0F9FF", borderColor: "primary.dark" },
                                  }}
                                >
                                  Click to Draw Digital Signature
                                </Button>
                              )}
                            </Box>
                          )}

                          {/* Mode 2: Upload Signature File */}
                          {cms.insuredSignatureType === "UPLOAD" && (
                            <Box sx={{ mb: 2 }}>
                              <Button
                                fullWidth
                                variant="outlined"
                                component="label"
                                startIcon={signatureUploading === "insured" ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                disabled={signatureUploading === "insured"}
                                sx={{
                                  borderStyle: "dashed",
                                  borderWidth: 1.5,
                                  py: 1.5,
                                  textTransform: "none",
                                  borderColor: "primary.main",
                                  color: "primary.main",
                                  backgroundColor: "background.paper",
                                }}
                              >
                                {signatureUploading === "insured" ? "Uploading Signature..." : "Choose Signature Image (PNG / JPG)"}
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={(e) => handleSignatureUpload("insuredSignatureImage", e)}
                                />
                              </Button>

                              {cms.insuredSignatureImage && (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    border: "1px solid", borderColor: "divider",
                                    backgroundColor: "background.paper",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={cms.insuredSignatureImage}
                                    alt="Insured Signature"
                                    sx={{ maxHeight: 48, maxWidth: 180, objectFit: "contain" }}
                                  />
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<CloseIcon />}
                                    onClick={() => handleRemoveSignature("insured")}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>

                        {/* Date Signed with Calendar Picker */}
                        <Box sx={{ mt: 1, pt: 2, borderTop: "1px solid #E2E8F0" }}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="13. Date Signed"
                              format="dd/MM/yyyy"
                              disableFuture
                              minDate={new Date("1900-01-01")}
                              maxDate={new Date()}
                              shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                              value={parseStringToDate(cms.insuredSignatureDate)}
                              onChange={(newDate: Date | null) =>
                                updateField("insuredSignatureDate", formatDateToString(newDate))
                              }
                              slotProps={{
                                textField: {
                                  InputLabelProps: { shrink: true },
                                  fullWidth: true,
                                  size: "small",
                                  sx: { backgroundColor: "background.paper" },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── 5. ITEMS 14 - 20: CLINICAL DETAILS & OTHER CLAIM INFO ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    5,
                    "Items 14 - 20: Clinical Details & Other Claim Information",
                    "Dates of illness, disability, referring physician and lab details"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Row 1: 14, 14 Qual, 15 */}
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="14. Date of Current Illness / Injury / LMP"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(cms.dateCurrentIllness)}
                        onChange={(newDate: Date | null) =>
                          updateField("dateCurrentIllness", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            sx: { backgroundColor: "background.paper" },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-date-current-illness-qual"
                        InputProps={{ id: "cms-date-current-illness-qual" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="14. Illness Date Qual (e.g. 431, 484)"
                        value={cms.dateCurrentIllnessQual}
                        error={Boolean(dateCurrentIllnessQualError)}
                        helperText={dateCurrentIllnessQualError || undefined}
                        inputProps={{ maxLength: 3 }}
                        onChange={(e) => {
                          markFieldTouched("dateCurrentIllnessQual");
                          const val = e.target.value.replace(/\D/g, "").slice(0, 3);
                          updateField("dateCurrentIllnessQual", val);
                        }}
                        onBlur={() => markFieldTouched("dateCurrentIllnessQual")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="15. Other Date"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(cms.otherDate)}
                        onChange={(newDate: Date | null) =>
                          updateField("otherDate", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            sx: { backgroundColor: "background.paper" },
                          },
                        }}
                      />
                    </Grid>

                    {/* Row 2: 15 Qual, 16 From, 16 To */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-other-date-qual"
                        InputProps={{ id: "cms-other-date-qual" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="15. Other Date Qual (e.g. 454 Initial, 455 X-ray)"
                        value={cms.otherDateQual}
                        error={Boolean(otherDateQualError)}
                        helperText={otherDateQualError || undefined}
                        inputProps={{ maxLength: 3 }}
                        onChange={(e) => {
                          markFieldTouched("otherDateQual");
                          const val = e.target.value.replace(/\D/g, "").slice(0, 3);
                          updateField("otherDateQual", val);
                        }}
                        onBlur={() => markFieldTouched("otherDateQual")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="16. Dates Unable to Work: From"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(cms.unableWorkFrom)}
                        onChange={(newDate: Date | null) =>
                          updateField("unableWorkFrom", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            sx: { backgroundColor: "background.paper" },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="16. Dates Unable to Work: To"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(cms.unableWorkTo)}
                        onChange={(newDate: Date | null) =>
                          updateField("unableWorkTo", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            sx: { backgroundColor: "background.paper" },
                          },
                        }}
                      />
                    </Grid>

                    {/* Row 3: 17, 17 Qual, 17a */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-referring-provider-name"
                        InputProps={{ id: "cms-referring-provider-name" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="17. Name of Referring Provider or Other Source"
                        value={cms.referringProviderName}
                        error={Boolean(referringProviderNameError)}
                        helperText={referringProviderNameError || undefined}
                        inputProps={{ maxLength: 60 }}
                        onChange={(e) => {
                          markFieldTouched("referringProviderName");
                          const val = e.target.value.replace(/[^A-Za-z\s.'-]/g, "").slice(0, 60);
                          updateField("referringProviderName", val);
                        }}
                        onBlur={() => markFieldTouched("referringProviderName")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>17. Provider Qualifier</InputLabel>
                        <Select
                          value={cms.referringProviderQual}
                          label="17. Provider Qualifier"
                          onChange={(e) => updateField("referringProviderQual", e.target.value)}
                        >
                          <MenuItem value=""><em>Select Qual</em></MenuItem>
                          <MenuItem value="DN">DN - Referring Provider</MenuItem>
                          <MenuItem value="DK">DK - Ordering Provider</MenuItem>
                          <MenuItem value="DQ">DQ - Supervising Provider</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-referring-provider-other-id"
                        InputProps={{ id: "cms-referring-provider-other-id" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="17a. Referring Provider Other ID"
                        value={cms.referringProviderOtherId}
                        error={Boolean(referringProviderOtherIdError)}
                        helperText={referringProviderOtherIdError || undefined}
                        inputProps={{ maxLength: 17 }}
                        onChange={(e) => {
                          markFieldTouched("referringProviderOtherId");
                          const val = e.target.value.replace(/[^A-Za-z0-9\-_ ]/g, "").slice(0, 17);
                          updateField("referringProviderOtherId", val);
                        }}
                        onBlur={() => markFieldTouched("referringProviderOtherId")}
                      />
                    </Grid>

                    {/* Row 4: 17b, 18 From, 18 To */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-referring-provider-npi"
                        InputProps={{ id: "cms-referring-provider-npi" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="17b. Referring Provider NPI"
                        value={cms.referringProviderNpi}
                        error={Boolean(referringProviderNpiError)}
                        helperText={referringProviderNpiError || undefined}
                        inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => {
                          markFieldTouched("referringProviderNpi");
                          const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                          updateField("referringProviderNpi", val);
                        }}
                        onBlur={() => markFieldTouched("referringProviderNpi")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="18. Hospitalization Dates: From"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(cms.hospitalizationFrom)}
                        onChange={(newDate: Date | null) =>
                          updateField("hospitalizationFrom", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            sx: { backgroundColor: "background.paper" },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="18. Hospitalization Dates: To"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(cms.hospitalizationTo)}
                        onChange={(newDate: Date | null) =>
                          updateField("hospitalizationTo", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            sx: { backgroundColor: "background.paper" },
                          },
                        }}
                      />
                    </Grid>

                    {/* Row 5: 19, 20 Outside Lab, 20 Charges */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-additional-claim-info"
                        InputProps={{ id: "cms-additional-claim-info" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="19. Additional Claim Information (NUCC)"
                        value={cms.additionalClaimInfo}
                        error={Boolean(additionalClaimInfoError)}
                        helperText={additionalClaimInfoError || undefined}
                        inputProps={{ maxLength: 71 }}
                        onChange={(e) => {
                          markFieldTouched("additionalClaimInfo");
                          const val = e.target.value.slice(0, 71);
                          updateField("additionalClaimInfo", val);
                        }}
                        onBlur={() => markFieldTouched("additionalClaimInfo")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>20. Outside Lab?</InputLabel>
                        <Select
                          value={cms.outsideLab}
                          label="20. Outside Lab?"
                          onChange={(e) => updateField("outsideLab", e.target.value)}
                        >
                          <MenuItem value="NO">No</MenuItem>
                          <MenuItem value="YES">Yes</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-outside-lab-charges"
                        InputProps={{ id: "cms-outside-lab-charges" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="20. Outside Lab Charges ($)"
                        value={cms.outsideLabCharges}
                        error={Boolean(outsideLabChargesError)}
                        helperText={outsideLabChargesError || undefined}
                        inputProps={{ inputMode: "decimal" }}
                        onChange={(e) => {
                          markFieldTouched("outsideLabCharges");
                          let val = e.target.value.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }
                          if (parts.length === 2 && parts[1].length > 2) {
                            val = parts[0] + "." + parts[1].slice(0, 2);
                          }
                          updateField("outsideLabCharges", val);
                        }}
                        onBlur={() => markFieldTouched("outsideLabCharges")}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── 6. ITEMS 21 - 23: ICD-10 DIAGNOSES & AUTHORIZATIONS ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    6,
                    "Items 21 - 23: ICD-10 Diagnosis Codes & Authorizations",
                    "Diagnosis codes and prior authorization numbers"
                  )}

                  {/* Item 21: Diagnoses A through L */}
                  <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                        21. Diagnosis or Nature of Illness or Injury (Relate A - L to Service Lines in 24E)
                      </Typography>
                      <Chip label="ICD Ind: 0 (ICD-10)" size="small" variant="outlined" sx={{ height: 22, fontWeight: 600 }} />
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-a"
                          InputProps={{ id: "cms-diag-a" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          required
                          error={Boolean(diagAError)}
                          helperText={diagAError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          label="A. Primary ICD-10"
                          value={cms.diagA}
                          onChange={(e) => {
                            markFieldTouched("diagA");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagA", val);
                          }}
                          onBlur={() => markFieldTouched("diagA")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-b"
                          InputProps={{ id: "cms-diag-b" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="B. ICD-10 Code"
                          value={cms.diagB}
                          error={Boolean(diagBError)}
                          helperText={diagBError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagB");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagB", val);
                          }}
                          onBlur={() => markFieldTouched("diagB")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-c"
                          InputProps={{ id: "cms-diag-c" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="C. ICD-10 Code"
                          value={cms.diagC}
                          error={Boolean(diagCError)}
                          helperText={diagCError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagC");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagC", val);
                          }}
                          onBlur={() => markFieldTouched("diagC")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-d"
                          InputProps={{ id: "cms-diag-d" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="D. ICD-10 Code"
                          value={cms.diagD}
                          error={Boolean(diagDError)}
                          helperText={diagDError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagD");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagD", val);
                          }}
                          onBlur={() => markFieldTouched("diagD")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-e"
                          InputProps={{ id: "cms-diag-e" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="E. ICD-10 Code"
                          value={cms.diagE}
                          error={Boolean(diagEError)}
                          helperText={diagEError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagE");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagE", val);
                          }}
                          onBlur={() => markFieldTouched("diagE")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-f"
                          InputProps={{ id: "cms-diag-f" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="F. ICD-10 Code"
                          value={cms.diagF}
                          error={Boolean(diagFError)}
                          helperText={diagFError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagF");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagF", val);
                          }}
                          onBlur={() => markFieldTouched("diagF")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-g"
                          InputProps={{ id: "cms-diag-g" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="G. ICD-10 Code"
                          value={cms.diagG}
                          error={Boolean(diagGError)}
                          helperText={diagGError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagG");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagG", val);
                          }}
                          onBlur={() => markFieldTouched("diagG")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-h"
                          InputProps={{ id: "cms-diag-h" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="H. ICD-10 Code"
                          value={cms.diagH}
                          error={Boolean(diagHError)}
                          helperText={diagHError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagH");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagH", val);
                          }}
                          onBlur={() => markFieldTouched("diagH")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-i"
                          InputProps={{ id: "cms-diag-i" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="I. ICD-10 Code"
                          value={cms.diagI}
                          error={Boolean(diagIError)}
                          helperText={diagIError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagI");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagI", val);
                          }}
                          onBlur={() => markFieldTouched("diagI")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-j"
                          InputProps={{ id: "cms-diag-j" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="J. ICD-10 Code"
                          value={cms.diagJ}
                          error={Boolean(diagJError)}
                          helperText={diagJError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagJ");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagJ", val);
                          }}
                          onBlur={() => markFieldTouched("diagJ")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-k"
                          InputProps={{ id: "cms-diag-k" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="K. ICD-10 Code"
                          value={cms.diagK}
                          error={Boolean(diagKError)}
                          helperText={diagKError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagK");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagK", val);
                          }}
                          onBlur={() => markFieldTouched("diagK")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="cms-diag-l"
                          InputProps={{ id: "cms-diag-l" }}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          label="L. ICD-10 Code"
                          value={cms.diagL}
                          error={Boolean(diagLError)}
                          helperText={diagLError || undefined}
                          inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                          onChange={(e) => {
                            markFieldTouched("diagL");
                            const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                            updateField("diagL", val);
                          }}
                          onBlur={() => markFieldTouched("diagL")}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Items 22 & 23 */}
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="22. Resubmission Code"
                        value={cms.resubmissionCode}
                        onChange={(e) => updateField("resubmissionCode", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="22. Original Reference Number"
                        value={cms.originalRefNo}
                        onChange={(e) => updateField("originalRefNo", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="23. Prior Authorization Number"
                        value={cms.priorAuthNumber}
                        inputProps={{ maxLength: 30 }}
                        onKeyDown={(e) => {
                          if (e.key === " ") e.preventDefault();
                          if (e.ctrlKey || e.metaKey || e.altKey) return;
                          if (
                            e.key === "Backspace" ||
                            e.key === "Delete" ||
                            e.key === "Tab" ||
                            e.key === "Enter" ||
                            e.key === "Escape" ||
                            e.key.startsWith("Arrow") ||
                            e.key === "Home" ||
                            e.key === "End"
                          ) return;
                          if (!/^[A-Za-z0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e) => updateField("priorAuthNumber", e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 30))}
                        onBlur={() => updateField("priorAuthNumber", (cms.priorAuthNumber || "").trim())}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── 7. ITEM 24: PROFESSIONAL SERVICE LINES (24A - 24J) ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    7,
                    "Item 24: Professional Lines of Service (24A - 24J)",
                    "Dates of service, procedure codes, modifiers, diagnosis pointers and charges"
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Item 24: Professional Service Lines (24A - 24J)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Specify Dates of Service, Place of Service, EMG, CPT/HCPCS, Modifiers, Diagnosis Pointer, Charges, Units & Rendering NPI
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddServiceLine}
                      sx={{ textTransform: "none", color: "primary.main", borderColor: "primary.main" }}
                    >
                      Add Service Line
                    </Button>
                  </Box>

                  <TableContainer sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "background.default" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, py: 1.5 }}>
                            24A. Dates of Service (From - To) <span style={{ color: "#ef4444" }}>*</span>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 80 }}>24B. POS</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 70 }}>24C. EMG</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 130 }}>
                            24D. CPT/HCPCS <span style={{ color: "#ef4444" }}>*</span>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 75 }}>Mod 1</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 75 }}>Mod 2</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 85 }}>24E. Diag</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 120 }}>
                            24F. Charges ($) <span style={{ color: "#ef4444" }}>*</span>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 70 }}>24G. Units</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 145 }}>24J. Rendering NPI</TableCell>
                          <TableCell sx={{ width: 50 }} align="center"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {serviceLines.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <DatePicker
                                  format="dd/MM/yyyy"
                                  disableFuture
                                  minDate={new Date("1900-01-01")}
                                  maxDate={new Date()}
                                  shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                                  value={parseStringToDate(row.dateFrom)}
                                  onChange={(newDate: Date | null) =>
                                    handleServiceLineChange(row.id, "dateFrom", formatDateToString(newDate))
                                  }
                                  slotProps={{
                                    textField: {
                                      id: `cms-service-from-${row.id}`,
                                      InputLabelProps: { shrink: true },
                                      size: "small",
                                      required: true,
                                      error: Boolean(
                                        (formTouched && !row.dateFrom) ||
                                        (row.dateFrom && row.dateTo && parseStringToDate(row.dateFrom) && parseStringToDate(row.dateTo) && parseStringToDate(row.dateTo)!.getTime() < parseStringToDate(row.dateFrom)!.getTime())
                                      ),
                                      helperText:
                                        formTouched && !row.dateFrom
                                          ? "Required"
                                          : row.dateFrom && row.dateTo && parseStringToDate(row.dateFrom) && parseStringToDate(row.dateTo) && parseStringToDate(row.dateTo)!.getTime() < parseStringToDate(row.dateFrom)!.getTime()
                                            ? "Before To"
                                            : undefined,
                                      sx: { width: 145, backgroundColor: "background.paper" },
                                    },
                                  }}
                                />
                                <DatePicker
                                  format="dd/MM/yyyy"
                                  disableFuture
                                  minDate={parseStringToDate(row.dateFrom) || new Date("1900-01-01")}
                                  maxDate={new Date()}
                                  shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                                  value={parseStringToDate(row.dateTo)}
                                  onChange={(newDate: Date | null) =>
                                    handleServiceLineChange(row.id, "dateTo", formatDateToString(newDate))
                                  }
                                  slotProps={{
                                    textField: {
                                      id: `cms-service-to-${row.id}`,
                                      InputLabelProps: { shrink: true },
                                      size: "small",
                                      required: true,
                                      error: Boolean(
                                        (formTouched && !row.dateTo) ||
                                        (row.dateFrom && row.dateTo && parseStringToDate(row.dateFrom) && parseStringToDate(row.dateTo) && parseStringToDate(row.dateTo)!.getTime() < parseStringToDate(row.dateFrom)!.getTime())
                                      ),
                                      helperText:
                                        formTouched && !row.dateTo
                                          ? "Required"
                                          : row.dateFrom && row.dateTo && parseStringToDate(row.dateFrom) && parseStringToDate(row.dateTo) && parseStringToDate(row.dateTo)!.getTime() < parseStringToDate(row.dateFrom)!.getTime()
                                            ? "On/After From"
                                            : undefined,
                                      sx: { width: 145, backgroundColor: "background.paper" },
                                    },
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                value={row.pos}
                                onChange={(e) => handleServiceLineChange(row.id, "pos", e.target.value)}
                                sx={{ width: 60 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                value={row.emg}
                                onChange={(e) => handleServiceLineChange(row.id, "emg", e.target.value)}
                                sx={{ width: 55 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                id={`cms-service-cpt-${row.id}`}
                                InputProps={{ id: `cms-service-cpt-${row.id}` }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                required
                                error={formTouched && !row.cpt?.trim()}
                                helperText={formTouched && !row.cpt?.trim() ? "Required" : undefined}
                                value={row.cpt}
                                onChange={(e) => handleServiceLineChange(row.id, "cpt", e.target.value)}
                                sx={{ width: 110 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                value={row.mod1}
                                onChange={(e) => handleServiceLineChange(row.id, "mod1", e.target.value)}
                                sx={{ width: 60 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                value={row.mod2}
                                onChange={(e) => handleServiceLineChange(row.id, "mod2", e.target.value)}
                                sx={{ width: 60 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                value={row.diagPointer}
                                onChange={(e) => handleServiceLineChange(row.id, "diagPointer", e.target.value)}
                                sx={{ width: 60 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                id={`cms-service-charges-${row.id}`}
                                InputProps={{ id: `cms-service-charges-${row.id}` }}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                type="number"
                                required
                                error={formTouched && !(Number(row.charges) > 0)}
                                helperText={formTouched && !(Number(row.charges) > 0) ? "Required" : undefined}
                                value={row.charges}
                                onChange={(e) => handleServiceLineChange(row.id, "charges", e.target.value)}
                                sx={{ width: 105 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                type="number"
                                value={row.daysUnits}
                                onChange={(e) => handleServiceLineChange(row.id, "daysUnits", e.target.value)}
                                sx={{ width: 55 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                value={row.renderingNpi}
                                inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                                onChange={(e) => handleServiceLineChange(row.id, "renderingNpi", e.target.value.replace(/\D/g, "").slice(0, 10))}
                                sx={{ width: 125 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveServiceLine(row.id)}
                                disabled={serviceLines.length === 1}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                {/* ── 8. ITEMS 25 - 30: FEDERAL TAX ID, FINANCIAL TOTALS & NUCC ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    8,
                    "Items 25 - 30: Federal Tax ID, Claim Totals & NUCC Details",
                    "Federal identification, total charges and assignment of benefits"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Row 1: 25 Tax ID, 25 Tax Type, 26 Account No */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="25. Federal Tax I.D. Number"
                        value={cms.taxId}
                        onChange={(e) => updateField("taxId", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>25. Tax ID Type</InputLabel>
                        <Select
                          value={cms.taxIdType}
                          label="25. Tax ID Type"
                          onChange={(e) => updateField("taxIdType", e.target.value)}
                        >
                          <MenuItem value="EIN">EIN (Employer Identification Number)</MenuItem>
                          <MenuItem value="SSN">SSN (Social Security Number)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="26. Resident's Account No."
                        value={cms.patientAccountNo}
                        onChange={(e) => updateField("patientAccountNo", e.target.value)}
                      />
                    </Grid>

                    {/* Row 2: 27 Assignment, 28 Total Charge, 29 Amount Paid */}
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>27. Accept Assignment?</InputLabel>
                        <Select
                          value={cms.acceptAssignment}
                          label="27. Accept Assignment?"
                          onChange={(e) => updateField("acceptAssignment", e.target.value)}
                        >
                          <MenuItem value="YES">Yes</MenuItem>
                          <MenuItem value="NO">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card elevation={0} sx={{ p: 2, backgroundColor: "background.default", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                          28. TOTAL CHARGE ($)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", mt: 0.5 }}>
                          ${Number(cms.totalCharge).toFixed(2)}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        type="number"
                        label="29. Amount Paid ($)"
                        value={cms.amountPaid}
                        onChange={(e) => updateField("amountPaid", parseFloat(e.target.value) || 0)}
                      />
                    </Grid>

                    {/* Row 3: 30 Reserved for NUCC & 30 Balance Due */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="30. Rsvd for NUCC Use"
                        value={cms.reservedNuccBox30}
                        onChange={(e) => updateField("reservedNuccBox30", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card elevation={0} sx={{ p: 2, backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 700 }}>
                          30. BALANCE DUE ($)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "success.dark", mt: 0.5 }}>
                          ${Number(cms.balanceDue).toFixed(2)}
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── 9. ITEMS 31 - 33: PHYSICIAN SIGNATURE, SERVICE FACILITY & BILLING PROVIDER ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    9,
                    "Items 31 - 33: Physician Signature, Service Facility & Billing Provider",
                    "Physician signature date, facility location and billing provider details"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Item 31: Physician Signature & Date */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="31. Physician / Supplier Signature (Credentials)"
                        value={cms.physicianSignature}
                        onChange={(e) => updateField("physicianSignature", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="31. Date Signed"
                          format="dd/MM/yyyy"
                          disableFuture
                          minDate={new Date("1900-01-01")}
                          maxDate={new Date()}
                          shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                          value={parseStringToDate(cms.physicianSignatureDate)}
                          onChange={(newDate: Date | null) =>
                            updateField("physicianSignatureDate", formatDateToString(newDate))
                          }
                          slotProps={{
                            textField: {
                              InputLabelProps: { shrink: true },
                              fullWidth: true,
                              size: "small",
                              sx: { backgroundColor: "background.paper" },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 1.5, backgroundColor: "background.default", borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
                        <Typography variant="caption" color="text.secondary">
                          31. I certify that the statements on the reverse apply to this bill and are made a part thereof.
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Item 32: Service Facility Location Information */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary", mt: 1, mb: 1 }}>
                        32. Service Facility Location Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="32. Service Facility Name"
                        value={cms.facilityName}
                        onChange={(e) => updateField("facilityName", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="32. Service Facility Street Address"
                        value={cms.facilityAddress}
                        onChange={(e) => updateField("facilityAddress", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="32. Service Facility City"
                        value={cms.facilityCity}
                        onChange={(e) => updateField("facilityCity", e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="32. Service Facility State"
                        value={cms.facilityState}
                        onChange={(e) => updateField("facilityState", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="32. Service Facility ZIP Code"
                        value={cms.facilityZip}
                        onChange={(e) => updateField("facilityZip", e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="32a. Service Facility NPI"
                        value={cms.facilityNpi}
                        inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => updateField("facilityNpi", e.target.value.replace(/\D/g, "").slice(0, 10))}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="32b. Service Facility Other ID"
                        value={cms.facilityOtherId}
                        onChange={(e) => updateField("facilityOtherId", e.target.value)}
                      />
                    </Grid>

                    {/* Item 33: Billing Provider Info & Phone Number */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary", mt: 1, mb: 1 }}>
                        33. Billing Provider Information & Phone Number
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="33. Billing Provider Name / Organization"
                        value={cms.billingProviderName}
                        onChange={(e) => updateField("billingProviderName", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="33. Billing Provider Street Address"
                        value={cms.billingProviderAddress}
                        onChange={(e) => updateField("billingProviderAddress", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="33. Billing Provider City"
                        value={cms.billingProviderCity}
                        onChange={(e) => updateField("billingProviderCity", e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="33. Billing Provider State"
                        value={cms.billingProviderState}
                        onChange={(e) => updateField("billingProviderState", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="33. Billing Provider ZIP Code"
                        value={cms.billingProviderZip}
                        onChange={(e) => updateField("billingProviderZip", e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="33. Billing Provider Phone (Include Area Code)"
                        value={cms.billingProviderPhone}
                        onChange={(e) => updateField("billingProviderPhone", e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="cms-billing-provider-npi"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="33a. Billing Provider NPI"
                        value={cms.billingProviderNpi}
                        error={Boolean(billingProviderNpiError)}
                        helperText={billingProviderNpiError || undefined}
                        inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => {
                          markFieldTouched("billingProviderNpi");
                          const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                          updateField("billingProviderNpi", val);
                        }}
                        onBlur={() => markFieldTouched("billingProviderNpi")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="33b. Billing Provider Other ID"
                        value={cms.billingProviderOtherId}
                        onChange={(e) => updateField("billingProviderOtherId", e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Paper>


                {/* Bottom Actions */}
                <Box sx={{ display: isReadOnly ? "none" : "flex", justifyContent: "flex-end", gap: 2, "@media print": { display: "none" } }}>
                  <Button variant="outlined" color="inherit" startIcon={<ResetIcon />} onClick={handleResetForm} sx={{ borderRadius: "12px", px: 2.5 }}>
                    Reset Form
                  </Button>
                  <Button variant="outlined" startIcon={<SaveIcon />} disabled={saving} onClick={handleSaveDraft} sx={{ borderRadius: "12px", px: 3 }}>
                    Save Draft
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    disabled={saving}
                    onClick={handleSubmitClaim}
                    sx={{
                      borderRadius: "12px",
                      px: 3.5,
                      boxShadow: "none",
                      backgroundColor: "#48CAE4",
                      color: "#000000",
                      "&:hover": { backgroundColor: "#3ab3cc", boxShadow: "none" },
                    }}
                  >
                    Submit Claim
                  </Button>
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
          UB-04 (CMS-1450) FORM
          ═══════════════════════════════════════════════════════════════════════ */}
        {selectedForm === "UB_04" && (
          <Box sx={{ mt: 3 }}>
            {/* Top Info Banner */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 2.5,
                border: "1px solid #BAE6FD",
                backgroundColor: "#F0F9FF",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.dark" }}>
                  UB-04 (CMS-1450) Institutional Claim Form
                </Typography>
                <Chip label="Institutional Claim" size="small" sx={{ backgroundColor: "#E0F2FE", color: "primary.dark", fontWeight: 600 }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Standard Institutional Claim Form for Skilled Nursing Facilities & Inpatient Care
              </Typography>
            </Paper>

            {/* ── Conditional Display: Show UB-04 form fields only when resident is selected ── */}
            <Box sx={{ display: selectedResidentId ? "none" : "block" }}>
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  textAlign: "center",
                  borderRadius: 2.5,
                  border: "1px dashed #BAE6FD",
                  backgroundColor: "background.default",
                }}
              >
                <DescriptionIcon sx={{ fontSize: 48, color: "primary.main", mb: 1.5, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}>
                  Please Select a Resident to View & Fill Form
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a resident from the "Search Resident by Name" box above to display and populate the UB-04 claim fields.
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ display: selectedResidentId ? "block" : "none" }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                {/* ── SECTION 1: FACILITY & BILLING PROVIDER INFORMATION ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    1,
                    "1. Facility & Billing Provider Information",
                    "Provider identity, physical facility address, tax identification and bill classification"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Row 1: Facility Identifiers */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        id="ub04-provider-name"
                        InputProps={{ id: "ub04-provider-name" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        required
                        error={Boolean(ub04ProviderNameError)}
                        helperText={ub04ProviderNameError || undefined}
                        inputProps={{ maxLength: 100 }}
                        label="1. Provider / Facility Name"
                        value={ub04Form.providerName}
                        onChange={(e) => {
                          markFieldTouched("ub04ProviderName");
                          const val = e.target.value.replace(/[^A-Za-z0-9\s&'.-]/g, "").slice(0, 100);
                          updateUb04Field("providerName", val);
                        }}
                        onBlur={() => markFieldTouched("ub04ProviderName")}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        id="ub04-provider-npi"
                        InputProps={{ id: "ub04-provider-npi" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        required
                        error={Boolean(ub04ProviderNpiError)}
                        helperText={ub04ProviderNpiError || undefined}
                        inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                        label="56. Billing Provider / Facility NPI"
                        value={ub04Form.providerNpi}
                        onChange={(e) => {
                          markFieldTouched("ub04ProviderNpi");
                          const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                          updateUb04Field("providerNpi", val);
                        }}
                        onBlur={() => markFieldTouched("ub04ProviderNpi")}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        id="ub04-fed-tax-no"
                        InputProps={{ id: "ub04-fed-tax-no" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        required
                        error={Boolean(ub04FedTaxNoError)}
                        helperText={ub04FedTaxNoError || undefined}
                        inputProps={{ maxLength: 10, inputMode: "numeric" }}
                        label="5. Federal Tax No. (Fed. Tax ID)"
                        value={ub04Form.fedTaxNo}
                        onChange={(e) => {
                          markFieldTouched("ub04FedTaxNo");
                          updateUb04Field("fedTaxNo", formatFedTaxNo(e.target.value));
                        }}
                        onBlur={() => markFieldTouched("ub04FedTaxNo")}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                        }}
                      />
                    </Grid>

                    {/* Row 2: Physical Facility Address & Bill Classification */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="ub04-provider-address"
                        InputProps={{ id: "ub04-provider-address" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        required
                        error={Boolean(ub04ProviderAddressError)}
                        helperText={ub04ProviderAddressError || undefined}
                        inputProps={{ maxLength: 200 }}
                        label="1. Facility Street Address"
                        value={ub04Form.providerAddress}
                        onChange={(e) => {
                          markFieldTouched("ub04ProviderAddress");
                          const val = e.target.value.replace(/[^A-Za-z0-9\s#\-.,/']/g, "").slice(0, 200);
                          updateUb04Field("providerAddress", val);
                        }}
                        onBlur={() => markFieldTouched("ub04ProviderAddress")}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        id="ub04-provider-city"
                        InputProps={{ id: "ub04-provider-city" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        required
                        error={Boolean(ub04ProviderCityError)}
                        helperText={ub04ProviderCityError || undefined}
                        inputProps={{ maxLength: 100 }}
                        label="1. City"
                        value={ub04Form.providerCity}
                        onChange={(e) => {
                          markFieldTouched("ub04ProviderCity");
                          const val = e.target.value.replace(/[^A-Za-z\s.'-]/g, "").slice(0, 100);
                          updateUb04Field("providerCity", val);
                        }}
                        onBlur={() => markFieldTouched("ub04ProviderCity")}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "#d32f2f !important" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={1.5}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="1. State"
                        value={ub04Form.providerState}
                        onChange={(e) => updateUb04Field("providerState", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={1.5}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="1. ZIP Code"
                        value={ub04Form.providerZip}
                        onChange={(e) => updateUb04Field("providerZip", e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="4. Type of Bill (TOB)"
                        value={ub04Form.typeOfBill}
                        onChange={(e) => updateUb04Field("typeOfBill", e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── SECTION 2: RESIDENT IDENTIFICATION & DEMOGRAPHICS ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    2,
                    "2. Resident Identification & Demographics",
                    "Resident identifiers, statement covers period, personal demographics and home address"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Row 1: Resident Control & Statement Period */}
                    <Grid item xs={12} sm={3}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="3a. Resident Control Number"
                        value={ub04Form.patientControlNo}
                        onChange={(e) => updateUb04Field("patientControlNo", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="3b. Medical Record Number"
                        value={ub04Form.medicalRecordNo}
                        onChange={(e) => updateUb04Field("medicalRecordNo", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <DatePicker
                        label="6. Statement Covers - From"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(ub04Form.statementCoversFrom)}
                        onChange={(newDate: Date | null) =>
                          updateUb04Field("statementCoversFrom", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            required: true,
                            error: Boolean(
                              (formTouched && !ub04Form.statementCoversFrom) ||
                              (ub04Form.statementCoversFrom &&
                                ub04Form.statementCoversThrough &&
                                parseStringToDate(ub04Form.statementCoversThrough) &&
                                parseStringToDate(ub04Form.statementCoversFrom) &&
                                parseStringToDate(ub04Form.statementCoversThrough)!.getTime() <
                                parseStringToDate(ub04Form.statementCoversFrom)!.getTime())
                            ),
                            helperText:
                              formTouched && !ub04Form.statementCoversFrom
                                ? "Statement From is required"
                                : ub04Form.statementCoversFrom &&
                                  ub04Form.statementCoversThrough &&
                                  parseStringToDate(ub04Form.statementCoversThrough) &&
                                  parseStringToDate(ub04Form.statementCoversFrom) &&
                                  parseStringToDate(ub04Form.statementCoversThrough)!.getTime() <
                                  parseStringToDate(ub04Form.statementCoversFrom)!.getTime()
                                  ? "From date must be on or before Through date"
                                  : undefined,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <DatePicker
                        label="6. Statement Covers - Through"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={parseStringToDate(ub04Form.statementCoversFrom) || new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(ub04Form.statementCoversThrough)}
                        onChange={(newDate: Date | null) =>
                          updateUb04Field("statementCoversThrough", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            required: true,
                            error: Boolean(
                              (formTouched && !ub04Form.statementCoversThrough) ||
                              (ub04Form.statementCoversFrom &&
                                ub04Form.statementCoversThrough &&
                                parseStringToDate(ub04Form.statementCoversThrough) &&
                                parseStringToDate(ub04Form.statementCoversFrom) &&
                                parseStringToDate(ub04Form.statementCoversThrough)!.getTime() <
                                parseStringToDate(ub04Form.statementCoversFrom)!.getTime())
                            ),
                            helperText:
                              formTouched && !ub04Form.statementCoversThrough
                                ? "Statement Through is required"
                                : ub04Form.statementCoversFrom &&
                                  ub04Form.statementCoversThrough &&
                                  parseStringToDate(ub04Form.statementCoversThrough) &&
                                  parseStringToDate(ub04Form.statementCoversFrom) &&
                                  parseStringToDate(ub04Form.statementCoversThrough)!.getTime() <
                                  parseStringToDate(ub04Form.statementCoversFrom)!.getTime()
                                  ? "Through date must be on or after From date"
                                  : undefined,
                          },
                        }}
                      />
                    </Grid>

                    {/* Row 2: Resident Demographics */}
                    <Grid item xs={12} sm={5}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="8. Resident Name"
                        value={ub04Form.patientName}
                        onChange={(e) => updateUb04Field("patientName", e.target.value.replace(/[^A-Za-z\s,.'-]/g, ""))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3.5}>
                      <DatePicker
                        label="10. Resident's Birth Date"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(ub04Form.patientDob)}
                        onChange={(newDate: Date | null) => {
                          setPatientDobTouched(true);
                          if (newDate && !isNaN(newDate.getTime())) {
                            updateUb04Field("patientDob", formatDateToString(newDate));
                          } else {
                            updateUb04Field("patientDob", "");
                          }
                        }}
                        onError={(newError) => {
                          setDobPickerError(newError ? String(newError) : null);
                        }}
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                            required: true,
                            error: Boolean(patientDobError),
                            helperText: patientDobError || undefined,
                            onBlur: () => setPatientDobTouched(true),
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>11. Resident's Gender (Sex)</InputLabel>
                        <Select
                          value={parseGenderToSex(ub04Form.patientSex)}
                          label="11. Resident's Gender (Sex)"
                          onChange={(e) => updateUb04Field("patientSex", e.target.value)}
                        >
                          <MenuItem value="M">Male (M)</MenuItem>
                          <MenuItem value="F">Female (F)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Row 3: Physical Address */}
                    <Grid item xs={12} sm={5}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="9a. Resident Street Address"
                        value={ub04Form.patientAddress}
                        onChange={(e) => updateUb04Field("patientAddress", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="9b. City"
                        value={ub04Form.patientCity}
                        onChange={(e) => updateUb04Field("patientCity", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="9c. State"
                        value={ub04Form.patientState}
                        onChange={(e) => updateUb04Field("patientState", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="9d. ZIP Code"
                        value={ub04Form.patientZip}
                        onChange={(e) => updateUb04Field("patientZip", e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10))}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── SECTION 3: PAYER & INSURED INFORMATION ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    3,
                    "3. Payer & Insured Information",
                    "Payer identity, responsible party address, insured subscriber details and treatment authorization"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Row 1: Payer Identification */}
                    <Grid item xs={12} sm={6}>
                      {residentInsurances.length > 1 ? (
                        <FormControl
                          fullWidth
                          size="small"
                          required
                          error={formTouched && !ub04Form.payerName?.trim()}
                        >
                          <InputLabel shrink id="ub04-payer-name-label">
                            50. Payer Name
                          </InputLabel>
                          <Select
                            id="ub04-payer-name"
                            labelId="ub04-payer-name-label"
                            label="50. Payer Name"
                            notched
                            value={effectiveUb04SelectedInsuranceId}
                            onChange={(e) => handleInsuranceCardSelect(e.target.value)}
                          >
                            {residentInsurances.map((card, idx) => {
                              const valId = card.id || String(idx);
                              const name = card.insuranceCompanyName || card.provider || "Unnamed Payer";
                              const typeTag = card.coverageType || card.insuranceType;
                              const labelText = typeTag ? `${name} (${typeTag})` : name;

                              return (
                                <MenuItem key={valId} value={valId}>
                                  {labelText}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          {formTouched && !ub04Form.payerName?.trim() && (
                            <FormHelperText error>Payer Name is required</FormHelperText>
                          )}
                        </FormControl>
                      ) : (
                        <TextField
                          id="ub04-payer-name"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          required
                          error={formTouched && !ub04Form.payerName?.trim()}
                          helperText={formTouched && !ub04Form.payerName?.trim() ? "Payer Name is required" : undefined}
                          label="50. Payer Name"
                          value={ub04Form.payerName}
                          onChange={(e) => updateUb04Field("payerName", e.target.value)}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="51. Health Plan ID"
                        value={ub04Form.healthPlanId}
                        onChange={(e) => updateUb04Field("healthPlanId", e.target.value)}
                      />
                    </Grid>

                    {/* Row 2: Responsible Party Address */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="38. Responsible Party / Payer Address"
                        value={ub04Form.payerAddress}
                        onChange={(e) => updateUb04Field("payerAddress", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="38. Payer City, State, ZIP"
                        value={ub04Form.payerCityStateZip}
                        onChange={(e) => updateUb04Field("payerCityStateZip", e.target.value)}
                      />
                    </Grid>

                    {/* Row 3: Insured Subscriber */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="58. Insured's Name"
                        value={ub04Form.insuredName}
                        onChange={(e) => updateUb04Field("insuredName", e.target.value.replace(/[^A-Za-z\s,.'-]/g, ""))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="60. Insured's Unique ID"
                        inputProps={{ maxLength: 30 }}
                        value={ub04Form.insuredUniqueId}
                        onChange={(e) => {
                          const val = e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9-]/g, "")
                            .replace(/-{2,}/g, "-")
                            .replace(/^-+/, "")
                            .slice(0, 30);
                          updateUb04Field("insuredUniqueId", val);
                        }}
                      />
                    </Grid>

                    {/* Row 4: Group & Authorization */}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="61. Group Name"
                        value={ub04Form.insuredGroupName}
                        onChange={(e) => updateUb04Field("insuredGroupName", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="62. Insurance Group No."
                        value={ub04Form.insuredGroupNo}
                        onChange={(e) => updateUb04Field("insuredGroupNo", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="63. Treatment Authorization Codes"
                        value={ub04Form.treatmentAuthCode}
                        inputProps={{ maxLength: 30 }}
                        onKeyDown={(e) => {
                          if (e.key === " ") e.preventDefault();
                          if (e.ctrlKey || e.metaKey || e.altKey) return;
                          if (
                            e.key === "Backspace" ||
                            e.key === "Delete" ||
                            e.key === "Tab" ||
                            e.key === "Enter" ||
                            e.key === "Escape" ||
                            e.key.startsWith("Arrow") ||
                            e.key === "Home" ||
                            e.key === "End"
                          ) return;
                          if (!/^[A-Za-z0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e) => updateUb04Field("treatmentAuthCode", e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 30))}
                        onBlur={() => updateUb04Field("treatmentAuthCode", (ub04Form.treatmentAuthCode || "").trim())}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── SECTION 4: REVENUE CODES & CHARGES TABLE ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 2, mb: 2.5, borderBottom: "1px solid #F1F5F9", flexWrap: "wrap", gap: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          backgroundColor: "#E0F2FE",
                          color: "#0284C7",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          flexShrink: 0,
                        }}
                      >
                        4
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0F172A", lineHeight: 1.25 }}>
                          4. Revenue Codes & Institutional Service Lines
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748B", display: "block", mt: 0.25 }}>
                          Institutional services, accommodation codes, and billed charges
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddUb04RevenueLine}
                      sx={{ textTransform: "none", color: "primary.main", borderColor: "primary.main", borderRadius: "10px", px: 2, fontWeight: 600 }}
                    >
                      Add Revenue Line
                    </Button>
                  </Box>

                  <TableContainer sx={{ borderRadius: "12px", border: "1px solid #E2E8F0", mb: 2.5 }}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#F8FAFC" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, width: 110, color: "#1A2B45" }}>42. Rev. Cd.</TableCell>
                          <TableCell sx={{ fontWeight: 700, minWidth: 200, color: "#1A2B45" }}>43. Description</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 160, color: "#1A2B45" }}>
                            44. HCPCS / Rate / HIPPS <span style={{ color: "#ef4444" }}>*</span>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 155, color: "#1A2B45" }}>45. Serv. Date</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 90, color: "#1A2B45" }}>46. Serv. Units</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 130, color: "#1A2B45" }}>
                            47. Total Charges ($) <span style={{ color: "#ef4444" }}>*</span>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 130, color: "#1A2B45" }}>48. Non-Covered ($)</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 50, color: "#1A2B45" }} align="center">Act</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ub04RevenueLines.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                                value={row.revCode}
                                onChange={(e) =>
                                  setUb04RevenueLines((prev) =>
                                    prev.map((l) => (l.id === row.id ? { ...l, revCode: e.target.value } : l))
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                                value={row.description}
                                onChange={(e) =>
                                  setUb04RevenueLines((prev) =>
                                    prev.map((l) => (l.id === row.id ? { ...l, description: e.target.value } : l))
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                                required
                                error={formTouched && !row.hcpcsRate?.trim()}
                                value={row.hcpcsRate}
                                onChange={(e) =>
                                  setUb04RevenueLines((prev) =>
                                    prev.map((l) => (l.id === row.id ? { ...l, hcpcsRate: e.target.value } : l))
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <DatePicker
                                format="dd/MM/yyyy"
                                disableFuture
                                minDate={new Date("1900-01-01")}
                                maxDate={new Date()}
                                shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                                value={parseStringToDate(row.serviceDate)}
                                onChange={(newDate: Date | null) =>
                                  setUb04RevenueLines((prev) =>
                                    prev.map((l) =>
                                      l.id === row.id ? { ...l, serviceDate: formatDateToString(newDate) } : l
                                    )
                                  )
                                }
                                slotProps={{
                                  textField: {
                                    InputLabelProps: { shrink: true },
                                    fullWidth: true,
                                    size: "small",
                                    sx: { backgroundColor: "background.paper" },
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                                type="number"
                                value={row.units}
                                onChange={(e) =>
                                  setUb04RevenueLines((prev) =>
                                    prev.map((l) => (l.id === row.id ? { ...l, units: e.target.value } : l))
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                                type="number"
                                required
                                error={formTouched && !(Number(row.totalCharges) > 0)}
                                value={row.totalCharges}
                                onChange={(e) =>
                                  setUb04RevenueLines((prev) =>
                                    prev.map((l) => (l.id === row.id ? { ...l, totalCharges: e.target.value } : l))
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                                type="number"
                                value={row.nonCoveredCharges || ""}
                                onChange={(e) =>
                                  setUb04RevenueLines((prev) =>
                                    prev.map((l) => (l.id === row.id ? { ...l, nonCoveredCharges: e.target.value } : l))
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveUb04RevenueLine(row.id)}
                                disabled={ub04RevenueLines.length === 1}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Summary: Creation Date & Totals */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 2,
                      p: 2.5,
                      borderRadius: "12px",
                      backgroundColor: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <Box sx={{ width: { xs: "100%", sm: 260 } }}>
                      <DatePicker
                        label="Creation Date"
                        format="dd/MM/yyyy"
                        disableFuture
                        minDate={new Date("1900-01-01")}
                        maxDate={new Date()}
                        shouldDisableDate={(date: Date) => date.getTime() > new Date().setHours(23, 59, 59, 999)}
                        value={parseStringToDate(ub04Form.creationDate)}
                        onChange={(newDate: Date | null) =>
                          updateUb04Field("creationDate", formatDateToString(newDate))
                        }
                        slotProps={{
                          textField: {
                            InputLabelProps: { shrink: true },
                            fullWidth: true,
                            size: "small",
                          },
                        }}
                      />
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: "#1A2B45" }}>
                        TOTALS:
                      </Typography>
                      <Chip
                        label={`$${(Number(ub04Form.totalCharges) || 0).toFixed(2)}`}
                        sx={{
                          fontWeight: 800,
                          fontSize: "1.1rem",
                          py: 2.2,
                          px: 1.5,
                          backgroundColor: "primary.main",
                          color: "common.white",
                          borderRadius: "10px",
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>

                {/* ── SECTION 5: CLINICAL DIAGNOSES & ATTENDING / OPERATING PROVIDERS ── */}
                <Paper elevation={0} sx={sectionCardSx}>
                  {renderSectionHeader(
                    5,
                    "5. Clinical Diagnoses & Attending / Operating Providers",
                    "Diagnosis classification, principal diagnosis codes, attending physician and operating provider details"
                  )}
                  <Grid container spacing={2.5}>
                    {/* Row 1: Clinical Diagnoses */}
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>66. ICD Indicator</InputLabel>
                        <Select
                          value={ub04Form.icdIndicator}
                          label="66. ICD Indicator"
                          onChange={(e) => updateUb04Field("icdIndicator", e.target.value)}
                        >
                          <MenuItem value="0">0 - ICD-10-CM</MenuItem>
                          <MenuItem value="9">9 - ICD-9-CM</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        id="ub04-principal-diag"
                        InputProps={{ id: "ub04-principal-diag" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        required
                        error={Boolean(ub04PrincipalDiagError)}
                        helperText={ub04PrincipalDiagError || undefined}
                        inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                        label="67. Principal Diagnosis (DX)"
                        value={ub04Form.principalDiagnosis}
                        onChange={(e) => {
                          markFieldTouched("principalDiagnosis");
                          const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                          updateUb04Field("principalDiagnosis", val);
                        }}
                        onBlur={() => markFieldTouched("principalDiagnosis")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        id="ub04-other-diag-1"
                        InputProps={{ id: "ub04-other-diag-1" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="67A. Other Diagnosis Code"
                        value={ub04Form.otherDiagnosis1}
                        error={Boolean(ub04OtherDiag1Error)}
                        helperText={ub04OtherDiag1Error || undefined}
                        inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                        onChange={(e) => {
                          markFieldTouched("otherDiagnosis1");
                          const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                          updateUb04Field("otherDiagnosis1", val);
                        }}
                        onBlur={() => markFieldTouched("otherDiagnosis1")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        id="ub04-other-diag-2"
                        InputProps={{ id: "ub04-other-diag-2" }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="67B. Other Diagnosis Code"
                        value={ub04Form.otherDiagnosis2}
                        error={Boolean(ub04OtherDiag2Error)}
                        helperText={ub04OtherDiag2Error || undefined}
                        inputProps={{ maxLength: 8, style: { textTransform: "uppercase" } }}
                        onChange={(e) => {
                          markFieldTouched("otherDiagnosis2");
                          const val = e.target.value.replace(/[^A-Za-z0-9.]/g, "").toUpperCase().slice(0, 8);
                          updateUb04Field("otherDiagnosis2", val);
                        }}
                        onBlur={() => markFieldTouched("otherDiagnosis2")}
                      />
                    </Grid>

                    {/* Section Divider: Attending Clinician */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary", mt: 0.5 }}>
                        76. Attending Provider Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="76. Attending Provider NPI"
                        value={ub04Form.attendingNpi}
                        inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => updateUb04Field("attendingNpi", e.target.value.replace(/\D/g, "").slice(0, 10))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="76. Attending Qualifier"
                        value={ub04Form.attendingQual}
                        onChange={(e) => updateUb04Field("attendingQual", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="76. Attending Last Name"
                        value={ub04Form.attendingLastName}
                        onChange={(e) => updateUb04Field("attendingLastName", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="76. Attending First Name"
                        value={ub04Form.attendingFirstName}
                        onChange={(e) => updateUb04Field("attendingFirstName", e.target.value)}
                      />
                    </Grid>

                    {/* Section Divider: Operating Clinician */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary", mt: 0.5 }}>
                        77. Operating Provider Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="77. Operating Provider NPI"
                        value={ub04Form.operatingNpi}
                        inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => updateUb04Field("operatingNpi", e.target.value.replace(/\D/g, "").slice(0, 10))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="77. Operating Last Name"
                        value={ub04Form.operatingLastName}
                        onChange={(e) => updateUb04Field("operatingLastName", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        label="77. Operating First Name"
                        value={ub04Form.operatingFirstName}
                        onChange={(e) => updateUb04Field("operatingFirstName", e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Paper>


                {/* ── UB-04 ACTION BUTTONS ── */}
                <Box sx={{ display: isReadOnly ? "none" : "flex", justifyContent: "flex-end", gap: 2, "@media print": { display: "none" } }}>
                  <Button variant="outlined" color="inherit" startIcon={<ResetIcon />} onClick={handleResetForm} sx={{ borderRadius: "12px", px: 2.5 }}>
                    Reset Form
                  </Button>
                  <Button variant="outlined" startIcon={<SaveIcon />} disabled={saving} onClick={handleSaveDraft} sx={{ borderRadius: "12px", px: 3 }}>
                    Save Draft
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    disabled={saving}
                    onClick={handleSubmitClaim}
                    sx={{
                      borderRadius: "12px",
                      px: 3.5,
                      boxShadow: "none",
                      backgroundColor: "#48CAE4",
                      color: "#000000",
                      "&:hover": { backgroundColor: "#3ab3cc", boxShadow: "none" },
                    }}
                  >
                    Submit Claim
                  </Button>
                </Box>
              </LocalizationProvider>
            </Box>
          </Box>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
          INVOICE FORM & LIVE VISUAL PREVIEW
          ═══════════════════════════════════════════════════════════════════════ */}
        {selectedForm === "INVOICE" && (
          <InvoiceFormSection
            selectedResidentId={selectedResidentId}
            isReadOnly={isReadOnly}
            invoiceForm={invoiceForm}
            updateInvoiceField={updateInvoiceField}
            invoiceLines={invoiceLines}
            onSetInvoiceLines={(lines) => setInvoiceLines(lines)}
            onShowSnackbar={(msg, sev) => setSnackbar({ open: true, message: msg, severity: sev || "info" })}
            handleInvoiceLineChange={handleInvoiceLineChange}
            handleAddInvoiceLine={handleAddInvoiceLine}
            handleRemoveInvoiceLine={handleRemoveInvoiceLine}
            renderSectionHeader={renderSectionHeader}
            sectionCardSx={sectionCardSx}
            onPrint={() => window.print()}
            residentInsurances={residentInsurances}
            selectedInsuranceId={effectiveSelectedInsuranceId}
            onInsuranceCardSelect={handleInsuranceCardSelect}
            onCreateInvoice={handleCreateIndividualInvoice}
            onSaveDraft={handleSaveDraft}
            isSubmitting={saving}
          />
        )}

      </Box>

      {/* ── Notification Feedback ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: "auto",
            maxWidth: 420,
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.15)",
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: "0.875rem",
            alignItems: "center",
            ...(snackbar.severity === "success" && {
              bgcolor: "#F0FDF4 !important",
              color: "#166534",
              border: "1px solid #86EFAC",
              "& .MuiAlert-icon": { color: "#16A34A" },
            }),
            ...(snackbar.severity === "error" && {
              bgcolor: "#FEF2F2 !important",
              color: "#991B1B",
              border: "1px solid #FCA5A5",
              "& .MuiAlert-icon": { color: "#DC2626" },
            }),
            ...(snackbar.severity === "warning" && {
              bgcolor: "#FFFBEB !important",
              color: "#92400E",
              border: "1px solid #FDE68A",
              "& .MuiAlert-icon": { color: "#D97706" },
            }),
            ...(snackbar.severity === "info" && {
              bgcolor: "#EFF6FF !important",
              color: "#1E40AF",
              border: "1px solid #BFDBFE",
              "& .MuiAlert-icon": { color: "#2563EB" },
            }),
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* ── Digital Signature Modal (Drawing Canvas) ── */}
      <SignatureInput
        open={signatureModalTarget !== null}
        onClose={() => setSignatureModalTarget(null)}
        onSave={handleSignatureModalSave}
        title={
          signatureModalTarget === "patient"
            ? "Create Resident Digital Signature"
            : "Create Insured Digital Signature"
        }
        confirmMessage="Are you sure you want to save this digital signature?"
        drawOnly={true}
        initialTab={0}
      />
    </Box>
  );
};

export default ClaimEditorPage;